<template>
  <section id="cart">
    <!-- ===== PAGE HEADER + PO CHIP ===== -->
    <div id="cartTop">
      <div class="container">
        <div class="row" v-if="cartError">
          <div class="col-12">
            <div class="alert alert-danger alert-dismissible">
              <button type="button" class="close" data-dismiss="alert" @click="cartError = false">
                &times;
              </button>
              <strong>Warning!</strong> Please add parts to your cart to continue
              the order process.
            </div>
          </div>
        </div>

        <div class="cart-header">
          <div class="cart-header-title">
            <h1>Review & Checkout</h1>
          </div>
          <div class="cart-header-actions" v-if="orders.length > 0">
            <a @click="openModifyForModify()"
               class="btn btn-back-search"
               title="Modify your order — review or edit assemblies, PO Numbers, and Special Instructions">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="btn-icon">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              {{ groupedOrders.length === 1 ? 'Modify Order' : 'Modify Orders' }}
            </a>
            <a @click="openSaveCartPicker()" :disabled="savingCart" class="btn btn-save-cart" title="Save this cart to resume later">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="btn-icon">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              {{ saveCartButtonLabel }}
            </a>
            <a @click="showDeleteModal" class="btn btn-clear-cart" title="Clear all items from this cart">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                class="btn-icon">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"></path>
                <path d="M10 11v6"></path>
                <path d="M14 11v6"></path>
                <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
              </svg>
              Clear Cart
            </a>
          </div>
        </div>
        <!-- NOTE: #cartTop > .container is intentionally left OPEN here. The
             cart body (empty state + the order b-form: PO grid, Cart Total,
             Place Order) now renders INSIDE this same card, below the header
             buttons, instead of as separate sibling sections. The container +
             #cartTop are closed after </b-form> further down. -->

    <!-- ===== REORDER WARNING / MAIN PO ENTRY ===== -->
    <!-- Legacy "Warning! Entering a PO ... entire order"
         banner + inline PO input that previously rendered on
         reorder routes was removed in favor of the existing
         Modify Order modal (#showModifyModal). When a reorder
         lands with PO-less orders, getCartSuccessful() now
         auto-opens that modal so the user is presented with
         the same in-modal PO + Special Instructions editor
         they get from clicking "Modify Order" manually. The
         banner-and-inline-input UX it replaced was visually
         inconsistent with the rest of the cart page (full-
         viewport gray bands extending past the rails) and
         duplicated functionality the modal already provides.
         See getCartSuccessful for the auto-open trigger. -->


    <!-- ===== EMPTY CART STATE ===== -->
    <div id="emptyCart" v-if="isCartEmpty">
      <div><!-- was .container; now nested inside #cartTop's .container -->
        <!-- Single unified white container holding the empty-state hero,
             the resume intro line, and both saved-carts columns. Replaces
             the old multi-card layout where each section had its own
             white background and the intro paragraph floated on the page
             background. -->
        <div class="empty-cart-panel">
          <div class="empty-cart-hero">
            <div class="empty-cart-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                stroke-linejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
            </div>
            <h2 class="empty-cart-title">Your cart is empty</h2>
            <p class="empty-cart-subtitle">There are currently no items in your cart.</p>
          </div>

        <!-- Saved carts picker: two columns, Orders on left, Quotes on right -->
        <div v-if="loadingSavedCarts" class="saved-loading">
          <p>Loading your saved carts…</p>
        </div>

        <div v-else-if="hasSavedCarts" class="saved-carts-picker">
          <p class="saved-carts-intro">
            Pick up where you left off — resume one of your recent saved carts below,
            or start fresh using <strong>New Order</strong> or <strong>New Quote</strong>
            in the menu above.
          </p>

          <div class="row">
            <!-- Recent Saved Orders -->
            <div class="col-lg-6 mb-4">
              <div class="saved-section">
                <div class="saved-section-header">
                  <h3 class="saved-section-title">Recent Saved Orders</h3>
                  <span class="saved-section-count" v-if="recentSavedOrders.length > 0">
                    Top {{ recentSavedOrders.length }}
                  </span>
                </div>
                <div class="saved-list" v-if="recentSavedOrders.length > 0">
                  <a
                    v-for="cart in recentSavedOrders"
                    :key="'order-' + cart.id"
                    class="saved-item"
                    @click="resumeSavedCart(cart)">
                    <div class="saved-item-main">
                      <div class="saved-item-top">
                        <span class="saved-item-date">{{ formatSavedDate(cart.dateCreated) }}</span>
                        <span class="saved-item-type saved-item-type-order">Order</span>
                      </div>
                      <div class="saved-item-company">{{ cart.companyName }}</div>
                      <div class="saved-item-meta">
                        <span v-if="cart.companyCode">{{ cart.companyCode }}</span>
                        <span class="saved-item-sep" v-if="cart.companyCode">·</span>
                        <span>{{ cart.itemCount }} {{ cart.itemCount === 1 ? "item" : "items" }}</span>
                      </div>
                    </div>
                    <div class="saved-item-resume">Resume →</div>
                  </a>
                </div>
                <div class="saved-list-empty" v-else>
                  <p>No saved orders.</p>
                </div>
              </div>
            </div>

            <!-- Recent Saved Quotes -->
            <div class="col-lg-6 mb-4">
              <div class="saved-section">
                <div class="saved-section-header">
                  <h3 class="saved-section-title">Recent Saved Quotes</h3>
                  <span class="saved-section-count" v-if="recentSavedQuotes.length > 0">
                    Top {{ recentSavedQuotes.length }}
                  </span>
                </div>
                <div class="saved-list" v-if="recentSavedQuotes.length > 0">
                  <a
                    v-for="cart in recentSavedQuotes"
                    :key="'quote-' + cart.id"
                    class="saved-item"
                    @click="resumeSavedCart(cart)">
                    <div class="saved-item-main">
                      <div class="saved-item-top">
                        <span class="saved-item-date">{{ formatSavedDate(cart.dateCreated) }}</span>
                        <span class="saved-item-type saved-item-type-quote">Quote</span>
                      </div>
                      <div class="saved-item-company">{{ cart.companyName }}</div>
                      <div class="saved-item-meta">
                        <span v-if="cart.companyCode">{{ cart.companyCode }}</span>
                        <span class="saved-item-sep" v-if="cart.companyCode">·</span>
                        <span>{{ cart.itemCount }} {{ cart.itemCount === 1 ? "item" : "items" }}</span>
                      </div>
                    </div>
                    <div class="saved-item-resume">Resume →</div>
                  </a>
                </div>
                <div class="saved-list-empty" v-else>
                  <p>No saved quotes.</p>
                </div>
              </div>
            </div>
          </div>

          <div class="saved-view-all" v-if="hasMoreSavedCarts">
            <router-link to="/account/savedcarts" class="saved-view-all-link">
              View all saved carts →
            </router-link>
          </div>

        </div>

        <!-- No saved carts at all -->
        <div v-else class="saved-carts-empty">
          <p>
            Ready to begin? Click <strong>New Order</strong> or
            <strong>New Quote</strong> in the menu above to get started.
          </p>

        </div>
      </div><!-- /.empty-cart-panel -->
      </div><!-- /.container -->
    </div><!-- /#emptyCart -->

    <b-form id="assemblyForm" @submit.prevent="">
      <!-- ===== ORDERS WITHOUT A PO =====
           Legacy per-order banner-and-input markup that
           previously rendered for every order missing a PO
           was removed in favor of the existing Modify Order
           modal. When the cart loads with PO-less orders
           (typically after a Reorder, since
           getCartSuccessful intentionally clears
           order.number on the reorder route), the
           getCartSuccessful flow auto-opens the modal so the
           user can assign POs in the same in-modal editor
           they get from clicking "Modify Order" manually.

           Why we replaced it: the legacy block painted full-
           viewport gray bands behind the warning + input
           that extended past the brushed-steel rails on
           either side, and it duplicated functionality the
           modal already provides. Deleting it also means
           PO-less orders won't render in the grid below
           (groupedOrders excludes PO-less orders), but the
           modal is open in the foreground so the user is
           focused on assigning POs anyway — once they save,
           the orders flow into the grid naturally. -->


      <!-- ===== GROUPED ORDERS (by PO Number → Machine + Assembly) =====
           Wrapped in a 2-column grid (col-lg-6) so two orders
           render side-by-side on desktop. Lets the user see
           more orders at a glance without scrolling, and
           collapses cleanly to a single column on smaller
           viewports. The `align-items-start` keeps cards
           top-aligned so a tall card doesn't stretch its
           shorter neighbor. -->
      <div class="cart-orders-grid-wrap"><!-- .container dropped: now nested in #cartTop's container -->
        <div class="row align-items-start cart-orders-grid">
          <div class="col-lg-6 cart-order-col"
               v-for="(group, groupIndex) in groupedOrders"
               :key="'po-' + group.number">
            <div class="po-group">
              <!-- Order header — Purchase Order # is the
                   section identifier. Reads "Purchase Order
                   #100", "Purchase Order #101", etc. (one per
                   PO group). PO number itself is also editable
                   from the subtotal row at the bottom of each
                   PO group. -->
              <div class="order-header">
                <div class="order-header-row">
                  <span class="order-header-label">
                    Purchase Order #{{ group.number }}
                  </span>
                  <span class="order-header-meta">
                    {{ group.machineGroups.length }}
                    {{ group.machineGroups.length === 1 ? 'item' : 'items' }}
                  </span>
                </div>
                <!-- Per-PO Special Instructions readout. Saved
                     by the Modify Order modal on the cart, then
                     read back here from group.specialInstructions
                     (pooled across the PO's backing rows in
                     buildGroupedOrders). Truncated past ~140 chars
                     with a Show more / Show less toggle so a long
                     note doesn't push the rest of the card down. -->
                <!-- Order-level (consolidated) Special
                     Instructions readout. Set ONLY when every
                     assembly under this Order shares the same
                     non-empty value (see buildGroupedOrders'
                     instructionsConsolidated check). Mixed-
                     value cases hide this and let the per-
                     assembly readouts under each card heading
                     handle display instead. Long instructions
                     collapse to a one-line preview with a
                     Show more / Show less toggle so they
                     don't push the cards down. -->
                <div class="order-header-instr"
                     v-if="group.specialInstructions">
                  <span class="order-header-instr-label">
                    Special Instructions
                    <span class="order-header-instr-scope">
                      (applies to all
                      {{ group.machineGroups.length }}
                      {{ group.machineGroups.length === 1
                          ? 'item'
                          : 'items' }}):
                    </span>
                  </span>
                  <span class="order-header-instr-text">
                    <template v-if="!isInstrLong(group.specialInstructions)
                                    || isInstrExpanded(group.number)">
                      {{ group.specialInstructions }}
                    </template>
                    <template v-else>
                      {{ instrPreview(group.specialInstructions) }}
                    </template>
                  </span>
                  <a class="order-header-instr-toggle"
                     v-if="isInstrLong(group.specialInstructions)"
                     @click.stop="toggleInstructions(group.number)">
                    {{ isInstrExpanded(group.number)
                        ? 'Show less'
                        : 'Show more' }}
                  </a>
                </div>
              </div>

          <!-- One card per unique Machine + Assembly combo within this PO. -->
          <div class="assembly-card"
            v-for="(mg, mgIndex) in group.machineGroups"
            :key="'asm-' + group.number + '-' + mg.key">
            <div class="assembly-card-header">
              <div class="assembly-card-heading">
                <div class="assemblyName">{{ mg.machineName }}</div>
                <div class="assemblyDescription">{{ mg.assemblyName }}</div>
                <!-- Per-assembly Special Instructions readout.
                     Renders this assembly's own instructions
                     (mg.specialInstructions, computed in
                     buildGroupedOrders by pooling across the
                     assembly's backing PurchaseOrder rows). The
                     Order-header readout above is hidden when
                     assemblies under the same Order have
                     different texts, so this is the only place
                     mixed-instruction cases show up. When the
                     whole Order has been broadcast (every
                     assembly carries the same value) the Order
                     header shows the consolidated value AND
                     each assembly shows its own — intentional
                     redundancy that confirms "yes, this applies
                     to the whole Order". -->
                <div class="assembly-instructions"
                     v-if="mg.specialInstructions">
                  <span class="assembly-instructions-label">
                    Special Instructions:
                  </span>
                  <span class="assembly-instructions-text">
                    {{ mg.specialInstructions }}
                  </span>
                </div>
              </div>
              <!-- PO subtotal sits in the FIRST assembly card's header,
                   right-justified next to the assembly title. Removes
                   the standalone subtotal row that used to sit below
                   all the cards, tightening vertical spacing between
                   orders. The PO number is still inline-editable here. -->
              <div class="assembly-card-po-subtotal"
                   v-if="mgIndex === 0">
                <div class="assembly-card-po-label">
                  <template v-if="editingPoNumber !== group.number">
                    Subtotal for PO Number:
                    <span class="subtotal-po">{{ group.number }}</span>
                    <a class="subtotal-po-edit"
                       @click="startEditPo(group.number)"
                       title="Edit PO Number">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 20h9"></path>
                        <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                      </svg>
                    </a>
                  </template>
                  <template v-else>
                    Subtotal for PO Number:
                    <input type="text"
                           v-model="editingPoValue"
                           class="subtotal-po-edit-input"
                           @keyup.enter="savePoEdit(group)"
                           @keyup.esc="cancelEditPo()" />
                    <button @click="savePoEdit(group)"
                            class="btn btn-primary btn-sm subtotal-po-save">
                      Save
                    </button>
                    <button @click="cancelEditPo()"
                            class="btn btn-link btn-sm subtotal-po-cancel"
                            type="button">
                      Cancel
                    </button>
                  </template>
                </div>
                <div class="assembly-card-po-amount" v-if="group.cost > 0">
                  {{ group.cost | currency }}
                </div>
                <div class="assembly-card-po-amount" v-else>$0.00</div>
              </div>
            </div>

            <!-- Collapsible summary row: toggle + code×qty chips + subtotal -->
            <div class="assembly-summary"
              :class="{ expanded: isGroupExpanded(group.number, mg.key) }"
              @click="toggleGroup(group.number, mg.key)">
              <div class="assembly-toggle"
                :class="{ expanded: isGroupExpanded(group.number, mg.key) }"
                :aria-label="isGroupExpanded(group.number, mg.key) ? 'Collapse' : 'Expand'">
                <span class="assembly-toggle-icon">{{ isGroupExpanded(group.number, mg.key) ? '−' : '+' }}</span>
              </div>
              <div class="assembly-summary-content">
                <span class="assembly-summary-count">
                  {{ mg.mergedParts.length }} {{ mg.mergedParts.length === 1 ? 'part' : 'parts' }}
                </span>
                <span class="code-chip" v-for="p in mg.mergedParts" :key="'chip-' + group.number + '-' + mg.key + '-' + p.id">
                  {{ p.part.code }}<span class="code-qty">×{{ p.quantity }}</span>
                </span>
                <span class="assembly-summary-empty" v-if="mg.mergedParts.length === 0">
                  No parts — click <strong>Add Parts</strong> to begin.
                </span>
              </div>
              <div class="assembly-summary-total">
                <span v-if="mg.cost > 0">{{ mg.cost | currency }}</span>
                <span v-else>$0.00</span>
              </div>
            </div>

            <!-- Parts table (collapsed by default).
                 Rebuilt as flexbox-based detail rows (see steele-
                 consistency-fix.css §24). Each part renders as a
                 self-contained card-row with item·code·description
                 on top and four stat blocks (Qty/Machine, Price,
                 Cart Qty + Update, Cost) on the bottom that wrap
                 naturally on narrow widths instead of bursting out
                 of the col-lg-6 parent. Old b-table approach
                 demanded 8 inline columns and overflowed the card. -->
            <div class="assembly-table-wrap" v-show="isGroupExpanded(group.number, mg.key)">
              <div class="parts-detail-list">
                <div class="part-detail-row"
                     v-for="(p, pIdx) in mg.mergedParts"
                     :key="'pdrow-' + group.number + '-' + mg.key + '-' + pIdx">
                  <div class="part-detail-head">
                    <div class="part-detail-meta">
                      <span class="part-item-num">{{ p.part.item }}</span>
                      <span class="part-detail-divider">·</span>
                      <span class="part-code">{{ p.part.code }}</span>
                      <!-- 2% Discount chip — green pill rendered
                           inline next to the part code on /cart's
                           Review & Checkout part-detail rows
                           when the part is wear-eligible. Uses
                           the same isWearEligible(p) helper that
                           drives the chip in the Modify Order
                           modal. The parent .part-detail-meta is
                           a flex ROW (not column), so no
                           align-self workaround is needed here. -->
                      <span class="part-discount-chip"
                            v-if="isWearEligible(p)">
                        2% Discount
                      </span>
                      <span class="part-detail-divider"
                            v-if="p.part.description">·</span>
                      <span class="part-desc"
                            v-if="p.part.description">{{ p.part.description }}</span>
                    </div>
                    <a class="part-detail-remove"
                       title="Remove this part"
                       @click="removeMergedPart(p, mg)">×</a>
                  </div>
                  <div class="part-detail-body">
                    <div class="part-stat">
                      <div class="part-stat-label">Qty / Machine</div>
                      <div class="part-stat-value">
                        {{ p.part.quantityPerMachine }}
                      </div>
                    </div>
                    <div class="part-stat">
                      <div class="part-stat-label">Price</div>
                      <div class="part-stat-value">
                        {{ p.part.price | currency }}
                      </div>
                    </div>
                    <div class="part-stat part-stat-qty">
                      <div class="part-stat-label">Cart Qty</div>
                      <div class="part-qty-controls">
                        <input type="text"
                               v-model="p.quantity"
                               class="part-qty-input" />
                        <button type="button"
                                class="part-qty-update btn btn-dark"
                                @click="updateMergedQuantity(p, mg)">
                          Update
                        </button>
                      </div>
                    </div>
                    <div class="part-stat part-stat-cost">
                      <div class="part-stat-label">Cost</div>
                      <div class="part-stat-value part-cost-amount">
                        {{ (p.part.price * p.quantity) | currency }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
          </div><!-- /.col-lg-6 cart-order-col -->
        </div><!-- /.row cart-orders-grid -->
      </div><!-- /.container cart-orders-grid-wrap -->

      <!-- ===== CART GRAND TOTAL =====
           Sum of every PO group subtotal plus any orders still waiting
           for a PO. Shown only when the cart has any contents at all. -->
      <div v-if="!isCartEmpty"><!-- .container dropped: nested in #cartTop's container -->
        <div class="cart-grand-total">
          <div class="cart-grand-total-label">Cart Total</div>
          <div class="cart-grand-total-value">{{ cartGrandTotal | currency }}</div>
        </div>
      </div>

      <!-- ===== FINAL ACTIONS ===== -->
      <div id="finalActions" v-if="orders.length > 0">
        <div><!-- .container dropped: nested in #cartTop's container -->
          <div class="row justify-content-center">
            <div class="col-lg-6">
              <a @click="openModifyForPlace()" :disabled="placeOrder" class="btn btn-primary btn-place-order">place order</a>
            </div>
          </div>
          <!-- Old "Would you like to reset your cart?" link removed —
               the same action is available via the Clear Cart button in
               the page header next to Save Cart. -->
        </div>
      </div>
    </b-form>
      </div><!-- /.container (#cartTop) — wraps the whole cart body now -->
    </div><!-- /#cartTop -->

    <!-- ===== RESET CART CONFIRMATION MODAL =====
         Custom b-modal (not msgBoxConfirm) so we can show a structured
         summary of what's being deleted: each assembly with its part
         count + subtotal, and an overall total at the bottom. Visual
         treatment matches the "Cart Inactivity Warning" modal shown
         elsewhere in the app — centered, title bar, body list, stacked
         full-width buttons at the bottom. -->
    <b-modal
      id="resetCartModal"
      size="md"
      centered
      hide-footer
      :hide-header-close="false"
      title="Reset Your Cart"
      body-class="reset-cart-modal-body"
      header-class="reset-cart-modal-header"
    >
      <p class="reset-cart-intro">
        You're about to remove <strong>everything</strong> from your cart.
        This can't be undone.
      </p>

      <!-- Totals shown FIRST so the user sees the magnitude up front. -->
      <div class="reset-cart-totals">
        <div class="reset-cart-totals-counts">
          <span class="reset-cart-totals-count">
            <span class="reset-cart-totals-num">{{ resetSummary.totalAssemblies }}</span>
            <span class="reset-cart-totals-unit">
              {{ resetSummary.totalAssemblies === 1 ? 'Item' : 'Items' }}
            </span>
          </span>
          <span class="reset-cart-totals-sep">·</span>
          <span class="reset-cart-totals-count">
            <span class="reset-cart-totals-num">{{ resetSummary.totalParts }}</span>
            <span class="reset-cart-totals-unit">
              {{ resetSummary.totalParts === 1 ? 'Part' : 'Parts' }}
            </span>
          </span>
        </div>
        <div class="reset-cart-totals-amount">
          <span class="reset-cart-totals-amount-label">Grand Total</span>
          <span class="reset-cart-totals-amount-value">
            ${{ (resetSummary.totalCost || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}
          </span>
        </div>
      </div>

      <!-- Per-assembly breakdown below the totals. -->
      <div class="reset-cart-summary">
        <div class="reset-cart-summary-header">
          <span>Item</span>
          <span>Subtotal</span>
        </div>
        <div
          v-for="(row, idx) in resetSummary.rows"
          :key="'reset-row-' + idx"
          class="reset-cart-summary-row"
        >
          <div class="reset-cart-summary-left">
            <div class="reset-cart-summary-machine">{{ row.machineName }}</div>
            <div class="reset-cart-summary-assembly" v-if="row.assemblyName">
              {{ row.assemblyName }}
            </div>
            <div class="reset-cart-summary-meta">
              PO {{ row.poNumber }} · {{ row.partCount }}
              {{ row.partCount === 1 ? 'part' : 'parts' }}
            </div>
          </div>
          <div class="reset-cart-summary-cost">
            ${{ (row.cost || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}
          </div>
        </div>

        <div class="reset-cart-summary-orphan" v-if="resetSummary.orphanCount > 0">
          Plus {{ resetSummary.orphanCount }}
          {{ resetSummary.orphanCount === 1 ? 'order' : 'orders' }}
          waiting for a PO number.
        </div>
      </div>

      <div class="reset-cart-actions">
        <button
          type="button"
          class="btn btn-primary reset-cart-btn-keep"
          @click="$bvModal.hide('resetCartModal')"
        >
          Keep my cart
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary reset-cart-btn-clear"
          @click="confirmResetCart"
        >
          Yes, clear everything
        </button>
      </div>
    </b-modal>

    <!-- ===== MODIFY ORDER MODAL =====
         The user can pick an order in their cart and:
         · Edit its PO Number
         · Edit its Special Instructions
         · Tick "Apply instructions to all orders" so they
           don't have to retype the same note
         · Save those changes
         · Add more parts to that order (pre-fills the
           create page)

         Replaces the old Special Instructions textarea
         that lived above the Place Order button.

         Order side intentionally has NO Convert action —
         orders can't go back to quotes. -->
    <b-modal
      v-model="showModifyModal"
      title="Modify Order"
      size="lg"
      hide-footer
      modal-class="modify-order-modal"
      @show="onModifyModalOpen"
      @hidden="onModifyModalClose"
    >
      <div v-if="modifyOptions.length === 0" class="modify-modal-intro">
        Nothing to modify — your cart appears to be empty.
      </div>
      <div v-else>
        <!-- ===== BROWSE / EDIT VIEW =====
             The normal modal body (banner + order list + per-
             assembly editor). In the 'reordering' context this is
             swapped for the confirmation step below once the user
             clicks "Add to Cart" with a partial set of POs
             (reorderConfirmStep). All other contexts always show this
             view — reorderConfirmStep is only ever toggled on in the
             reorder flow. -->
        <div v-show="!reorderConfirmStep">
        <!-- ===== CONTEXT BANNER =====
             High-emphasis confirmation panel rendered from
             the modifyBanner computed. Title + body vary
             by modifyContext (restored / placing / saving /
             adding) so each entry point gives the user a
             clear "what is this for" cue. Boxed, bordered,
             shadowed so the eye is drawn to it on open —
             the modal popping up should read as a confirm
             beat, not an interruption.

             v-html on the body is intentional and safe —
             the strings are constants in modifyBanner, never
             user input. Using <strong> in the markup lets
             us highlight key action labels ("Go To Cart")
             inline. -->
        <div v-if="modifyBanner"
             class="modify-restored-banner"
             :class="'modify-banner-' + modifyContext">
          <div class="modify-restored-title">
            {{ modifyBanner.title }}
          </div>
          <div class="modify-restored-body" v-html="modifyBanner.body"></div>
        </div>

        <!-- ===== RE-ORDER COMPANY LABEL (Feature 2) =====
             States which company this re-order is being added to so
             the user has an unambiguous cue (especially relevant after
             a cross-company / "Add to my company" reorder, where the
             cart's company may differ from where the order came from).
             Reordering context only; uses the loaded cart's company. -->
        <div v-if="modifyContext === 'reordering' && reorderCompanyDisplay"
             class="modify-reorder-company">
          Re-ordering for:
          <strong>{{ reorderCompanyDisplay }}</strong>
        </div>

        <p class="modify-modal-intro">
          Expand any Order to see its parts. Select an
          assembly to edit its PO Number, Special Instructions,
          or add more parts.
        </p>
        <div class="modify-quote-picker">
          <!-- Hierarchical picker — one Order header per
               PO group, collapsible to keep the modal scan-
               friendly when there are many Orders. Each
               header shows: Order # (left), grand total
               (center), assembly count (right), with a
               +/− chevron beneath the title that expands
               the group to show its assembly rows. Each
               assembly row shows machine + assembly +
               part count + per-assembly cost on the right.
               Editor below the picker is hidden until the
               user actually selects (radio-checks) an
               assembly, so a fresh open feels like a
               browse view, not an immediate edit form. -->
          <div v-for="grp in modifyOptionsGrouped"
               :key="'mgrp-' + grp.key"
               class="modify-order-group"
               :class="{
                 'is-multi': grp.assemblies.length > 1,
                 'is-expanded': expandedModifyGroups[grp.key]
               }">
            <!-- Top row: optional select checkbox (placing
                 context only) + Order label · Total · Assembly
                 count. Checkbox sits OUTSIDE the toggle button
                 so clicking it doesn't also expand the group;
                 placing-context users select Orders to ship,
                 they don't necessarily want to expand them
                 to do that. -->
            <div class="modify-order-group-head-row">
              <label v-if="modifyContext === 'placing'"
                     class="modify-order-group-select"
                     :title="(selectedPlaceGroups[grp.key] ? 'Unselect' : 'Select') + ' this Order for placement'"
                     @click.stop>
                <input type="checkbox"
                       :checked="!!selectedPlaceGroups[grp.key]"
                       @change="toggleSelectPlaceGroup(grp.key)" />
              </label>
              <button type="button"
                      class="modify-order-group-head"
                      @click="toggleModifyGroup(grp.key)">
                <span class="modify-order-group-label">
                  {{ grp.orderLabel }}
                </span>
                <span class="modify-order-group-total">
                  {{ grp.cost | currency }}
                </span>
                <span class="modify-order-group-count">
                  {{ grp.assemblies.length }}
                  {{ grp.assemblies.length === 1 ? 'ITEM' : 'ITEMS' }}
                </span>
              </button>

              <!-- Per-order PO Number — set every order's PO right here
                   without expanding; one "Save Changes" commits them
                   all. Outside the toggle button + @click.stop so
                   typing/clicking doesn't expand/collapse the group. -->
              <label class="modify-order-group-po-field" @click.stop>
                <span class="modify-order-group-po-label">PO #</span>
                <input type="text"
                       class="modify-order-group-po-input"
                       :value="groupPoDrafts[grp.key] || ''"
                       @input="setGroupPo(grp.key, $event.target.value)"
                       @click.stop
                       placeholder="Enter PO Number"
                       :aria-label="'PO Number for ' + grp.orderLabel" />
              </label>
            </div>

            <!-- Below the title: chevron + helper text.
                 Visually subordinate so the header row
                 reads as the primary identifier and the
                 expand affordance reads as a secondary
                 action. Helper text wording flips between
                 expanded / collapsed so users always know
                 what the click will do. -->
            <button type="button"
                    class="modify-order-group-toggle"
                    :class="{ 'is-open': expandedModifyGroups[grp.key] }"
                    @click="toggleModifyGroup(grp.key)">
              <span class="modify-order-group-toggle-icon">
                {{ expandedModifyGroups[grp.key] ? '−' : '+' }}
              </span>
              <span class="modify-order-group-toggle-text">
                {{ expandedModifyGroups[grp.key]
                    ? 'Hide item details'
                    : 'Click the plus to see item details' }}
              </span>
            </button>

            <!-- Assembly rows — visible only when expanded.
                 Radio selection sets activeModifyId which
                 reveals the PO/Special Instructions editor
                 below. Per-assembly cost on the right. -->
            <div v-if="expandedModifyGroups[grp.key]"
                 class="modify-order-group-assemblies">
              <div
                v-for="opt in grp.assemblies"
                :key="'pick-wrap-' + opt.id"
                class="modify-quote-pick-wrap">
                <label
                  class="modify-quote-pick-row"
                  :class="{
                    'is-selected': activeModifyId === opt.id,
                    'is-nested': grp.assemblies.length > 1
                  }"
                >
                  <input type="radio"
                         :value="opt.id"
                         v-model="activeModifyId"
                         class="modify-quote-pick-radio" />
                  <div class="modify-quote-pick-body">
                    <div class="modify-option-head">
                      <span class="modify-option-assembly">
                        {{ opt.assemblyName || '—' }}
                      </span>
                    </div>
                    <div class="modify-option-meta">
                      <span v-if="opt.machineName">{{ opt.machineName }}</span>
                      <span v-if="opt.machineName">·</span>
                      <span>
                        {{ opt.partCount }}
                        {{ opt.partCount === 1 ? 'part' : 'parts' }}
                      </span>
                      <span v-if="opt.specialInstructions"
                            class="modify-option-instr-flag">
                        · has instructions
                      </span>
                    </div>
                  </div>
                  <span class="modify-quote-pick-cost">
                    {{ opt.cost | currency }}
                  </span>
                </label>

                <!-- Per-part list inside each assembly card.
                     Each row shows the part code, description,
                     qty, line cost, plus a red X delete button.
                     Always visible so the user doesn't have to
                     dig past the radio to manage parts; clicking
                     a part row's X is a separate gesture from
                     selecting the radio. -->
                <div class="modify-parts-list"
                     v-if="opt.parts && opt.parts.length > 0">
                  <div v-for="p in opt.parts"
                       :key="'mp-' + opt.id + '-' + p.id"
                       class="modify-part-row">
                    <div class="modify-part-info">
                      <span class="modify-part-code">
                        {{ p.part && p.part.code ? p.part.code : '—' }}
                      </span>
                      <!-- 2% Discount chip — green pill next to
                           the part code when the part is wear-
                           eligible. Mirrors Create_-_Order.vue's
                           .inline-wear-discount-chip. wearPartIds
                           is loaded on Cart's mount; if it's
                           empty (no master/admin wear list yet,
                           or the API failed), isWearEligible
                           returns false everywhere and the chip
                           simply doesn't render — no error. -->
                      <span class="modify-part-discount-chip"
                            v-if="isWearEligible(p)">
                        2% Discount
                      </span>
                      <span class="modify-part-desc"
                            v-if="p.part && p.part.description">
                        {{ p.part.description }}
                      </span>
                    </div>
                    <span class="modify-part-qty">
                      ×{{ p.quantity || 0 }}
                    </span>
                    <span class="modify-part-cost">
                      {{ ((p.part && p.part.price ? p.part.price : 0) * (p.quantity || 0)) | currency }}
                    </span>
                    <!-- Delete X. Type=button so it doesn't
                         submit any enclosing form; stop
                         propagation so a click here doesn't
                         also select the assembly radio. -->
                    <button type="button"
                            class="modify-part-remove"
                            :title="'Remove ' + (p.part && p.part.code ? p.part.code : 'this part')"
                            @click.stop.prevent="removePartFromCart(p)">
                      ×
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modify-editor" v-if="activeModifyOption">
          <!-- PO Numbers are now set per order in the headers above
               (no expansion needed). This editor — shown when an
               assembly row is selected — handles that assembly's
               Special Instructions only. -->
          <div class="modify-editor-row">
            <label class="modify-editor-label" for="modifySpecialInstructions">
              Special Instructions
              <span class="modify-editor-scope">
                — for {{ activeModifyOption.assemblyName || 'this assembly' }}
              </span>
            </label>
            <textarea id="modifySpecialInstructions"
                      v-model="modifySpecialInstructionsDraft"
                      class="modify-editor-input modify-editor-textarea"
                      rows="4"
                      placeholder="Notes or instructions for this assembly"></textarea>
          </div>

          <!-- In-Order broadcast toggle. Only meaningful when
               the active Order has multiple assemblies — for a
               single-assembly Order the per-assembly edit IS
               the Order-wide edit, and the checkbox would just
               be noise. -->
          <div class="modify-editor-row modify-editor-toggle-row"
               v-if="activeModifyOrderAssemblyCount > 1">
            <label class="modify-apply-all">
              <input type="checkbox"
                     v-model="applyInstructionsToOrder" />
              <span>
                Apply to all {{ activeModifyOrderAssemblyCount }}
                assemblies in {{ activeModifyOption.orderLabel }}
              </span>
            </label>
          </div>

          <div class="modify-editor-row modify-editor-toggle-row"
               v-if="modifyUniquePoCount > 1">
            <label class="modify-apply-all">
              <input type="checkbox"
                     v-model="applyInstructionsToAll" />
              <span>
                Apply these Special Instructions to all
                {{ modifyUniquePoCount }} orders
              </span>
            </label>
          </div>
        </div>
        </div><!-- /v-show="!reorderConfirmStep" (browse/edit view) -->

        <!-- ===== REORDER CONFIRMATION STEP =====
             Shown only in the 'reordering' context after the user
             clicks "Add to Cart" with SOME (not all) orders carrying a
             PO#. Lists what will be added (orders WITH a PO#) vs. what
             will be dropped (orders WITHOUT one), so the partial submit
             is explicit. "Revise" returns to the order/PO list to enter
             more POs; "Save Orders to Cart" commits — persisting the
             PO'd orders and removing the no-PO ones from the cart. This
             whole block is reorder-only (reorderConfirmStep never turns
             on in any other context). -->
        <div v-if="reorderConfirmStep" class="modify-reorder-confirm">
          <div class="modify-restored-banner modify-banner-reordering">
            <div class="modify-restored-title">
              Confirm what's being added
            </div>
            <div class="modify-restored-body">
              Orders with a PO# will be added to your cart. Orders
              without a PO# or Quote reference won't be added.
            </div>
          </div>

          <div class="modify-reorder-confirm-section modify-reorder-confirm-added">
            <div class="modify-reorder-confirm-heading">
              Added to your cart:
            </div>
            <ul class="modify-reorder-confirm-list">
              <li v-for="grp in reorderPoGroups"
                  :key="'rc-add-' + grp.key"
                  class="modify-reorder-confirm-item">
                <span class="modify-reorder-confirm-order">
                  {{ grp.orderLabel }}
                </span>
                <span class="modify-reorder-confirm-po">
                  PO #{{ (groupPoDrafts[grp.key] || '').trim() }}
                </span>
              </li>
            </ul>
          </div>

          <div class="modify-reorder-confirm-section modify-reorder-confirm-dropped"
               v-if="reorderNoPoGroups.length > 0">
            <div class="modify-reorder-confirm-heading">
              Not added (no PO/Quote reference):
            </div>
            <ul class="modify-reorder-confirm-list">
              <li v-for="grp in reorderNoPoGroups"
                  :key="'rc-drop-' + grp.key"
                  class="modify-reorder-confirm-item">
                <span class="modify-reorder-confirm-order">
                  {{ grp.orderLabel }}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Action row always renders when there's something
             in the cart, even if no assembly is selected. The
             banner-only state (no row selected) still wants a
             clean-form button so the user has a clear exit.
             Per-button v-if branches handle the selection
             conditional internally.

             Primary button label flips between "Save Changes"
             (dirty) and a context-driven label ("Place Order"
             / "Continue to Save" / "Add to Cart" / "Go To
             Cart") via modifyPrimaryLabel. Click handler also
             splits on dirty: dirty → saveModifyChanges (which
             chains into dispatchModifyConfirm on success);
             clean → dispatchModifyConfirm directly. Either
             path lands the user where the entry point
             intended to take them.

             REORDER overrides (modifyContext === 'reordering'):
             a dedicated set of buttons — the browse-view primary is
             "Add to Cart" (gated on reorderHasAnyPo, routes through
             confirmAddToCartReorder); the confirmation step shows
             "Revise" + "Save Orders to Cart". All other contexts use
             the original generic button untouched. -->
        <div class="modify-actions">
          <!-- Reorder, confirmation step: Revise / Save Orders to Cart -->
          <template v-if="modifyContext === 'reordering' && reorderConfirmStep">
            <button type="button"
                    class="btn btn-dark modify-action-btn"
                    :disabled="savingModify"
                    @click="reviseReorder">
              Revise
            </button>
            <button type="button"
                    class="btn btn-primary modify-action-btn"
                    :disabled="savingModify"
                    @click="saveReorderToCart(true)">
              Save Orders to Cart
            </button>
          </template>
          <!-- Reorder, browse/edit view: single "Add to Cart", gated on
               at least one PO being present. -->
          <template v-else-if="modifyContext === 'reordering'">
            <button v-if="reorderHasAnyPo"
                    type="button"
                    class="btn btn-primary modify-action-btn"
                    :disabled="savingModify"
                    @click="confirmAddToCartReorder">
              {{ modifyPrimaryLabel }}
            </button>
            <button v-if="activeModifyOption"
                    type="button"
                    class="btn btn-dark modify-action-btn"
                    :disabled="savingModify"
                    @click="goAddMoreParts">
              Add More Parts
            </button>
            <!-- Cancel — closes the modal, which discards the phantom
                 reorder cart via onModifyModalClose (reorderConfirmed stays
                 false). Always available, even with no PO entered. -->
            <button type="button"
                    class="btn btn-light modify-action-btn"
                    :disabled="savingModify"
                    @click="showModifyModal = false">
              Cancel
            </button>
          </template>
          <!-- All other contexts — unchanged. -->
          <template v-else>
            <button type="button"
                    class="btn btn-primary modify-action-btn"
                    :disabled="savingModify || (modifyContext === 'placing' && !hasModifyChanges && selectedPlaceGroupCount === 0)"
                    @click="hasModifyChanges ? saveModifyChanges() : dispatchModifyConfirm()">
              {{ modifyPrimaryLabel }}
            </button>
            <button v-if="activeModifyOption"
                    type="button"
                    class="btn btn-dark modify-action-btn"
                    :disabled="savingModify"
                    @click="goAddMoreParts">
              Add More Parts
            </button>
          </template>
        </div>
      </div>
    </b-modal>

    <!-- ===== SAVE CART PICKER MODAL =====
         Mirrors RFQSavedItems' Save for Later flow. Lists
         each order in the cart with a checkbox so the user
         can pick a subset to save. Selected orders split
         off into a NEW cart row server-side via
         POST /cart/{id}/savepartial; unselected orders
         stay in the active cart so editing can continue.

         All selected (the most common case) takes a cheap
         path — no server call, just clears localStorage and
         navigates to /account/savedcarts. -->
    <b-modal id="save-cart-picker-modal"
             v-model="showSaveCartPickerModal"
             title="Save Selected Orders for Later"
             size="md"
             centered
             :ok-title="saveCartPickerOkTitle"
             ok-variant="primary"
             cancel-title="Cancel"
             cancel-variant="outline-secondary"
             :ok-disabled="selectedOrdersForPicker.length === 0 || savingCart"
             @ok="confirmSaveCartPicker">

      <p class="convert-modal-intro">
        Select which orders you want to save for later.
        They'll be available from Saved Carts. Any unselected
        orders stay in your active cart so you can keep
        editing them.
      </p>

      <div class="convert-quote-list">
        <!-- One row per Order # (= PO Number group), so a
             multi-assembly Order like "Order #1: WEDGE GROOVE
             LINER + CORE PIN FOR K DIE + None (PO 1)" shows
             up here as one selectable unit instead of three.
             Mirrors the Place Order picker exactly — same
             grouping, same selection model (by group.number
             rather than row id) so saveCartForLater can
             walk this.orders by PO membership. -->
        <div v-for="(group, gIdx) in groupedOrders"
             :key="'savecart-pick-' + group.number"
             class="convert-quote-row">
          <b-form-checkbox v-model="selectedOrdersForPicker"
                           :value="group.number">
            <div class="convert-quote-row-main">
              <strong class="convert-quote-row-label">
                Purchase Order #{{ group.number }}
              </strong>
              <span class="convert-quote-row-meta">
                {{ group.machineGroups.length }}
                {{ group.machineGroups.length === 1
                    ? 'item'
                    : 'items' }}
              </span>
            </div>
            <div class="convert-quote-row-sub">
              <span v-for="(mg, mIdx) in group.machineGroups"
                    :key="'sc-mg-' + group.number + '-' + mIdx"
                    class="convert-quote-row-asm">
                <span v-if="mIdx > 0" class="convert-quote-row-sep"> · </span>{{ mg.machineName }}<span v-if="mg.assemblyName"> ({{ mg.assemblyName }})</span>
              </span>
              <span class="convert-quote-row-amount"
                    v-if="(group.cost || 0) > 0">
                · {{ group.cost | currency }}
              </span>
            </div>
          </b-form-checkbox>
        </div>
      </div>

      <div class="convert-quote-toggle-all">
        <a href="#"
           @click.prevent="toggleSelectAllOrdersForPicker">
          {{ selectedOrdersForPicker.length === groupedOrders.length
              ? 'Deselect All'
              : 'Select All' }}
        </a>
      </div>

      <p v-if="selectedOrdersForPicker.length > 0
               && selectedOrdersForPicker.length < groupedOrders.length"
         class="convert-quote-note">
        The selected
        {{ selectedOrdersForPicker.length === 1 ? 'order' : 'orders' }}
        will be saved to your Saved Carts. The
        {{ groupedOrders.length - selectedOrdersForPicker.length }}
        unselected
        {{ groupedOrders.length - selectedOrdersForPicker.length === 1
            ? 'order stays'
            : 'orders stay' }}
        in your active cart so you can keep editing.
      </p>
      <p v-if="selectedOrdersForPicker.length === 0"
         class="convert-quote-note convert-quote-note-warn">
        Select at least one order to continue.
      </p>
    </b-modal>

    <!-- ===== PLACE ORDER PICKER MODAL =====
         Opens from the Place Order button at the bottom of
         the cart when there are 2+ Orders. Lets the user pick
         which Orders go through right now and which stay in
         the cart for later — useful when shipping schedules
         line up differently for different machines or POs.

         Selection is by Order # (= group.number = PO Number);
         each row represents one logical Order regardless of
         how many machine+assembly assemblies it spans, so a
         multi-assembly Order can't be partially placed
         (e.g. "ship POINT AUGERS now but not CORE PIN FOR
         K DIE under the same PO" doesn't make sense and
         isn't an option here).

         All-selected (the common case) takes the cheap path
         — no server call, just routes to Cart Success with
         the existing cartId. Partial selection invokes the
         existing savePartialQuotes split endpoint to move
         unselected Orders out into a saved cart row before
         placing what remains.

         Custom action row at the bottom (hide-footer + body
         buttons) instead of b-modal's default footer — gives
         us direct @click handlers and predictable styling
         that doesn't depend on Bootstrap's modal-footer
         layout. -->
    <b-modal id="place-order-picker-modal"
             v-model="showPlaceOrderPickerModal"
             title="Place Selected Orders"
             size="md"
             centered
             hide-footer
             modal-class="place-order-picker-modal">

      <p class="convert-modal-intro">
        Select which orders to place now. Unselected orders
        stay in your active cart so you can place or edit
        them later.
      </p>

      <div class="convert-quote-list">
        <div v-for="(group, gIdx) in groupedOrders"
             :key="'place-pick-' + group.number"
             class="convert-quote-row">
          <b-form-checkbox v-model="selectedOrdersForPlace"
                           :value="group.number">
            <div class="convert-quote-row-main">
              <strong class="convert-quote-row-label">
                Purchase Order #{{ group.number }}
              </strong>
              <span class="convert-quote-row-meta">
                {{ group.machineGroups.length }}
                {{ group.machineGroups.length === 1
                    ? 'item'
                    : 'items' }}
              </span>
            </div>
            <div class="convert-quote-row-sub">
              <span v-for="(mg, mIdx) in group.machineGroups"
                    :key="'plc-mg-' + group.number + '-' + mIdx"
                    class="convert-quote-row-asm">
                <span v-if="mIdx > 0" class="convert-quote-row-sep"> · </span>{{ mg.machineName }}<span v-if="mg.assemblyName"> ({{ mg.assemblyName }})</span>
              </span>
              <span class="convert-quote-row-amount"
                    v-if="(group.cost || 0) > 0">
                · {{ group.cost | currency }}
              </span>
            </div>
          </b-form-checkbox>
        </div>
      </div>

      <div class="convert-quote-toggle-all">
        <a href="#"
           @click.prevent="toggleSelectAllOrdersForPlace">
          {{ selectedOrdersForPlace.length === groupedOrders.length
              ? 'Deselect All'
              : 'Select All' }}
        </a>
      </div>

      <p v-if="selectedOrdersForPlace.length > 0
               && selectedOrdersForPlace.length < groupedOrders.length"
         class="convert-quote-note">
        The selected
        {{ selectedOrdersForPlace.length === 1 ? 'order' : 'orders' }}
        will be placed. The
        {{ groupedOrders.length - selectedOrdersForPlace.length }}
        unselected
        {{ groupedOrders.length - selectedOrdersForPlace.length === 1
            ? 'order will stay' : 'orders will stay' }}
        in your cart for you to place or edit later.
      </p>
      <p v-if="selectedOrdersForPlace.length === 0"
         class="convert-quote-note convert-quote-note-warn">
        Select at least one order to continue.
      </p>

      <!-- Custom action row at the bottom of the body. Three
           buttons sit side-by-side with equal flex widths so
           they never wrap or stack regardless of label length.
           Direct @click handlers (rather than relying on
           b-modal's @ok event) sidestep the variant-styling
           and event-firing oddities that the default footer
           was hitting. -->
      <div class="place-order-actions">
        <button type="button"
                class="btn btn-primary place-order-action-btn"
                :disabled="selectedOrdersForPlace.length === 0 || placeOrder"
                @click="confirmPlaceOrderPicker">
          {{ placeOrder ? 'Placing…' : placeOrderPickerOkTitle }}
        </button>
        <button type="button"
                class="btn btn-outline-dark place-order-action-btn"
                :disabled="placeOrder"
                @click="showPlaceOrderPickerModal = false">
          Cancel
        </button>
      </div>
    </b-modal>
  </section>
</template>

<script>
import FeApiService from "@/assets/js/FeApiService";
const apiService = new FeApiService();

import "expose-loader?$!expose-loader?jQuery!jquery";
import "semantic-ui-dropdown/dropdown.js";

import Vue from "vue";
import Vue2Filters from "vue2-filters";

Vue.use(Vue2Filters);

export default {
  name: "Cart",
  computed: {
    // ===== SAVE CART PICKER MODAL =====
    // Button label flexes based on selection size + total
    // count so the user sees a confirmation that matches
    // what's about to happen ("Save 1 Order" vs "Save All
    // 3 Orders for Later").
    saveCartPickerOkTitle() {
      var n = this.selectedOrdersForPicker.length;
      var total = (this.groupedOrders || []).length;
      if (n === 0) return "Save for Later";
      if (n === total) {
        return n === 1 ? "Save 1 Order for Later" : "Save All " + n + " Orders for Later";
      }
      return n === 1 ? "Save 1 Order for Later" : "Save " + n + " Orders for Later";
    },

    // Header button label — pluralizes against the actual
    // count of Orders (PO groups) in the cart, so a cart with
    // one Order reads "Save Cart Order" and multiple reads
    // "Save Cart Orders". Empty cart falls back to the
    // generic "Save Cart" text (the button is effectively
    // disabled in that state via openSaveCartPicker's empty
    // guard, so the label is rarely seen, but keep it sane).
    saveCartButtonLabel() {
      var total = (this.groupedOrders || []).length;
      if (total === 0) return "Save Cart";
      return total === 1 ? "Save Cart Order" : "Save Cart Orders";
    },
    // ===== PLACE ORDER PICKER =====
    // Dynamic OK button label so the user always sees a
    // confirmation that matches what's about to happen
    // ("Place 1 Order" vs "Place All 3 Orders" vs
    // "Place 2 Orders"). Bound to the picker modal's
    // ok-title prop. Operates on groupedOrders.length —
    // each group is one Order # (= one PO Number).
    placeOrderPickerOkTitle() {
      var n = this.selectedOrdersForPlace.length;
      var total = (this.groupedOrders || []).length;
      if (n === 0) return "Place Order";
      if (n === total) {
        return n === 1
          ? "Place 1 Order"
          : "Place All " + n + " Orders";
      }
      return n === 1 ? "Place 1 Order" : "Place " + n + " Orders";
    },
    // Orders that still need a PO number assigned (shown at top with the
    // "Enter PO" form, outside the grouped view).
    ordersWithoutPo() {
      return this.orders.filter((o) => !o.number);
    },
    // Orders that have a PO number, grouped two levels deep:
    //   1. By PO number (top level)
    //   2. Within each PO, by Machine + Assembly combination
    //
    // Within each machine+assembly subgroup, parts are deduplicated by code
    // (quantities are summed). Each merged row tracks its `_sources` — the
    // backing (order, lineItem) pairs it came from — so quantity updates and
    // removes can be routed back to the correct PurchaseOrder records on the
    // backend. This hides the duplicate-PurchaseOrder records the backend can
    // sometimes produce for the same machine/assembly under one PO.
    groupedOrders() {
      return this.buildGroupedOrders(this.orders);
    },
    // Modify Order modal options — flattens groupedOrders into
    // one row per (Order#, machine+assembly) combo. Each row
    // carries the IDs needed to pre-fill /orders/create's
    // Step 1-3: companyId (from cart-level), CompanyMachine
    // ID, parent assembly ID, plus the PO Number so the
    // user keeps adding to the same Order. Sub-assembly
    // case: we hand the parent assembly ID to Step 3 (which
    // is what its picker expects), and pass the leaf
    // assembly ID through as `subAssemblyId` so the
    // sub-assembly picker can auto-select it on arrival.
    modifyOptions() {
      var rows = [];
      var orderCounter = 0;

      // PO'd orders — flatten groupedOrders into one row per
      // (PO#, machine+assembly). Same as the original modal
      // contract: lets the user edit existing orders' PO and
      // Special Instructions.
      (this.groupedOrders || []).forEach(function (g, gIdx) {
        // ===== Per-PO labelling =====
        // Each PO group is one logical "Purchase Order #{n}",
        // regardless of how many machine+assembly rows it
        // contains. Two assemblies under the same PO show
        // as two rows but share the same Purchase Order label
        // — mirroring how the cart page itself groups them.
        // The label uses the actual PO number (g.number) so
        // it reads "Purchase Order #100" rather than a
        // sequential "Order #1" — matches the rest of the
        // app's vocabulary now that the section identifier
        // IS the customer-facing PO number.
        orderCounter += 1;
        var orderLabel = "Purchase Order #" + g.number;

        (g.machineGroups || []).forEach(function (mg) {
          var primary = mg.primaryOrder || {};
          var asm = primary.assembly || {};
          // Parent assembly id is what Step 3's picker
          // expects. If parentAssemblyId is null this part
          // came from a top-level assembly — use the
          // assembly's own id.
          var parentAssemblyId = asm.parentAssemblyId || asm.id || primary.assemblyId;
          // Leaf id (only present when we drilled into a
          // sub-assembly). Used to pre-select the
          // sub-assembly picker on the create page.
          var leafAssemblyId = asm.parentAssemblyId
            ? (asm.id || primary.assemblyId)
            : null;
          // Total parts in this (PO, machine, assembly) combo —
          // sum the merged-part quantities so the user sees
          // "5 parts" not "3 unique line items". Falls back
          // to the line-item count if mergedParts is empty.
          var partCount = 0;
          (mg.mergedParts || []).forEach(function (p) {
            partCount += Number(p.quantity) || 0;
          });
          if (partCount === 0) {
            partCount = (mg.mergedParts || []).length;
          }

          // Recompute the assembly's subtotal from its
          // current part prices instead of reading the cached
          // mg.cost (which is summed from order.cost on each
          // backing PurchaseOrder — a snapshot from when the
          // cart was saved). The placed-order endpoint
          // recalculates from current part prices on
          // submission, so trusting the cached aggregate
          // produced a discrepancy where the modal showed a
          // different total from what the user saw on the
          // success page. Computing the same way as the
          // success page (p.part.price * p.quantity, summed)
          // makes the two totals agree.
          //
          // Falls back to mg.cost if any line has no price
          // available (defensive — shouldn't happen for
          // valid carts but doesn't make things worse).
          var partsCostSum = 0;
          var anyPriced = false;
          (mg.mergedParts || []).forEach(function (p) {
            var price = p && p.part && p.part.price ? Number(p.part.price) : NaN;
            var qty = Number(p && p.quantity) || 0;
            if (!isNaN(price)) {
              partsCostSum += price * qty;
              anyPriced = true;
            }
          });
          var assemblyCost = anyPriced ? partsCostSum : (mg.cost || 0);
          rows.push({
            // PO row id — used as the radio v-model value
            // and as the lookup key for save flows.
            id: primary.id,
            orderLabel: orderLabel,
            poNumber: g.number,
            // Per-ASSEMBLY Special Instructions, sourced from
            // the machineGroup's own pooled value (see
            // buildGroupedOrders). Each assembly carries its
            // own text now — picking POINT AUGERS in the modal
            // pre-fills with POINT AUGERS' instructions, not a
            // PO-wide pool that would cross-contaminate
            // siblings.
            specialInstructions: mg.specialInstructions || "",
            machineName: mg.machineName,
            assemblyName: mg.assemblyName,
            machineId: primary.machineId || 0,
            assemblyId: parentAssemblyId,
            subAssemblyId: leafAssemblyId,
            configuredWear: primary.configuredWear === true,
            partCount: partCount,
            // Per-assembly subtotal — sum of (price × qty)
            // across this (PO, machine, assembly) group's
            // parts, computed from live prices so it matches
            // the placed-order success page.
            cost: assemblyCost,
            // Merged part rows — each carries its own id,
            // quantity, and the _sources array that maps
            // back to the underlying (order, lineItem) pairs.
            // The modal renders these inside the expanded
            // assembly card with a delete X per row.
            // removePartFromCart uses _sources to drive the
            // updatePurchaseOrder calls.
            parts: mg.mergedParts || [],
          });
        });
      });

      // PO-less orders — typically the result of a Reorder,
      // where getCartSuccessful intentionally clears
      // order.number so the user is forced to assign a new
      // PO before placing. These orders aren't in
      // groupedOrders (buildGroupedOrders filters out
      // !o.number) so we append them here as their own rows.
      // Each one shows up with poNumber: '' so the modal's
      // PO-Number input renders empty and the user types in
      // the value they want.
      //
      // saveModifyChanges already handles the empty-to-set
      // transition correctly: line ~2260 reads
      //   if (newPo && newPo !== order.number) { order.number = newPo }
      // which means assigning a PO to a previously-empty
      // order works the same as renaming an existing one.
      // PO-less orders. Group by (machine, assembly) the
      // same way buildGroupedOrders does for PO'd rows so
      // stale duplicate backing rows for the same assembly
      // collapse into one entry. Without this dedup, a cart
      // that's been through partial save/place flows can
      // arrive here with N PurchaseOrder rows for the same
      // assembly, all PO-less, which would show up as
      // Order #3 / Order #4 / etc. in the modal even though
      // the user only sees one of them in the cart (the cart
      // hides PO-less rows entirely via groupedOrders'
      // !!o.number filter).
      //
      // Each merged entry sums quantity across its backing
      // rows so partCount and cost reflect the full
      // assembly. The first backing row's id is used for
      // saveModifyChanges' lookup target — assigning a PO
      // through the modal will land on that row, leaving
      // any duplicates orphaned. (The duplicates are stale
      // data that shouldn't exist; merging them into the
      // primary on save is a separate cleanup pass.)
      var pendingByKey = {};
      var pendingOrder = [];
      (this.ordersWithoutPo || []).forEach(function (order) {
        var asm = order.assembly || {};
        var machine = asm.machine || {};
        var machineName = machine.description || "";
        var assemblyName = asm.name || "";
        var key = machineName + "||" + assemblyName;
        var partCountThis = 0;
        (order.parts || []).forEach(function (p) {
          partCountThis += Number(p.quantity) || 0;
        });
        if (partCountThis === 0) {
          partCountThis = (order.parts || []).length;
        }
        var parentAssemblyId = asm.parentAssemblyId || asm.id || order.assemblyId;
        var leafAssemblyId = asm.parentAssemblyId
          ? (asm.id || order.assemblyId)
          : null;

        if (!pendingByKey[key]) {
          pendingByKey[key] = {
            id: order.id,
            specialInstructions: order.specialInstructions || "",
            machineName: machineName,
            assemblyName: assemblyName,
            machineId: order.machineId || 0,
            assemblyId: parentAssemblyId,
            subAssemblyId: leafAssemblyId,
            configuredWear: order.configuredWear === true,
            partCount: 0,
            cost: 0,
            // Per-part merge map keyed by part code so
            // duplicate line items across stale backing
            // PurchaseOrder rows collapse into one row in
            // the modal, with quantity summed and _sources
            // tracking which (order, lineItem) pairs back it.
            // Same shape as buildGroupedOrders' _partsMap.
            _partsMap: {},
          };
          pendingOrder.push(key);
        }
        var entry = pendingByKey[key];
        entry.partCount += partCountThis;
        entry.cost += Number(order.cost) || 0;

        // Walk this backing order's line items into the
        // dedup map. Same key strategy as the PO'd path
        // (part.code | part.item | id-fallback).
        (order.parts || []).forEach(function (lineItem) {
          if (!lineItem || !lineItem.part) return;
          var pk =
            lineItem.part.code ||
            lineItem.part.item ||
            ("__id_" + lineItem.id);
          if (!entry._partsMap[pk]) {
            entry._partsMap[pk] = {
              id: lineItem.id,
              part: lineItem.part,
              quantity: Number(lineItem.quantity) || 0,
              _sources: [{ order: order, lineItem: lineItem }],
            };
          } else {
            entry._partsMap[pk].quantity += Number(lineItem.quantity) || 0;
            entry._partsMap[pk]._sources.push({ order: order, lineItem: lineItem });
          }
        });
      });

      pendingOrder.forEach(function (key) {
        var entry = pendingByKey[key];
        orderCounter += 1;

        // Recompute cost from parts (same reason as the
        // PO'd branch above — cached order.cost can drift
        // from current part prices, and the placed-order
        // endpoint uses current prices). Falls back to the
        // accumulated entry.cost if no parts have a price.
        var pendingPartsCostSum = 0;
        var pendingAnyPriced = false;
        Object.keys(entry._partsMap).forEach(function (pk) {
          var p = entry._partsMap[pk];
          var price = p && p.part && p.part.price ? Number(p.part.price) : NaN;
          var qty = Number(p && p.quantity) || 0;
          if (!isNaN(price)) {
            pendingPartsCostSum += price * qty;
            pendingAnyPriced = true;
          }
        });
        var pendingAssemblyCost = pendingAnyPriced
          ? pendingPartsCostSum
          : (entry.cost || 0);

        rows.push({
          id: entry.id,
          orderLabel: "Order #" + orderCounter,
          poNumber: "",
          specialInstructions: entry.specialInstructions,
          machineName: entry.machineName,
          assemblyName: entry.assemblyName,
          machineId: entry.machineId,
          assemblyId: entry.assemblyId,
          subAssemblyId: entry.subAssemblyId,
          configuredWear: entry.configuredWear,
          partCount: entry.partCount,
          cost: pendingAssemblyCost,
          // Flatten the _partsMap into an array of merged
          // part rows for template rendering. Same shape
          // the PO'd branch produces above so the modal's
          // delete-X markup doesn't need to branch on
          // PO-less vs PO'd entries.
          parts: Object.keys(entry._partsMap).map(function (pk) {
            return entry._partsMap[pk];
          }),
        });
      });

      return rows;
    },
    // Currently-active row in the Modify modal, derived
    // from activeModifyId. Null when nothing is selected.
    activeModifyOption() {
      var id = this.activeModifyId;
      if (id == null) return null;
      var match = this.modifyOptions.filter(function (o) {
        return o.id === id;
      });
      return match.length > 0 ? match[0] : null;
    },
    // Count of unique PO groups in the Modify modal. The modal's
    // rows are flattened — one per (PO, machine+assembly) — so
    // raw row count overstates the number of "orders" the user
    // perceives. The "Apply to all N orders" toggle and the gate
    // for whether to show that toggle at all both want PO-group
    // count, not row count.
    modifyUniquePoCount() {
      var seen = {};
      var count = 0;
      (this.modifyOptions || []).forEach(function (o) {
        var key = o.poNumber || ("__nopo_" + o.id);
        if (!seen[key]) {
          seen[key] = true;
          count += 1;
        }
      });
      return count;
    },
    // Hierarchical view of modifyOptions for the modal picker:
    // groups assemblies under their parent Order # (PO Number)
    // so the user sees one section per Order with the assemblies
    // listed inside it, instead of a flat run of rows where
    // siblings just happen to share an "Order #2" label. This is
    // purely visual — the radio v-model still selects an
    // individual assembly so per-category instructions can be
    // edited. The "Apply to all assemblies in this Order" toggle
    // (gated on activeModifyOrderAssemblyCount > 1) gives the
    // user an explicit broadcast option for the common case
    // where one note applies across the whole Order.
    modifyOptionsGrouped() {
      var groups = [];
      var byPo = {};
      (this.modifyOptions || []).forEach(function (opt) {
        var key = opt.poNumber || ("__nopo_" + opt.id);
        if (!byPo[key]) {
          byPo[key] = {
            key: key,
            orderLabel: opt.orderLabel,
            poNumber: opt.poNumber,
            assemblies: [],
            cost: 0,
          };
          groups.push(byPo[key]);
        }
        byPo[key].assemblies.push(opt);
        byPo[key].cost += Number(opt.cost) || 0;
      });
      return groups;
    },
    // ===== MODIFY MODAL BANNER COPY =====
    // Maps modifyContext to the title + body shown in the
    // prominent banner at the top of the modal. Returns
    // null when no banner should render. Adding a new
    // entry point is two changes: a new context value
    // and a new branch here.
    modifyBanner() {
      switch (this.modifyContext) {
        case "restored":
          return {
            title: "Cart Restored Successfully",
            body:
              "Click <strong>Go To Cart</strong> below or click any " +
              "order to make changes to the PO Number or add " +
              "Special Instructions.",
          };
        case "placing":
          return {
            title: "Ready to Place the Order?",
            body: "Confirm details below.",
          };
        case "saving":
          return {
            title: "Save Cart Order(s) for Later",
            body:
              "Review the orders below before choosing which to save.",
          };
        case "modifying":
          return {
            title: "Modify Your Cart",
            body:
              "Expand any Order to view its items. Select an " +
              "item to edit its <strong>PO Number</strong>, " +
              "<strong>Special Instructions</strong>, or to add " +
              "more parts.",
          };
        case "reordering":
          // Auto-opened on the /cart landing after the user
          // clicks RE-ORDER on an order's details page. The
          // backend clones the order with PO Numbers cleared
          // (one PO Number per real-world purchase — they
          // shouldn't carry over), so each order needs a
          // fresh PO before the cart grid renders the rows.
          // The banner copy keeps that one-step focus: this
          // is about assigning new PO Numbers, not a general
          // "modify your cart" pass.
          return {
            title: "Add a New PO Number",
            body:
              "Click the <strong>(+)</strong> icon for the " +
              "order # below to set a new PO #",
          };
        default:
          return null;
      }
    },

    // ===== REORDER BANNER COMPANY LABEL (Feature 2) =====
    // The loaded cart's company as "Name - (CODE)" for the
    // "Re-ordering for: …" banner in the Modify Order modal.
    // Prefers the exact select-list text (matches the header), then
    // falls back to the company name + id already on the component
    // (this.companyName / this.companyId), then the bare id.
    reorderCompanyDisplay() {
      var cid = this.companyId || "";
      var match = (this.companiesForLabel || []).find(function (c) {
        return String(c.value) === String(cid);
      });
      if (match && match.text) return match.text;
      if (this.companyName && cid) return this.companyName + " – " + cid;
      if (this.companyName) return this.companyName;
      return cid ? "Company " + cid : "";
    },

    // ===== PLACE-CONTEXT SELECTION COUNT =====
    // Count of Order groups the user has ticked in 'placing'
    // context. Drives enable state and the dynamic button
    // label. Empty {} → 0; unchecked checkboxes don't add
    // entries to the map. Used as the gate for the primary
    // button being clickable in 'placing' mode.
    selectedPlaceGroupCount() {
      var keys = Object.keys(this.selectedPlaceGroups || {});
      var n = 0;
      for (var i = 0; i < keys.length; i++) {
        if (this.selectedPlaceGroups[keys[i]]) n++;
      }
      return n;
    },

    // ===== REORDER: ORDER GROUPS THAT HAVE A PO =====
    // (modifyContext === 'reordering' only.) Order groups whose
    // groupPoDrafts entry is non-blank — i.e. the ones that will be
    // kept / added to the cart. Drives the "Add to your cart:" list on
    // the confirmation step. Treats any non-blank PO/Quote reference as
    // satisfying the requirement (per the reorder flow these are Order
    // carts, so the field is the PO#).
    reorderPoGroups() {
      var self = this;
      return (this.modifyOptionsGrouped || []).filter(function (g) {
        return (self.groupPoDrafts[g.key] || "").trim() !== "";
      });
    },

    // ===== REORDER: ORDER GROUPS MISSING A PO =====
    // (modifyContext === 'reordering' only.) The complement of
    // reorderPoGroups — order groups with a blank PO. These are listed
    // under "Not added (no PO/Quote reference):" on the confirmation
    // step and dropped from the cart on "Save Orders to Cart".
    reorderNoPoGroups() {
      var self = this;
      return (this.modifyOptionsGrouped || []).filter(function (g) {
        return (self.groupPoDrafts[g.key] || "").trim() === "";
      });
    },

    // ===== REORDER: AT LEAST ONE PO ENTERED =====
    // (modifyContext === 'reordering' only.) Gate for the primary
    // "Add to Cart" button — hidden/disabled until at least one order
    // group has a non-blank PO#.
    reorderHasAnyPo() {
      return this.reorderPoGroups.length > 0;
    },

    // ===== PRIMARY ACTION LABEL =====
    // The Modify modal's primary button has two faces: a
    // dirty-form "Save Changes" face and a clean-form face
    // whose label depends on what the modal is for. Single
    // computed so the template stays declarative.
    modifyPrimaryLabel() {
      if (this.savingModify) return "Saving…";
      // Reorder context: the primary action is always "Add to Cart"
      // (assign POs → add to the active cart), regardless of the
      // generic dirty-check. Checked BEFORE hasModifyChanges so a typed
      // PO doesn't flip the label to "Save Changes".
      if (this.modifyContext === "reordering") return "Add to Cart";
      if (this.hasModifyChanges) return "Save Changes";
      switch (this.modifyContext) {
        case "placing":
          // Reflect how many Orders are about to be placed.
          // Default-unchecked state means 0 → button is
          // disabled and label reads the static "Place
          // Order" so the user has a clue what action is
          // available once they pick something.
          var n = this.selectedPlaceGroupCount;
          var total = (this.modifyOptionsGrouped || []).length;
          if (n === 0) return "Place Order";
          if (n === total) {
            return n === 1 ? "Place 1 Order" : "Place All " + n + " Orders";
          }
          return n === 1 ? "Place 1 Order" : "Place " + n + " Orders";
        case "saving":
          return "Continue to Save";
        case "modifying":
          // Leaves the user on /cart after the modal closes —
          // primary action is just "I'm done editing here". The cart
          // grid re-renders with the newly-saved PO Numbers visible.
          // ('reordering' is handled above with its own "Add to Cart"
          // label and never reaches this switch.)
          return "Done";
        case "restored":
        default:
          return "Go To Cart";
      }
    },

    // ===== DIRTY-CHECK FOR MODIFY EDITOR =====
    // True when the user has typed something into the PO
    // Number or Special Instructions fields that doesn't
    // match what's currently saved on the active row.
    // Drives the action button label: "Save Changes" when
    // there's something to commit, "Go To Cart" when the
    // form is clean (in which case the button just closes
    // the modal — the cart is already in the right state).
    //
    // Watcher on activeModifyId reseeds the drafts to the
    // newly-selected row's values, which makes this
    // computed flip back to false on row switch — exactly
    // the behavior wanted ("if the customer clicks on
    // another order the 'Save Changes' should appear
    // again" only IF they then make changes; until they
    // do, the new row's clean state shows Go To Cart).
    //
    // applyInstructionsToOrder / applyInstructionsToAll
    // count as changes too — broadcasting the same value
    // to siblings is a meaningful save action even if the
    // active row's text didn't change.
    hasModifyChanges() {
      var self = this;
      // Any per-order PO changed from its current value? PO Numbers are
      // now edited per order in each header row, so a change to any of
      // them is a save action even with no assembly selected.
      var groups = this.modifyOptionsGrouped || [];
      var poChanged = groups.some(function (g) {
        return (
          (self.groupPoDrafts[g.key] || "").trim() !==
          (g.poNumber || "").trim()
        );
      });
      if (poChanged) return true;
      // Special Instructions changes — only when a row is selected in
      // the (optional) expanded editor.
      var active = this.activeModifyOption;
      if (active) {
        var savedInstr = active.specialInstructions || "";
        var draftInstr = this.modifySpecialInstructionsDraft || "";
        if (draftInstr !== savedInstr) return true;
        if (this.applyInstructionsToOrder) return true;
        if (this.applyInstructionsToAll) return true;
      }
      return false;
    },

    // How many assemblies share the active assembly's Order #
    // (PO Number). Used to gate the "Apply to all N assemblies
    // in this Order" toggle — only shown when the active Order
    // has 2+ assemblies (so there's a meaningful broadcast to
    // do). Returns 0 when nothing's selected.
    activeModifyOrderAssemblyCount() {
      var active = this.activeModifyOption;
      if (!active) return 0;
      var po = active.poNumber;
      if (!po) return 0;
      return (this.modifyOptions || []).filter(function (o) {
        return o.poNumber === po;
      }).length;
    },
    // Grand total across all PO groups + orders waiting for a PO. Used
    // by the new total row beneath the per-PO subtotals so the user has
    // one bottom-line number to glance at before checkout.
    cartGrandTotal() {
      var total = 0;
      (this.groupedOrders || []).forEach(function (g) {
        total += Number(g.cost) || 0;
      });
      (this.ordersWithoutPo || []).forEach(function (o) {
        total += Number(o.cost) || 0;
      });
      return total;
    },

    // ===== EMPTY CART / SAVED CARTS PICKER =====
    // True when there are no orders to display (the cart has nothing in it,
    // or the user just saved it for later).
    isCartEmpty() {
      return (
        (!this.ordersWithoutPo || this.ordersWithoutPo.length === 0) &&
        (!this.groupedOrders || this.groupedOrders.length === 0)
      );
    },

    // ===== RESET CART MODAL SUMMARY =====
    // Flattens groupedOrders into a list of assembly rows (machine name,
    // assembly name, part count, subtotal) for display in the reset-cart
    // confirmation modal. Also computes the overall totals so the user
    // sees exactly what they're about to lose.
    resetSummary() {
      var rows = [];
      var totalAssemblies = 0;
      var totalParts = 0;
      var totalCost = 0;

      (this.groupedOrders || []).forEach(function (poGroup) {
        (poGroup.machineGroups || []).forEach(function (mg) {
          var partCount = (mg.mergedParts || []).reduce(function (sum, p) {
            return sum + (Number(p.quantity) || 0);
          }, 0);
          rows.push({
            poNumber: poGroup.number,
            machineName: mg.machineName,
            assemblyName: mg.assemblyName,
            partCount: partCount,
            cost: mg.cost || 0,
          });
          totalAssemblies += 1;
          totalParts += partCount;
          totalCost += mg.cost || 0;
        });
      });

      // Orders still waiting for a PO aren't in groupedOrders but will
      // also be deleted — include them as a summary row.
      var orphanCount = (this.ordersWithoutPo || []).length;

      return {
        rows: rows,
        totalAssemblies: totalAssemblies,
        totalParts: totalParts,
        totalCost: totalCost,
        orphanCount: orphanCount,
      };
    },
    // All saved carts from the current company, sorted newest first.
    sortedSavedCarts() {
      var list = (this.savedCartsList || []).slice();
      list.sort(function (a, b) {
        var da = a && a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
        var db = b && b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
        return db - da;
      });
      return list;
    },
    // Saved orders (non-quote), top 5 most recent.
    recentSavedOrders() {
      var self = this;
      return this.sortedSavedCarts
        .filter(function (c) {
          var t = self.getCartType(c);
          return t !== "SavedQuote" && t !== "ProcessedQuote";
        })
        .slice(0, 5);
    },
    // Saved quotes, top 5 most recent.
    recentSavedQuotes() {
      var self = this;
      return this.sortedSavedCarts
        .filter(function (c) {
          var t = self.getCartType(c);
          return t === "SavedQuote" || t === "ProcessedQuote";
        })
        .slice(0, 5);
    },
    hasSavedCarts() {
      return (
        this.recentSavedOrders.length > 0 || this.recentSavedQuotes.length > 0
      );
    },
    // True when there are more than 5 orders OR more than 5 quotes saved —
    // i.e. the "View all saved carts" link is worth showing.
    hasMoreSavedCarts() {
      var self = this;
      var totalOrders = this.sortedSavedCarts.filter(function (c) {
        var t = self.getCartType(c);
        return t !== "SavedQuote" && t !== "ProcessedQuote";
      }).length;
      var totalQuotes = this.sortedSavedCarts.filter(function (c) {
        var t = self.getCartType(c);
        return t === "SavedQuote" || t === "ProcessedQuote";
      }).length;
      return totalOrders > 5 || totalQuotes > 5;
    },
    // Defensive master/admin detection. Role information may arrive in
    // several shapes depending on the backend version, so we check all of
    // them: explicit flags, a single role string, and a roles array that
    // may contain strings or {name} objects. Matches case-insensitively
    // against "master" or "admin".
    currentUser() {
      if (this.$store && this.$store.state) {
        return (
          this.$store.state.currentUser ||
          this.$store.state.user ||
          (this.$store.getters && this.$store.getters.currentUser) ||
          null
        );
      }
      return null;
    },
  },
  mounted() {
    if (localStorage.cartId && localStorage.cartId > 0) {
      this.cartId = localStorage.cartId;

      if (this.$route.params.fullOrder) {
        this.fullOrder = this.$route.params.fullOrder;
        this.saveCart();
      } else {
        this.getCart();
      }
    } else {
      if (this.$route.params.fullOrder && localStorage.companyId) {
        this.fullOrder = this.$route.params.fullOrder;
        this.saveCart();
      }
    }

    // Always attempt to load saved carts so the empty-cart state has data
    // to show. Works with or without a selected company.
    this.loadSavedCarts();
    this.loadWearPartIds();
    this.loadCompanyWearPartIds();
    // Company select-list (same source as the header) — used only to
    // resolve the "Name - (CODE)" label for the reorder banner in the
    // Modify Order modal (Feature 2). Non-blocking; the banner falls
    // back to this.companyName + companyId if it isn't loaded yet.
    this.loadCompaniesForLabel();

    // ===== ENTRY-POINT MODAL TRIGGER =====
    // Honors ?openModify=<context> on initial mount.
    // /account/savedcarts Resume sends users here with
    // ?openModify=restored. consumeOpenModifyQuery handles
    // both initial-mount and route-change cases (the
    // route-change watcher below also calls it).
    this.consumeOpenModifyQuery();

    // Poll for changes to localStorage.companyId so that if the user
    // selects or switches company via the nav dropdown while on this
    // page, the saved-carts section refreshes to match.
    // Mirrors the pattern used in SavedCarts.vue.
    this._currentCompanyIdForWatch = localStorage.companyId || "";
    this._companyCheckInterval = setInterval(function () {
      var newCompanyId = localStorage.companyId || "";
      if (newCompanyId !== this._currentCompanyIdForWatch) {
        this._currentCompanyIdForWatch = newCompanyId;
        this.loadSavedCarts();
        this.loadWearPartIds();
        this.loadCompanyWearPartIds();
      }
    }.bind(this), 500);
  },

  beforeDestroy() {
    if (this._companyCheckInterval) {
      clearInterval(this._companyCheckInterval);
    }
  },
  data() {
    return {
      cartId: 0,
      companyId: localStorage.companyId,
      // Snapshot the selected company at page load — captured here in data()
      // BEFORE getCart() can switch it to a reorder cart's (different)
      // company, so a reorder cancel can restore the original selection.
      reorderPrevCompanyId: localStorage.companyId || "",
      companyName: "",
      // Company select-list [{value,text:"Name - (CODE)"}] used to
      // render the reorder banner label in the Modify Order modal
      // (Feature 2). Same source the header uses.
      companiesForLabel: [],
      poNumber: localStorage.poNumber,
      editPoNumber: false,
      editPoNum: false,
      configuredWear: true,
      partsList: [],
      machineId: 0,
      orders: [],
      // Modify Order modal — opens when the user clicks
      // "Modify Order" in the cart header. Lists each
      // (Order#, assembly) combo so the user can pick one
      // and jump back to /orders/create with the matching
      // Steps 1-3 pre-filled, keeping the same PO Number.
      showModifyModal: false,
      // ===== EXPANDED-GROUP STATE =====
      // The redesigned Modify modal collapses each Order to
      // a single header row by default — title, PO, assembly
      // count, total cost. Clicking the chevron next to the
      // header expands the group to show its assembly rows
      // for selection. Stored as a Set-like map keyed by
      // group.key (the same key modifyOptionsGrouped uses)
      // so toggle is O(1) and the template can do
      // `expandedModifyGroups[grp.key]` for visibility checks.
      //
      // Auto-expand behavior is in onModifyModalOpen — the
      // group containing activeModifyId starts expanded so
      // the user can see their pre-selected row.
      expandedModifyGroups: {},

      // ===== MODIFY MODAL CONTEXT =====
      // String discriminator describing why the Modify Order
      // modal is open. Set by whichever caller triggers the
      // modal (openModifyForRestore / openModifyForPlace /
      // openModifyForSave / openModifyForModify) and read by
      // the banner template to render the right title +
      // subtitle and by saveModifyChanges to dispatch to the
      // right post-confirm action. Cleared in
      // onModifyModalClose.
      //
      // Values:
      //   'restored' — user just resumed a cart from
      //     /account/savedcarts; banner reads "Cart Restored
      //     Successfully"; primary action returns user to
      //     /cart with no further side effects.
      //   'modifying' — user clicked the Modify Order header
      //     button on /cart; no banner (the user opened it
      //     deliberately, doesn't need a "what is this for"
      //     cue); primary action just closes back to /cart.
      //   'placing' — user clicked Place Order on /cart;
      //     banner reads "Ready to Place the Order?";
      //     primary action runs the existing finalizeOrder
      //     flow (single Order → place direct; multi → open
      //     Place Order Picker).
      //   'saving' — user clicked Save Cart Order(s) on
      //     /cart; banner reads "Save Cart Order(s) for
      //     Later"; primary action opens the existing Save
      //     Cart Picker modal for per-Order selection.
      //   null — modal closed.
      modifyContext: null,

      // Single-shot queue for ?openModify=<context> when the
      // query lands before getCart has populated this.orders.
      // Set in consumeOpenModifyQuery, drained by the orders
      // watcher when the cart finally has rows.
      _pendingOpenModifyContext: null,

      // ===== PLACE-CONTEXT SELECTION =====
      // When the Modify modal is opened in 'placing' context,
      // each Order header row gets a checkbox. selectedPlaceGroups
      // is a Set-like map keyed by group.key. All Orders start
      // UNCHECKED — the user has to deliberately tick the
      // Orders they want to place. Unchecked Orders stay in
      // the cart. Drives:
      //   - Place button enabled state
      //   - Place button label ("Place 2 Orders" etc.)
      //   - confirmPlaceFromModify which dispatches to
      //     either direct place (all groups selected) or
      //     the existing partial-split flow that moves
      //     unselected Orders to a new active cart.
      // Cleared on modal close.
      selectedPlaceGroups: {},
      // Modify Order modal editor state — see RFQSavedItems
      // for full design rationale. Drafts are committed on
      // Save Changes; applyInstructionsToAll broadcasts the
      // instructions to every Order in one shot, and
      // applyInstructionsToOrder broadcasts within the active
      // assembly's Order# only (across its sibling assemblies).
      activeModifyId: null,
      modifyPoNumberDraft: "",
      modifySpecialInstructionsDraft: "",
      applyInstructionsToAll: false,
      applyInstructionsToOrder: false,
      // When true, Save applies the entered PO Number to EVERY order
      // (single shared PO) instead of just the selected order. Lets the
      // user pick "one PO for all" vs. a distinct PO per order.
      applyPoToAll: false,
      // Per-order PO Number drafts, keyed by modify-group key. Seeded
      // from each Order's current PO when the modal opens; the Order
      // header inputs bind to these, and Save Changes applies them all
      // at once so the user can set every order's PO in one pass.
      groupPoDrafts: {},
      savingModify: false,
      // ===== REORDER CONTEXT (modifyContext === 'reordering') =====
      // The reorder flow creates a server cart up front and auto-opens
      // the Modify modal so the user assigns a fresh PO to each cloned
      // order. These two flags drive the reorder-only behavior and are
      // reset on every modal open/close; they're inert in the
      // placing/saving/modifying/restored contexts.
      //   reorderConfirmStep — when true, the modal swaps its order/PO
      //     list for a confirmation step (some orders have a PO, others
      //     don't) listing what will be added vs. dropped.
      //   reorderConfirmed — set true ONLY on a successful "Add to Cart"
      //     / "Save Orders to Cart" path so onModifyModalClose knows NOT
      //     to discard the cart. A cancel (✕ / backdrop / Esc) leaves it
      //     false → the phantom reorder cart is discarded.
      reorderConfirmStep: false,
      reorderConfirmed: false,
      mainPO: "",
      total: 0,
      placeOrder: false,
      fullOrder: "",
      SpecialInstructions: "",
      search: {
        PONumber: "",
        keyPartNumber: "",
      },
      displayParts: {
        checked: [],
      },
      billingCompanyName: "",
      billingStreet: "",
      billingStreet2: "",
      billingCity: "",
      billingState: "",
      billingPostalCode: "",
      shippingCompanyName: "",
      shippingStreet: "",
      shippingStreet2: "",
      shippingCity: "",
      shippingState: "",
      shippingPostalCode: "",
      partsFields: [
        "item",
        "code",
        "description",
        { key: "quantityPerMachine", label: "QTY PER MACHINE" },
        "price",
        { key: "quantity", label: "Cart Qty" },
        "cost",
        { key: "remove", label: "" },
      ],
      clear: "",
      cartError: false,
      hasPo: false,
      savingCart: false,
      // ===== SAVE CART PICKER MODAL =====
      // Mirrors the RFQ-side Save for Later picker.
      // showSaveCartPickerModal — visibility flag.
      // selectedOrdersForPicker — array of order ids the
      //   user has ticked. Defaults to all selected on
      //   modal open (most common case is "save the lot").
      showSaveCartPickerModal: false,
      selectedOrdersForPicker: [],
      // ===== PLACE ORDER PICKER MODAL =====
      // Lets the user pick a subset of Orders (PO groups) to
      // submit when their cart contains 2+ Orders. Mirrors
      // the Save Cart picker flow above:
      // selectedOrdersForPlace holds PO Numbers (group.number)
      // because multi-assembly Orders share one group key but
      // map to multiple this.orders rows — selecting "Order #2"
      // should place every assembly under PO 2 together,
      // never half of them.
      showPlaceOrderPickerModal: false,
      selectedOrdersForPlace: [],
      // Tracks which PO group's instructions block is
      // expanded (showing full text vs. truncated preview).
      // Keyed by PO Number → boolean. Long instructions
      // would otherwise dominate the card, so we collapse
      // anything past ~140 chars to a one-line preview with
      // a "Show more" toggle.
      expandedInstructions: {},
      // Tracks which machine+assembly cards are expanded.
      // Keyed by "<poNumber>__<machineGroupKey>" → boolean.
      expandedGroups: {},
      // PO group inline edit state
      editingPoNumber: null,
      editingPoValue: "",
      // Saved carts (used by the empty-cart state to offer a resume picker)
      savedCartsList: [],
      loadingSavedCarts: false,
      // Map of PartId → true for the GLOBAL wear-eligible
      // catalog (wear parts across ALL companies). Loaded on
      // mount and consulted by goToSearch()/cartHasNonWearPart()
      // to decide whether to pass allParts=true (when the cart
      // contains any non-wear part) so the Search page doesn't
      // filter the user's actual cart contents out of the
      // results. This MUST stay global so the allParts
      // search-filter decision is unchanged.
      wearPartIds: {},
      // Map of PartId → true for THIS company's own wear parts
      // (its dbo.Wears = the Account Setup wear list). Drives
      // ONLY the "2% Discount" badge via isWearEligible(), so
      // the chip reflects the customer's own wear list rather
      // than the global catalog. Loaded alongside wearPartIds
      // (mount + company change) via loadCompanyWearPartIds().
      companyWearPartIds: {},
    };
  },
  watch: {
    // Refresh modal drafts when the user picks a different
    // order in the Modify modal. Without this, drafts
    // persist from the previously-edited row and silently
    // overwrite siblings on Save.
    activeModifyId(newId) {
      if (newId == null) {
        this.modifyPoNumberDraft = "";
        this.modifySpecialInstructionsDraft = "";
        return;
      }
      var match = (this.modifyOptions || []).filter(function (o) {
        return o.id === newId;
      });
      if (match.length > 0) {
        this.modifyPoNumberDraft = match[0].poNumber || "";
        this.modifySpecialInstructionsDraft = match[0].specialInstructions || "";
      }
    },

    // Watch the route's openModify query param so a Resume
    // click while ALREADY on /cart still triggers the modal.
    // Vue Router reuses the same component instance for
    // navigation within the same route, so mounted() doesn't
    // fire — the watcher is the only signal we get.
    "$route.query.openModify"(newCtx, oldCtx) {
      if (newCtx && newCtx !== oldCtx) {
        this.consumeOpenModifyQuery();
      }
    },

    // When this.orders finally populates (after getCart's
    // network response), kick consumeOpenModifyQuery again
    // in case it ran earlier with an empty cart and bailed.
    // Single-shot via _pendingOpenModify flag below.
    orders() {
      if (this._pendingOpenModifyContext && (this.orders || []).length > 0) {
        var ctx = this._pendingOpenModifyContext;
        this._pendingOpenModifyContext = null;
        this.applyOpenModifyContext(ctx);
      }
    },
  },
  methods: {
    // ===== SAVED CARTS (empty-cart state picker) =====
    // Fetches the user's saved carts. Used only by the empty-cart state
    // to offer a "resume" picker for the 5 most recent orders and quotes.
    // Works with OR without a selected company — if no company is set in
    // localStorage, the backend returns all carts the user has access to
    // across companies, and each row still shows its own companyName.
    loadSavedCarts() {
      var self = this;
      var companyId = localStorage.companyId || "";
      self.loadingSavedCarts = true;
      apiService
        .getActiveCarts(companyId ? { companyId: companyId } : {})
        .then(function (data) {
          self.savedCartsList = data || [];
          self.loadingSavedCarts = false;
        })
        .catch(function () {
          self.savedCartsList = [];
          self.loadingSavedCarts = false;
        });
    },

    // Pull the wear-eligible PartIds for the current company
    // and store as { [partId]: true } for O(1) lookup. Used
    // by goToSearch to decide whether the cart contains any
    // non-wear parts — when it does, we pass allParts=true
    // in the URL so the Search page doesn't filter the user's
    // own cart contents out of the results.
    //
    // Defensive against missing method (older API service),
    // missing companyId, non-array response, network failure.
    loadWearPartIds() {
      if (!apiService.getWearPartIds) {
        this.wearPartIds = {};
        return;
      }
      var companyId = localStorage.companyId || "";
      if (!companyId) {
        this.wearPartIds = {};
        return;
      }
      var self = this;
      apiService
        .getWearPartIds({ companyId: companyId })
        .then(function (data) {
          var map = {};
          if (Array.isArray(data)) {
            data.forEach(function (id) {
              if (id != null) map[id] = true;
            });
          }
          self.wearPartIds = map;
        })
        .catch(function () {
          self.wearPartIds = {};
        });
    },

    // Pull THIS company's own wear PartIds (its dbo.Wears = the
    // Account Setup wear list) into companyWearPartIds, used
    // ONLY by isWearEligible() for the "2% Discount" badge so
    // the chip reflects the customer's own wear list rather than
    // the global wear-eligible catalog. Kept SEPARATE from the
    // global wearPartIds map above, which still drives the
    // allParts search-filter decision (cartHasNonWearPart) and
    // must not change. Loaded wherever loadWearPartIds is called
    // (mount + company change). Failure (or a pre-deploy 404 on
    // the new endpoint) → empty map (badge simply doesn't render).
    loadCompanyWearPartIds() {
      if (!apiService.getCompanyWearPartIds) {
        this.companyWearPartIds = {};
        return;
      }
      var companyId = localStorage.companyId || "";
      if (!companyId) {
        this.companyWearPartIds = {};
        return;
      }
      var self = this;
      apiService
        .getCompanyWearPartIds(companyId)
        .then(function (data) {
          var map = {};
          if (Array.isArray(data)) {
            data.forEach(function (id) {
              if (id != null) map[id] = true;
            });
          }
          self.companyWearPartIds = map;
        })
        .catch(function () {
          self.companyWearPartIds = {};
        });
    },

    // True if any part anywhere in the user's active cart is
    // NOT in the company's wear-parts catalog. Drives the
    // allParts=true URL flag from goToSearch — when the user
    // returns to search after working with a cart that has
    // non-wear content, we don't want the Search page to
    // pre-filter to wear-only and hide what they came to add
    // more of.
    cartHasNonWearPart() {
      if (!this.orders || this.orders.length === 0) return false;
      // If the wear map hasn't loaded yet (network in flight,
      // missing endpoint, or no companyId), default to TRUE
      // — safer to surface all parts on the Search page than
      // to silently filter the user's own cart out.
      if (!this.wearPartIds || Object.keys(this.wearPartIds).length === 0) {
        return true;
      }
      for (var i = 0; i < this.orders.length; i++) {
        var po = this.orders[i];
        if (!po.parts) continue;
        for (var j = 0; j < po.parts.length; j++) {
          var p = po.parts[j];
          var pid = p && p.part ? (p.part.id || p.part.partId) : null;
          if (pid != null && !this.wearPartIds[pid]) {
            return true;
          }
        }
      }
      return false;
    },
    // Classify a cart as Order or Quote. Mirrors SavedCarts.vue's logic so
    // the two pages agree on how to bucket each cart.
    getCartType(cart) {
      if (!cart) return "SavedOrder";
      if (cart.cartType) return cart.cartType;
      if (cart.discriminator === "Rfq" || cart.discriminator === 1) {
        return "ProcessedQuote";
      }
      return cart.hasPoNumber ? "SavedOrder" : "SavedQuote";
    },
    // Human-friendly date for saved-cart rows (e.g. "Mar 14, 2026").
    formatSavedDate(dateStr) {
      if (!dateStr) return "";
      try {
        var d = new Date(dateStr);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      } catch (e) {
        return "";
      }
    },
    // Resume a saved cart: mirrors the flow in SavedCarts.vue. Orders route
    // back to /cart (with the selected cartId primed); quotes route to the
    // RFQ saved items view.
    resumeSavedCart(cart) {
      if (!cart) return;
      localStorage.companyId = cart.companyId;
      localStorage.cartLastActivity = Date.now().toString();

      var cartType = this.getCartType(cart);
      var self = this;

      if (cartType === "SavedQuote" || cartType === "ProcessedQuote") {
        localStorage.cartIdRFQ = cart.id;
        if (self.$cartBus) self.$cartBus.$emit("cart-updated");
        self.$router.push("/cart/rfqsaveditems");
        return;
      }

      // Order: load this cart into the current view in place.
      localStorage.cartId = cart.id;
      self.cartId = cart.id;
      self.poNumber = cart.poNumber || localStorage.poNumber || "";
      if (cart.poNumber) localStorage.poNumber = cart.poNumber;
      if (self.$cartBus) self.$cartBus.$emit("cart-updated");
      self.getCart();
    },

    // ===== GROUPING HELPER =====
    // Used by groupedOrders to render the user's cart. Groups orders by
    // PO number → machine+assembly name, then dedupes parts within each
    // machine+assembly by code and sums their quantities.
    buildGroupedOrders(orders) {
      var poGroups = {};

      (orders || [])
        .filter(function (o) { return !!o.number; })
        .forEach(function (order) {
          // --- PO level ---
          if (!poGroups[order.number]) {
            poGroups[order.number] = {
              number: order.number,
              cost: 0,
              _machineMap: {},
            };
          }
          var poGroup = poGroups[order.number];

          // --- Machine + Assembly level ---
          var machineName =
            (order.assembly && order.assembly.machine && order.assembly.machine.description) || "";
          var assemblyName = (order.assembly && order.assembly.name) || "";
          var mKey = machineName + "||" + assemblyName;

          if (!poGroup._machineMap[mKey]) {
            poGroup._machineMap[mKey] = {
              key: mKey,
              machineName: machineName,
              assemblyName: assemblyName,
              backingOrders: [],
              primaryOrder: order,
              cost: 0,
              _partsMap: {},
            };
          }
          var mGroup = poGroup._machineMap[mKey];
          mGroup.backingOrders.push(order);
          mGroup.cost += order.cost || 0;
          poGroup.cost += order.cost || 0;

          // --- Part dedup level ---
          (order.parts || []).forEach(function (lineItem) {
            if (!lineItem || !lineItem.part) return;
            var code =
              lineItem.part.code ||
              lineItem.part.item ||
              ("__id_" + lineItem.id);
            if (!mGroup._partsMap[code]) {
              mGroup._partsMap[code] = {
                id: lineItem.id,
                part: lineItem.part,
                quantity: Number(lineItem.quantity) || 0,
                _sources: [{ order: order, lineItem: lineItem }],
              };
            } else {
              mGroup._partsMap[code].quantity += Number(lineItem.quantity) || 0;
              mGroup._partsMap[code]._sources.push({ order: order, lineItem: lineItem });
            }
          });
        });

      return Object.keys(poGroups).map(function (poKey) {
        var po = poGroups[poKey];

        // Build the machineGroups list. Each one carries its
        // OWN specialInstructions, pooled across that group's
        // backing PurchaseOrder rows. This is what populates
        // the per-assembly readout under each card heading
        // (POINT AUGERS, CORE PIN FOR K DIE, etc.).
        var machineGroups = Object.keys(po._machineMap).map(function (mKey) {
          var mg = po._machineMap[mKey];
          var groupInstr = "";
          (mg.backingOrders || []).forEach(function (bo) {
            if (bo.specialInstructions
                && String(bo.specialInstructions).trim() !== "") {
              groupInstr = bo.specialInstructions;
            }
          });
          return {
            key: mg.key,
            machineName: mg.machineName,
            assemblyName: mg.assemblyName,
            specialInstructions: groupInstr,
            backingOrders: mg.backingOrders,
            primaryOrder: mg.primaryOrder,
            cost: mg.cost,
            mergedParts: Object.keys(mg._partsMap).map(function (k) {
              return mg._partsMap[k];
            }),
          };
        });

        // PO-level "consolidated" check: do every machineGroup
        // under this PO carry the SAME non-empty value? If yes
        // the Order header readout shows the consolidated text
        // (signalling "this applies across the whole Order"). If
        // some assemblies have different or empty values, the
        // header stays hidden and per-assembly readouts on each
        // card handle display instead — avoids the misleading
        // case of a PO header showing one assembly's text as if
        // it applied Order-wide.
        var consolidated = false;
        var consolidatedText = "";
        if (machineGroups.length > 0) {
          var firstInstr = machineGroups[0].specialInstructions || "";
          if (firstInstr) {
            var allMatch = true;
            for (var i = 1; i < machineGroups.length; i++) {
              if ((machineGroups[i].specialInstructions || "") !== firstInstr) {
                allMatch = false;
                break;
              }
            }
            consolidated = allMatch;
            consolidatedText = allMatch ? firstInstr : "";
          }
        }

        return {
          number: po.number,
          cost: po.cost,
          specialInstructions: consolidatedText,
          instructionsConsolidated: consolidated,
          machineGroups: machineGroups,
        };
      });
    },

    getCart() {
      apiService
        .getCart({
          id: this.cartId,
          fullOrder: this.fullOrder,
          SpecialInstructions: this.SpecialInstructions,
        })
        .then((request) => {
          this.getCartSuccessful(request);
          this.getSubTotals();
          this.getTotal();
          this.getCompany();
        })
        .catch((err) => this.getCartFailed(err));
    },
    getCartSuccessful(request) {
      localStorage.companyId = request.companyId;
      (this.cartId = request.id),
        (this.fullOrder = request),
        (this.orders = request.purchaseOrders),
        (this.companyId = request.companyId),
        (this.SpecialInstructions = request.specialInstructions);

      if (this.$route.params.reorder == true) {
        this.orders.forEach((order) => {
          $(order).each(function() {
            order.number = null;
          });
        });

        // ===== AUTO-OPEN MODIFY MODAL ON REORDER =====
        // Backend's /cart/reorder/{id} clears every PO Number
        // on the cloned cart (because PO Numbers shouldn't
        // carry over between purchases — each order needs a
        // fresh PO from the buyer). With every order PO-less,
        // groupedOrders filters them all out and the cart
        // grid renders empty even though orders.length > 0,
        // which looks broken to the user.
        //
        // The legacy "Warning: enter a PO" inline banner was
        // removed in favor of the existing Modify Order
        // modal — but the auto-open trigger that was supposed
        // to bridge that gap was never wired up. This is
        // that wiring: pop the Modify modal in 'modifying'
        // context on $nextTick so the user lands on a focused
        // editor for assigning PO Numbers + Special
        // Instructions before they continue. Once they save,
        // the orders flow into groupedOrders and render in
        // the grid normally.
        var hasPolessOrder = (this.orders || []).some(function (o) {
          return !o.number || String(o.number).trim() === "";
        });
        if (hasPolessOrder) {
          // Use the reorder-specific opener so the modal's
          // banner reads "Add a New PO Number / Click the (+)
          // icon..." instead of the generic 'modifying'
          // copy. See modifyBanner computed for the text.
          this.$nextTick(function () {
            this.openModifyForReorder();
          }.bind(this));
        }
      }
    },
    getCartFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    getCompany() {
      if (this.companyId) {
        apiService
          .getCompanyDetails({
            id: this.companyId,
          })
          .then((request) => {
            this.getCompanySuccessful(request);
          })
          .catch((err) => this.getCompanyFailed(err));
      }
    },
    getCompanySuccessful(request) {
      this.companyName = request.name;

      (this.billingCompanyName = request.plant.name),
        (this.billingStreet = request.plant.street),
        (this.billingStreet2 = request.plant.street2),
        (this.billingCity = request.plant.city),
        (this.billingState = request.plant.state),
        (this.billingPostalCode = request.plant.postalCode),
        (this.shippingCompanyName = request.shipTo.name),
        (this.shippingStreet = request.shipTo.street),
        (this.shippingStreet2 = request.shipTo.street2),
        (this.shippingCity = request.shipTo.city),
        (this.shippingState = request.shipTo.state),
        (this.shippingPostalCode = request.shipTo.postalCode);
    },
    getCompanyFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    // Load the company select-list (same call the header makes) so
    // the reorder banner (Feature 2) can show the exact "Name -
    // (CODE)" label. Failure is non-fatal — reorderCompanyDisplay
    // falls back to this.companyName + id.
    loadCompaniesForLabel() {
      apiService
        .getCompanyList()
        .then((data) => {
          this.companiesForLabel = data || [];
        })
        .catch(() => {
          this.companiesForLabel = [];
        });
    },
    saveCart() {
      // ===== DELETE-RECREATE PATTERN =====
      // Why we don't just call saveCart({ id: cartId }):
      // SaveOldCart server-side runs DoesPartExistInTheCart
      // before saving and returns 400 ("This part is already
      // added. Please adjust the quantity in the Cart.") when
      // ANY incoming PO matches a row already on the cart.
      // For the saveCart-on-mount case the route's fullOrder
      // is a SNAPSHOT of the existing cart (passed through
      // router params), so by definition every part is already
      // on the cart and the check rejects every save.
      //
      // Result before this fix: the mount-time saveCart failed
      // silently when route params carried the fullOrder
      // payload, this.orders never refreshed from the server,
      // and any per-PO state (PO Numbers, Special Instructions)
      // appeared to "not persist" because the page rendered
      // stale data.
      //
      // Mirroring saveModifyChanges' fix: delete the existing
      // cart row, then save with id=0 so the request routes to
      // SaveNewCart (no duplicate check). The new cart inherits
      // the same parts + state, identical to before minus the
      // dropped row.
      var self = this;
      var existingCartId = parseInt(self.cartId, 10) || 0;

      var doSave = function () {
        return apiService.saveCart({
          id: 0,
          parts: self.$route.params.parts,
          number: self.$route.params.number,
          purchaseOrders: self.fullOrder.purchaseOrders,
          companyId: self.$route.params.companyId,
          specialInstructions: self.SpecialInstructions,
        });
      };

      var savePromise;
      if (existingCartId > 0) {
        savePromise = apiService
          .deleteCart({ id: existingCartId })
          .catch(function (e) {
            // Soft-fail: a delete error doesn't block the
            // recreate. Worst case the old cart lingers in
            // /account/savedcarts as a duplicate; the new
            // cart still gets created and is the active one.
            console.warn(
              "[Cart saveCart] deleteCart before recreate failed:",
              e
            );
          })
          .then(doSave);
      } else {
        savePromise = doSave();
      }

      savePromise
        .then(function (request) {
          self.saveCartSuccessful(request);
          // saveCartSuccessful sets self.cartId from the
          // response; mirror that into localStorage so future
          // page loads bind to the freshly-created cart.
          try {
            localStorage.cartId = self.cartId;
          } catch (e) {
            // Storage disabled — surfaces nowhere critical.
          }
          self.getSubTotals();
          self.getTotal();
          self.getCompany();
        })
        .catch(function (err) {
          self.saveCartFailed(err);
        });
    },
    saveCartSuccessful(request) {
      if (this.$route.params.reorder == true) {
        this.orders.forEach((order) => {
          $(order).each(function() {
            order.number = null;
          });
        });
      }
      (this.cartId = request.id),
        (this.fullOrder = request),
        (this.total = request.cost),
        (this.orders = request.purchaseOrders),
        (this.SpecialInstructions = request.specialInstructions);
      if (this.$cartBus) {
        this.$cartBus.$emit('cart-updated');
      }
    },
    saveCartFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    changePoNumber(num) {
      localStorage.poNumber = num;

      this.editPoNumber = !this.editPoNumber;

      apiService
        .updatePONumbers({
          cartId: this.cartId,
          poId: this.orders[this.orders.length - 1].id,
          newPoNumber: num,
        })
        .then(() => {
          this.orders[this.orders.length - 1].number = num;
        })
        .catch();
    },
    changePoNumberAfterUpdate(num) {
      localStorage.poNumber = num;
      this.poNumber = num;
    },
    clearPoNumber() {
      this.$bvModal
        .msgBoxConfirm("Are you sure you want to clear all PO numbers on this cart?", {
          title: "Clear PO Numbers",
          id: "clearModal",
          size: "md",
          buttonSize: "sm",
          okVariant: "dark",
          okTitle: "Yes, clear them",
          cancelVariant: "outline",
          cancelTitle: "No, keep them",
          hideHeaderClose: true,
          centered: true,
        })
        .then((clear) => {
          if (clear) {
            apiService
              .updatePONumbers({
                cartId: this.cartId,
                poId: 0,
                newPoNumber: "",
              })
              .then(() => {
                this.mainPO = "";
                for (let i in this.orders) {
                  if (this.orders[i].id) {
                    this.orders[i].number = "";
                    self.hasPo = false;
                  }
                }
                this.hasPo = false;
                this.editPoNum = false;
                this.$forceUpdate(this.orders);
                this.$forceUpdate(this.mainPO);
              })
              .catch();
          }
        })
        .catch((err) => console.log(err));
    },
    updatePoNumber(order, from) {
      if (!from) {
        order.number = order.newPO;

        var idx = $.inArray(order, this.orders);

        if (idx == -1) {
          this.orders.push(order);
        } else {
          for (let i in this.orders) {
            if (this.orders[i].id == order.id) {
              this.orders[i].number = order.number;

              break; //Stop this loop, we found it!
            }
          }
        }

        apiService
          .updatePONumbers({
            cartId: this.cartId,
            poId: from ? 0 : order.id,
            newPoNumber: from ? this.mainPO : order.newPO,
          })
          .then(() => {
            if (from) {
              for (let i in this.orders) {
                this.orders[i].number = this.mainPO;
              }
            }
          })
          .catch();
      } else {
        apiService
          .updatePONumbers({
            cartId: this.cartId,
            poId: 0,
            newPoNumber: from ? this.mainPO : order.newPO,
          })
          .then(() => {
            if (from) {
              for (let i in this.orders) {
                this.orders[i].number = this.mainPO;
              }
            }
          });
      }
    },
    updatePoNum(order) {
      var idx = $.inArray(order, this.orders);

      if (idx == -1) {
        this.orders.push(order);
      } else {
        for (let i in this.orders) {
          if (this.orders[i].id == order.id) {
            this.orders[i].number = order.number;

            break; //Stop this loop, we found it!
          }
        }
      }

      this.editPoNum = !this.editPoNum;

      apiService
        .updatePONumbers({
          cartId: this.cartId,
          poId: order.id,
          newPoNumber: order.number,
        })
        .then(() => {
          //top nav PO Number can be only updated if last order is edited
          if (this.orders[this.orders.length - 1].id == order.id) {
            this.changePoNumberAfterUpdate(order.number)
          }
        })
        .catch();
    },
    getSubTotals() {
      this.orders.forEach((order) => {
        var subtotal = 0;
        $(order.parts).each(function() {
          subtotal = subtotal + this.part.price * this.quantity;

          order.cost = subtotal;
        });
      });
    },
    updateSubtotal(order) {
      var subtotal = 0;

      if (order.parts.length) {
        for (var i in order.parts) {
          subtotal = subtotal + order.parts[i].part.cost;
        }
        order.cost = subtotal;

        this.$forceUpdate(order);
      } else if (order.parts.length <= 0) {
        order.cost = 0;
        this.$forceUpdate();
      }
      apiService
        .updatePurchaseOrder({
          id: order.id,
          Number: order.number,
          parts: order.parts,
        })
        .then(() => {
          if (order.parts.length < 1) {
            this.deletePO(order);
          }
          this.getSubTotals();
          this.getTotal();
          if (this.$cartBus) {
            this.$cartBus.$emit('cart-updated');
          }
        })
        .catch((err) => this.updateCartFailed(err));
    },
    updateCartSuccessful(request) {
      this.SpecialInstructions = request.specialInstructions;
    },
    updateCartFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    getTotal() {
      var total = 0;
      this.orders.forEach((order) => {
        if (order.cost > 0) {
          total = total + order.cost;
        } else {
          total = total + 0;
        }
      });

      this.total = total;
    },
    showDeleteModal() {
      this.$bvModal.show("resetCartModal");
    },
    // Called from the Yes button inside the custom resetCartModal. Splits
    // the "confirm" and "execute" responsibilities so the template can
    // stay declarative.
    confirmResetCart() {
      this.$bvModal.hide("resetCartModal");
      if (!this.cartId) return;
      apiService
        .deleteCart({ id: this.cartId })
        .then((request) => {
          this.deleteCartSuccessful(request);
        })
        .catch((err) => this.deleteCartFailed(err));
    },
    deleteCartSuccessful() {
      // Clear cart-specific localStorage entries, but KEEP the selected
      // company so the empty-state picker still shows saved carts + team
      // carts filtered to that company. This replaces the old behavior
      // that redirected to "Create New Order" and blew away the company.
      localStorage.cartId = 0;
      delete localStorage.poNumber;
      delete localStorage.machineId;
      delete localStorage.assemblyId;

      // Reset component state so the cart renders its empty-state view
      // without a page navigation. isCartEmpty flips true when orders
      // and ordersWithoutPo both clear.
      this.cartId = 0;
      this.orders = [];
      this.fullOrder = null;
      this.poNumber = "";
      this.mainPO = "";
      this.SpecialInstructions = "";
      this.subTotals = {};
      this.cartTotal = 0;
      this.expandedGroups = {};
      this.editingPoNumber = null;
      this.editingPoValue = "";

      if (this.$cartBus) {
        this.$cartBus.$emit("cart-updated");
      }

      // Refresh the pickers so the empty state is populated immediately.
      this.loadSavedCarts();

      this.$bvToast.toast("Your cart has been cleared.", {
        title: "Cart reset",
        autoHideDelay: 3000,
        variant: "success",
        solid: true,
      });
    },
    deleteCartFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    removeItem(item, order) {
      var removeIndex = order.parts
        .map(function(item) {
          return item.id;
        })
        .indexOf(item.id);
      order.parts.splice(removeIndex, 1);
      this.$forceUpdate();
    },
    removePoNumber() {
      delete localStorage.poNumber;
      this.$router.push({
        name: "Create New Order",
        params: {
          companyId: this.companyId,
          fullOrder: this.fullOrder,
        },
      });
    },
    useSamePoNumber() {
      if (this.fullOrder) {
        this.fullOrder.purchaseOrders.forEach((order) => {
          if (order.number == this.poNumber) {
            if (order.configuredWear == true) {
              this.$router.push({
                name: "Create Order - Shop Wear Parts",
                params: {
                  number: this.poNumber,
                  machineId: this.machineId,
                  configuredWear: this.configuredWear,
                  fullOrder: this.fullOrder,
                },
              });
            } else {
              this.$router.push({
                name: "Create Order - Choose Assembly",
                params: {
                  number: this.poNumber,
                  machineId: this.machineId,
                  configuredWear: this.configuredWear,
                  fullOrder: this.fullOrder,
                },
              });
            }
          } else {
            this.$router.push({
              name: "Create Order - Choose Assembly",
              params: {
                number: this.poNumber,
                machineId: this.machineId,
                configuredWear: this.configuredWear,
                fullOrder: this.fullOrder,
              },
            });
          }
        });
      } else {
        this.$router.push({
          name: "Create Order - Shop Wear Parts",
          params: {
            number: this.poNumber,
            machineId: this.machineId,
            configuredWear: this.configuredWear,
            fullOrder: this.fullOrder,
          },
        });
      }
    },
    finalizeOrder() {
      try {
        if (!this.fullOrder || !this.cartId) {
          this.cartError = true;
          return;
        }
        if (this.placeOrder) return; // double-click guard

        // groupedOrders excludes any orders without a PO
        // Number — those can't be placed anyway. The cart
        // grid only renders groupedOrders, so anything the
        // user can see in the cart is already eligible.
        var groups = this.groupedOrders || [];
        if (groups.length === 0) {
          this.cartError = true;
          return;
        }

        // Single Order — no picker needed, just place. The
        // common path for first-time users with one Order
        // in their cart.
        if (groups.length === 1) {
          this.proceedToCartSuccess(this.cartId);
          return;
        }

        // Multiple Orders — open the picker so the user can
        // choose which to place vs. keep in the active cart
        // for later. Pre-selects every Order so the common
        // case (place all) is one tick away.
        this.selectedOrdersForPlace = groups.map(function (g) {
          return g.number;
        });
        this.showPlaceOrderPickerModal = true;
      } catch (e) {
        console.log(e);
      }
    },

    // Final navigation step shared by every place path
    // (single-Order direct, multi-Order all-selected, multi-
    // Order partial after the cart split). Routes to Cart
    // Success which calls the place-order API for the given
    // cartId and renders the receipt-style summary.
    //
    // restoreCartId — optional. Set by the partial-place flow
    // to tell Success.vue which cart to install as the user's
    // active cart after the just-placed cart is cleaned up.
    // Lets us split a multi-order cart into "place these now,
    // keep the rest in /cart" rather than shoving the
    // remainder into Saved Carts.
    proceedToCartSuccess(cartId, restoreCartId) {
      this.placeOrder = true;
      this.$router.push({
        name: "Cart Success",
        params: {
          id: cartId,
          order: this.fullOrder,
          companyId: this.companyId,
          fullOrder: this.fullOrder,
          SpecialInstructions: this.SpecialInstructions,
          restoreCartId: restoreCartId || "",
        },
      });
    },

    // ===== PLACE ORDER PICKER =====
    // Tick / untick all toggle for the picker modal, mirroring
    // the Save Cart picker's helper. Operates on PO Numbers
    // (group.number) since the picker rows are
    // groupedOrders entries.
    toggleSelectAllOrdersForPlace() {
      var groups = this.groupedOrders || [];
      if (this.selectedOrdersForPlace.length === groups.length) {
        this.selectedOrdersForPlace = [];
      } else {
        this.selectedOrdersForPlace = groups.map(function (g) {
          return g.number;
        });
      }
    },

    // Picker confirmation handler. Two paths:
    //
    // ALL-SELECTED → cheap path. No server call, just route
    // to Cart Success with the existing cartId; the place-
    // order endpoint processes the whole cart.
    //
    // PARTIAL → use savePartialQuotes (the existing split
    // endpoint also used by Save Cart's partial flow) to
    // move UNSELECTED orders OUT of the active cart into a
    // new saved cart row. The current cartId then has only
    // selected orders, so the standard place flow can run on
    // it. Unselected orders end up under /account/savedcarts
    // for the user to resume later.
    //
    // Selecting by PO Number (group.number) rather than by
    // this.orders row id matters: a multi-assembly Order can
    // map to multiple this.orders rows (one per machine+
    // assembly), but the picker shows them as ONE Order.
    // Walk this.orders here and split out every row whose
    // .number is NOT in the selected set.
    confirmPlaceOrderPicker() {
      // Triggered by the custom Place button inside the
      // modal body (not b-modal's default @ok event — see
      // modal markup comment for why). Guard against empty
      // selection or in-flight placement, then split the path
      // by all-selected vs partial.
      if (this.selectedOrdersForPlace.length === 0) return;
      if (this.placeOrder) return;

      var self = this;
      var groups = this.groupedOrders || [];
      var allSelected = this.selectedOrdersForPlace.length === groups.length;

      if (allSelected) {
        self.showPlaceOrderPickerModal = false;
        self.proceedToCartSuccess(self.cartId);
        return;
      }

      // Build the lookup of selected PO Numbers, then split
      // this.orders into selected vs unselected based on
      // each row's .number. We work with the FULL purchase
      // order objects (not just ids) because the partial
      // flow needs to recreate the orders in two carts.
      var selectedNumbers = {};
      this.selectedOrdersForPlace.forEach(function (n) {
        selectedNumbers[String(n)] = true;
      });
      var selectedOrders = (this.orders || []).filter(function (o) {
        return !!o.number && selectedNumbers[String(o.number)];
      });
      var unselectedOrders = (this.orders || []).filter(function (o) {
        return !!o.number && !selectedNumbers[String(o.number)];
      });

      if (selectedOrders.length === 0 || unselectedOrders.length === 0) {
        // Edge: nothing actually to split. Treat as all-selected.
        self.showPlaceOrderPickerModal = false;
        self.proceedToCartSuccess(self.cartId);
        return;
      }

      // ===== PARTIAL PLACE FLOW =====
      // FeApiService doesn't expose a "split cart" endpoint,
      // so we orchestrate the split using the primitives that
      // do exist (saveCart with id=0 + deleteCart):
      //
      //   1. saveCart({id:0, purchaseOrders: unselected})
      //      Creates a new cart for the orders the user did
      //      NOT pick. After Cart Success completes, this
      //      cart becomes the user's active cart so /cart
      //      picks it up on next visit — Success.vue receives
      //      its id via params.restoreCartId and re-sets
      //      localStorage.cartId to it after cleanup.
      //
      //   2. deleteCart({id: originalCartId})
      //      Removes the original cart that had everything.
      //      Soft-fail — a stale row in the system isn't
      //      catastrophic and shouldn't block the place flow.
      //
      //   3. saveCart({id:0, purchaseOrders: selected})
      //      Creates the cart that gets placed. Its id
      //      becomes self.cartId / localStorage.cartId
      //      immediately so Success.vue's createOrder targets
      //      it.
      //
      //   4. proceedToCartSuccess(selectedCartId, unselectedCartId)
      //      Standard place flow plus the restore-target
      //      handoff for Success.vue.
      //
      // id=0 forces SaveNewCart server-side which skips the
      // DoesPartExistInTheCart duplicate check — necessary
      // because both new carts contain parts that were already
      // in the original cart at the start of this flow.
      //
      // Why this ordering? We want the future-active cart
      // created FIRST so its id is stable and known before we
      // delete the original. If step 3 fails after step 2
      // succeeds, the unselected orders are still safe in the
      // new cart from step 1 — the user's data is never lost.
      self.placeOrder = true;
      var originalCartId = self.cartId;
      var unselectedCount = unselectedOrders.length;

      apiService
        .saveCart({
          id: 0,
          purchaseOrders: unselectedOrders,
          companyId: self.companyId,
          specialInstructions: "",
        })
        .then(function (unselectedCartResp) {
          // Capture the future-active cart's id for the
          // Success.vue handoff. If saveCart returned no id
          // for some reason, fall through with empty string —
          // the partial-place handoff in Success.vue treats
          // empty restoreCartId as "no restore", which leaves
          // the user with a cleared active cart pointer (same
          // as a normal full place).
          var futureActiveCartId =
            (unselectedCartResp && unselectedCartResp.id) || "";

          return apiService
            .deleteCart({ id: originalCartId })
            .catch(function (e) {
              // Soft-fail. A stale cart row is recoverable
              // from Saved Carts; doesn't justify aborting.
              console.warn(
                "[Place Partial] deleteCart for original failed:",
                e
              );
            })
            .then(function () {
              return apiService.saveCart({
                id: 0,
                purchaseOrders: selectedOrders,
                companyId: self.companyId,
                specialInstructions: "",
              });
            })
            .then(function (selectedCartResp) {
              if (selectedCartResp && selectedCartResp.id) {
                self.cartId = selectedCartResp.id;
                try { localStorage.cartId = selectedCartResp.id; } catch (e) {}
                self.fullOrder = selectedCartResp;
                if (selectedCartResp.purchaseOrders) {
                  self.orders = selectedCartResp.purchaseOrders;
                }
              }

              self.placeOrder = false;
              self.showPlaceOrderPickerModal = false;

              if (self.$cartBus) {
                self.$cartBus.$emit("cart-updated");
              }

              // Toast tells the user the unselected ones are
              // safe — language matches their mental model of
              // "the rest stay in my cart" rather than the
              // technical reality of "they're now a separate
              // cart that becomes active after Success.vue."
              self.$bvToast.toast(
                unselectedCount === 1
                  ? "1 order will return to your cart after this order is placed."
                  : unselectedCount + " orders will return to your cart after this order is placed.",
                {
                  title: "Orders Pending",
                  autoHideDelay: 5000,
                  variant: "info",
                  solid: true,
                }
              );

              self.proceedToCartSuccess(self.cartId, futureActiveCartId);
            });
        })
        .catch(function (err) {
          self.placeOrder = false;
          var traceId =
            err && err.error && err.error.response && err.error.response.data
              ? err.error.response.data.traceId
              : "";
          self.$bvToast.toast(
            "Failed to split orders. " + (traceId ? "TraceID: " + traceId : ""),
            {
              title: "Error",
              autoHideDelay: 5000,
              variant: "danger",
              solid: true,
            }
          );
        });
    },
    addParts(order) {
      // When the order's assembly has a parent, order.assembly IS the
      // sub-assembly the user originally picked parts from — pass its name
      // AND id as hints so the destination page can pre-select the
      // "Jump to Sub Assembly" dropdown. When there's no parent, the order
      // is already at the top level and no sub-assembly hint applies.
      var subAssemblyName = null;
      var subAssemblyId = null;
      if (
        order.assembly &&
        order.assembly.parentAssemblyId &&
        order.assembly.name
      ) {
        subAssemblyName = order.assembly.name;
        subAssemblyId = order.assembly.id || order.assemblyId || null;
      }

      var params = {
        id: this.cartId,
        order: order,
        number: order.number,
        machineId: order.machineId,
        assemblyId: order.assembly.parentAssemblyId || order.assemblyId,
        subAssemblyName: subAssemblyName,
        subAssemblyId: subAssemblyId,
        configuredWear: order.configuredWear,
        partsList: order.parts,
        fullOrder: this.fullOrder,
      };

      if (order.configuredWear == true) {
        this.$router.push({
          name: "Create Order - Shop Wear Parts",
          params: params,
        });
      } else {
        this.$router.push({
          name: "Create Order - Shop Assembly",
          params: params,
        });
      }
    },
    deletePO(order) {
      var idx = $.inArray(order, this.orders);
      this.orders.splice(idx, 1);

      apiService
        .deletePoNumber({
          id: order.id,
        })
        .then((request) => {
          //on delete nav po number will be updated to recent PO
          if (this.orders.length > 0) {
            this.poNumber = this.orders[this.orders.length - 1].number;
          } else {
            //0 cartId so it will hide clear cart on cart New Order screen
            localStorage.cartId = 0;
            delete localStorage.poNumber;
            this.poNumber = null;
          }
          this.deletePOSuccessful(request);
          this.getTotal();
        })
        .catch((err) => this.deletePOFailed(err));
    },
    deletePOSuccessful() {},
    deletePOFailed(err) {
      this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
        title: err.error.response.data.title,
        autoHideDelay: 10000,
        appendToast: false,
        variant: "danger",
        solid: true,
      });
    },
    // ===== MERGED-VIEW OPERATIONS =====
    // These methods operate on a "merged" row in the grouped view. A merged
    // row may be backed by line items in multiple PurchaseOrder records (when
    // the backend has duplicate PO records for the same machine+assembly).
    // Each merged row carries a `_sources` array of { order, lineItem } pairs
    // so updates/removes can be routed back to the correct backing records.

    updateMergedQuantity(mergedPart, machineGroup) {
      var newQty = Number(mergedPart.quantity) || 0;
      if (newQty < 1) {
        this.removeMergedPart(mergedPart, machineGroup);
        return;
      }

      var sources = mergedPart._sources || [];
      if (sources.length === 0) return;

      var primarySource = sources[0];
      // Write the new total to the primary source's line item
      primarySource.lineItem.quantity = newQty;
      if (primarySource.lineItem.part) {
        primarySource.lineItem.part.cost =
          (primarySource.lineItem.part.price || 0) * newQty;
      }

      // Any duplicate sources — remove their line items; updateSubtotal on
      // an empty order will auto-delete the PurchaseOrder record.
      var otherOrders = [];
      for (var i = 1; i < sources.length; i++) {
        var src = sources[i];
        var idx = src.order.parts.indexOf(src.lineItem);
        if (idx >= 0) {
          src.order.parts.splice(idx, 1);
          if (otherOrders.indexOf(src.order) === -1 && src.order !== primarySource.order) {
            otherOrders.push(src.order);
          }
        }
      }

      // Keep the merged view's displayed qty in sync
      mergedPart.quantity = newQty;

      // Persist: primary first, then any others that had duplicates removed
      this.updateSubtotal(primarySource.order);
      var self = this;
      otherOrders.forEach(function (order) {
        self.updateSubtotal(order);
      });
    },

    removeMergedPart(mergedPart, machineGroup) {
      var sources = mergedPart._sources || [];
      var affected = [];
      sources.forEach(function (src) {
        var idx = src.order.parts.indexOf(src.lineItem);
        if (idx >= 0) {
          src.order.parts.splice(idx, 1);
          if (affected.indexOf(src.order) === -1) affected.push(src.order);
        }
      });
      this.$forceUpdate();
      var self = this;
      affected.forEach(function (order) {
        self.updateSubtotal(order);
      });
    },

    deleteMachineGroup(machineGroup) {
      // Remove ONLY the backing PurchaseOrder records for this machine+assembly
      // combo. We intentionally DO NOT call deletePO here — the backend's
      // deletePoNumber endpoint has been observed to affect more than the single
      // record we target, which wiped the whole cart. Instead:
      //   1. Remove each backing order from this.orders locally so the card
      //      disappears from the view immediately.
      //   2. Persist the removal by writing an empty parts list to each backing
      //      order via updatePurchaseOrder (safer, record-scoped).
      // Empty records remaining server-side are filtered out by groupedOrders
      // (they produce no mergedParts rows).
      var self = this;
      var backingOrders = (machineGroup.backingOrders || []).slice();
      if (backingOrders.length === 0) return;

      // Local removal first (snappier UI)
      backingOrders.forEach(function (order) {
        var idx = self.orders.indexOf(order);
        if (idx >= 0) self.orders.splice(idx, 1);
      });

      // Clean up expansion state for this group
      var expandKey = self.groupExpandKey(
        machineGroup.primaryOrder && machineGroup.primaryOrder.number,
        machineGroup.key
      );
      if (self.expandedGroups[expandKey]) {
        self.$delete(self.expandedGroups, expandKey);
      }

      self.getTotal();
      self.$forceUpdate();

      // Persist — empty each backing order's parts list on the backend.
      backingOrders.forEach(function (order) {
        apiService
          .updatePurchaseOrder({
            id: order.id,
            Number: order.number,
            parts: [],
          })
          .catch(function (err) {
            self.updateCartFailed(err);
          });
      });

      if (self.$cartBus) self.$cartBus.$emit("cart-updated");
    },

    // ===== COLLAPSE / EXPAND =====
    // Machine+assembly cards start collapsed. Users click the row (or +/−
    // toggle) to reveal the parts table. State is keyed by PO + machine key
    // so it survives cart updates and is unique across duplicate cards.
    groupExpandKey(poNumber, mgKey) {
      return poNumber + "__" + mgKey;
    },
    isGroupExpanded(poNumber, mgKey) {
      return !!this.expandedGroups[this.groupExpandKey(poNumber, mgKey)];
    },
    toggleGroup(poNumber, mgKey) {
      var k = this.groupExpandKey(poNumber, mgKey);
      this.$set(this.expandedGroups, k, !this.expandedGroups[k]);
    },

    // ===== PER-PO SPECIAL INSTRUCTIONS DISPLAY =====
    // Helpers for the readout that sits in the order-header
    // of each PO group, showing the special instructions the
    // user wrote in the Modify Order modal. The text is
    // collapsed to a single-line preview (~140 chars) with a
    // "Show more" toggle so long notes don't dominate the
    // card, and we expose the truncation/expansion state via
    // these methods so the template stays clean.
    isInstrLong(text) {
      if (!text) return false;
      return String(text).length > 140;
    },
    isInstrExpanded(poNumber) {
      return !!this.expandedInstructions[poNumber];
    },
    toggleInstructions(poNumber) {
      this.$set(
        this.expandedInstructions,
        poNumber,
        !this.expandedInstructions[poNumber]
      );
    },
    instrPreview(text) {
      if (!text) return "";
      var s = String(text);
      if (s.length <= 140) return s;
      return s.substring(0, 140).replace(/\s+\S*$/, "") + "…";
    },

    // ===== PO GROUP INLINE EDIT =====
    // Editing the PO number at the top-left of a PO group updates every
    // backing PurchaseOrder record for that group.
    startEditPo(poNumber) {
      this.editingPoNumber = poNumber;
      this.editingPoValue = poNumber || "";
    },
    cancelEditPo() {
      this.editingPoNumber = null;
      this.editingPoValue = "";
    },
    savePoEdit(group) {
      var newNumber = (this.editingPoValue || "").trim();
      if (!newNumber) {
        this.$bvToast.toast("PO Number cannot be empty.", {
          title: "Invalid PO Number",
          autoHideDelay: 3500,
          variant: "warning",
          solid: true,
        });
        return;
      }
      if (newNumber === group.number) {
        this.cancelEditPo();
        return;
      }

      // Collect every distinct backing PurchaseOrder in this PO group
      var allOrders = [];
      (group.machineGroups || []).forEach(function (mg) {
        (mg.backingOrders || []).forEach(function (o) {
          if (allOrders.indexOf(o) === -1) allOrders.push(o);
        });
      });

      if (allOrders.length === 0) {
        this.cancelEditPo();
        return;
      }

      var self = this;
      var wasGlobalPo = this.poNumber === group.number;
      var oldNumber = group.number;

      var promises = allOrders.map(function (order) {
        return apiService
          .updatePONumbers({
            cartId: self.cartId,
            poId: order.id,
            newPoNumber: newNumber,
          })
          .then(function () {
            order.number = newNumber;
          });
      });

      Promise.all(promises)
        .then(function () {
          if (wasGlobalPo) {
            self.poNumber = newNumber;
            localStorage.poNumber = newNumber;
          }
          // Move any expanded-state entries from the old PO key to the new one
          Object.keys(self.expandedGroups).forEach(function (k) {
            if (k.indexOf(oldNumber + "__") === 0) {
              var suffix = k.substring((oldNumber + "__").length);
              var newKey = newNumber + "__" + suffix;
              self.$set(self.expandedGroups, newKey, self.expandedGroups[k]);
              self.$delete(self.expandedGroups, k);
            }
          });
          self.editingPoNumber = null;
          self.editingPoValue = "";
          self.$forceUpdate();
          if (self.$cartBus) self.$cartBus.$emit("cart-updated");
        })
        .catch(function () {
          self.$bvToast.toast(
            "Failed to update PO number. Please try again.",
            {
              title: "Error",
              autoHideDelay: 5000,
              variant: "danger",
              solid: true,
            }
          );
        });
    },

    // Navigate back to the Search page using the search
    // context that produced the MOST RECENTLY added order in
    // the staging area. Pulls code (last part's code), machine
    // (assembly.machine.description), and assembly name from
    // this.orders[orders.length - 1] and passes them as URL
    // query params. The Search page's route-watcher reads
    // those on mount and rebuilds the same search result-set,
    // landing the user back where they were when they added
    // their newest staged order.
    //
    // We use the LAST PO (most recent add) rather than orders[0]
    // because in real usage carts often accumulate older content
    // — saveQuote conversions, partial quote-to-order migrations,
    // or just earlier sessions — and the user's mental model of
    // "where was I just searching" maps to their most-recent
    // add, not the chronologically-first one. Falls back to a
    // bare /search push when the cart is empty or any field is
    // missing.
    // ===== MODIFY ORDER MODAL =====
    // Replaces the old "Return to Search" flow with a
    // pick + edit experience. The user picks an order in
    // their cart and can edit its PO Number, edit its
    // Special Instructions (with optional broadcast to
    // all orders), or add more parts to it.
    //
    // Per-PO Special Instructions depends on the backend
    // exposing `purchaseOrders[].specialInstructions`. Until
    // that's wired, the field round-trips through
    // this.orders[].specialInstructions on the client but
    // doesn't survive a refresh.
    // ===== PART REMOVAL FROM MODIFY MODAL =====
    // Triggered by the X button on each part row in the
    // expanded assembly card. Walks the merged part's
    // _sources array (mapped to underlying (order,
    // lineItem) pairs by buildGroupedOrders / the no-PO
    // fallback) and updates each affected PurchaseOrder
    // server-side via updatePurchaseOrder.
    //
    // Why per-source: a merged part row in the modal can
    // be backed by multiple PurchaseOrder rows when stale
    // partial-flow data leaves duplicate backings. Removing
    // the merged row means filtering the line item from
    // EVERY backing order, not just the primary.
    //
    // Confirmation: a single click. Mistaken removes are
    // recoverable — the user just searches the part again.
    // A confirm dialog would add friction without much
    // protection.
    //
    // After all updatePurchaseOrder calls resolve we reload
    // the cart via getCart() so this.orders reflects the
    // server's authoritative state and the modal re-renders
    // with the part gone.
    // ===== isWearEligible =====
    // Returns true when the given part appears in THIS
    // company's own wear parts map (companyWearPartIds,
    // loaded on mount + company change via
    // apiService.getCompanyWearPartIds). Drives the "2%
    // Discount" chip rendered next to wear part codes inside
    // the Modify Order modal. Uses the company-scoped map
    // (not the global wearPartIds, which still drives the
    // allParts search-filter decision via cartHasNonWearPart)
    // so the chip reflects the customer's own Account Setup
    // wear list. The map is keyed by partId so the lookup is
    // a single indexed access.
    //
    // Accepts either a CartPart (p.part.id, p.partId) or
    // a raw part object (p.id) so callers don't have to
    // remember which shape they have.
    isWearEligible(part) {
      if (!part) return false;
      var map = this.companyWearPartIds || {};
      var pid =
        (part.part && part.part.id) ||
        part.partId ||
        part.id ||
        null;
      if (pid == null) return false;
      return !!map[pid];
    },
    removePartFromCart(mergedPart) {
      if (!mergedPart || !mergedPart._sources) return;
      var self = this;
      var sources = mergedPart._sources.slice();

      // Build per-order updates: for each unique source
      // order, drop the matching line items from its parts
      // array, then PUT the trimmed array back.
      var updatesByOrder = {};
      sources.forEach(function (src) {
        var orderId = src.order.id;
        if (!updatesByOrder[orderId]) {
          updatesByOrder[orderId] = {
            order: src.order,
            removedLineItemIds: [],
          };
        }
        updatesByOrder[orderId].removedLineItemIds.push(src.lineItem.id);
      });

      var calls = Object.keys(updatesByOrder).map(function (orderId) {
        var bundle = updatesByOrder[orderId];
        var order = bundle.order;
        var keep = (order.parts || []).filter(function (lineItem) {
          return bundle.removedLineItemIds.indexOf(lineItem.id) === -1;
        });
        return apiService.updatePurchaseOrder({
          id: order.id,
          Number: order.number,
          parts: keep,
        });
      });

      Promise.all(calls)
        .then(function () {
          // If a backing order ends up with zero parts,
          // delete the PurchaseOrder server-side so we
          // don't leave empty orphan rows. Mirrors the
          // existing pattern in updatePurchaseOrder's
          // .then where parts.length < 1 triggers
          // deletePO.
          var emptyDeletes = Object.keys(updatesByOrder)
            .map(function (oid) { return updatesByOrder[oid].order; })
            .filter(function (o) {
              var keep = (o.parts || []).filter(function (li) {
                return updatesByOrder[o.id].removedLineItemIds.indexOf(li.id) === -1;
              });
              return keep.length === 0;
            });
          // Best-effort — don't fail the remove if the
          // delete sweep can't find the route. The cart
          // reload below will surface any inconsistency.
          var deleteCalls = emptyDeletes.map(function (o) {
            return self.deletePO ? self.deletePO(o) : Promise.resolve();
          });
          return Promise.all(deleteCalls).catch(function () {});
        })
        .then(function () {
          // Reload the cart so this.orders matches the
          // server. modifyOptions is a computed off
          // this.orders, so the modal's part row will
          // disappear automatically once getCart resolves.
          self.getCart();
          if (self.$cartBus) {
            self.$cartBus.$emit("cart-updated");
          }
          self.$bvToast.toast(
            (mergedPart.part && mergedPart.part.code
              ? mergedPart.part.code
              : "Part") + " removed.",
            {
              title: "Part Removed",
              autoHideDelay: 3000,
              variant: "success",
              solid: true,
            }
          );
        })
        .catch(function (err) {
          var traceId =
            err && err.error && err.error.response && err.error.response.data
              ? err.error.response.data.traceId
              : "";
          self.$bvToast.toast(
            "Failed to remove part. " + (traceId ? "TraceID: " + traceId : ""),
            {
              title: "Error",
              autoHideDelay: 5000,
              variant: "danger",
              solid: true,
            }
          );
        });
    },

    // ===== ENTRY-POINT QUERY CONSUMER =====
    // Reads ?openModify=<context> from the current route
    // and either opens the modal immediately (when the cart
    // is already loaded) or queues the trigger via
    // _pendingOpenModifyContext so the orders watcher can
    // pick it up once getCart's response lands.
    //
    // Called from:
    //   1. mounted() — initial page load with the query
    //   2. $route.query.openModify watcher — re-navigation
    //      to /cart while already mounted (Vue reuses the
    //      same instance, so mounted() doesn't fire)
    //
    // Strips the query param after consuming so a back-nav
    // doesn't replay the modal. The replace happens
    // unconditionally (whether we open immediately or queue)
    // because we've captured the context value already.
    consumeOpenModifyQuery() {
      var ctx = this.$route.query.openModify;
      if (!ctx) return;
      if (ctx !== "restored") {
        // Unknown context — ignore but still strip so the
        // URL stays clean.
        this._stripOpenModifyQuery();
        return;
      }

      // If orders are already loaded, open right away.
      // Otherwise queue and let the orders watcher fire when
      // getCart resolves.
      if ((this.orders || []).length > 0) {
        this.applyOpenModifyContext(ctx);
      } else {
        this._pendingOpenModifyContext = ctx;
      }

      this._stripOpenModifyQuery();
    },

    // Replace ?openModify=… with the same path minus that
    // param. NavigationDuplicated rejection is benign and
    // swallowed.
    _stripOpenModifyQuery() {
      var preservedQuery = Object.assign({}, this.$route.query);
      delete preservedQuery.openModify;
      this.$router
        .replace({ path: this.$route.path, query: preservedQuery })
        .catch(function () {});
    },

    // Dispatch a context value to the matching opener.
    // Single chokepoint so the queue-then-fire path
    // (mounted with empty cart → orders watcher) and the
    // immediate-fire path (mounted with cart already
    // populated) share logic.
    applyOpenModifyContext(ctx) {
      switch (ctx) {
        case "restored":
          this.openModifyForRestore();
          break;
      }
    },

    // ===== MODIFY MODAL OPENERS (BY CONTEXT) =====
    // Each opener sets modifyContext + showModifyModal in one
    // step. Templates / external callers should always use one
    // of these rather than touching showModifyModal directly,
    // so the banner copy and post-confirm dispatch always
    // match the actual entry point.

    // Resume from /account/savedcarts. Triggered on /cart
    // mount when the route arrives with ?openModify=restored.
    openModifyForRestore() {
      this.modifyContext = "restored";
      this.showModifyModal = true;
    },

    // Place Order on /cart. Replaces the previous direct
    // call into finalizeOrder — the modal now sits between
    // the click and the actual placement so the user has a
    // confirmation beat. The original finalize flow runs
    // when the user clicks Place Order inside the modal
    // (see dispatchModifyConfirm).
    openModifyForPlace() {
      // Empty cart guard mirroring the original finalize
      // pre-check so a click on a broken cart doesn't open
      // an empty modal and confuse the user.
      if (!this.fullOrder || !this.cartId) {
        this.cartError = true;
        return;
      }
      var groups = this.groupedOrders || [];
      if (groups.length === 0) {
        this.cartError = true;
        return;
      }
      this.modifyContext = "placing";
      this.showModifyModal = true;
    },

    // Modify Order header button on /cart — pure review/edit.
    // No banner shown; close-modal is the only post-confirm
    // action (no chained save / place / picker behavior).
    openModifyForModify() {
      this.modifyContext = "modifying";
      this.showModifyModal = true;
    },

    // Auto-opened on /cart landing after RE-ORDER from an
    // order's details page. Backend clears PO Numbers on the
    // cloned cart, so each order needs a new PO Number
    // before the cart grid will render the rows. The
    // 'reordering' context drives a banner specifically
    // about assigning new POs ("Add a New PO Number" /
    // "Click the (+) icon..."), not the generic
    // "modify your cart" copy used for the manual Modify
    // Order header click.
    openModifyForReorder() {
      this.modifyContext = "reordering";
      this.showModifyModal = true;
    },

    // Save Cart Order(s) on /cart. Replaces the direct
    // openSaveCartPicker call — modal sits between the
    // click and the picker so the user can review/edit
    // before deciding which Orders to save. The picker
    // still runs after; see dispatchModifyConfirm.
    openModifyForSave() {
      if (!this.cartId || !this.orders || this.orders.length === 0) {
        this.$bvToast.toast(
          "Your cart is empty — there is nothing to save.",
          {
            title: "Empty Cart",
            autoHideDelay: 4000,
            variant: "warning",
            solid: true,
          }
        );
        return;
      }
      this.modifyContext = "saving";
      this.showModifyModal = true;
    },

    // Open Modify Order modal pre-targeting a specific order's
    // row. Used by external callers that want to drill the
    // user into a specific assembly's editor right away.
    // orderId comes from the order's primary key.
    //
    // Setting activeModifyId BEFORE showing overrides
    // onModifyModalOpen's "default to first" path cleanly.
    openModifyForOrder(orderId) {
      this.activeModifyId = orderId;
      this.modifyContext = this.modifyContext || "restored";
      this.showModifyModal = true;
    },

    // Legacy generic opener — preserved for any callsite that
    // still uses it. Defaults to 'restored' context. New
    // callsites should use one of the context-specific
    // openers above.
    openModifyModal() {
      if (!this.modifyContext) this.modifyContext = "restored";
      this.showModifyModal = true;
    },

    // ===== POST-CONFIRM DISPATCHER =====
    // Routes the clean-form (no edits) primary button click
    // by modifyContext. Called from the template's button
    // @click when hasModifyChanges is false. Dirty-form
    // clicks still flow through saveModifyChanges — but
    // that method's success handler delegates here too so
    // the post-save action matches the entry point.
    dispatchModifyConfirm() {
      var ctx = this.modifyContext;

      // 'placing' branch needs context-state captured BEFORE
      // we close the modal — onModifyModalClose clears
      // selectedPlaceGroups, so we hydrate
      // selectedOrdersForPlace (the legacy place-flow
      // input) from the live selection here, then close.
      if (ctx === "placing") {
        var groups = this.modifyOptionsGrouped || [];
        var selectedPos = [];
        for (var i = 0; i < groups.length; i++) {
          if (this.selectedPlaceGroups[groups[i].key]) {
            // Picked Order. PO number is what
            // confirmPlaceOrderPicker filters by — match
            // the legacy contract.
            if (groups[i].poNumber) {
              selectedPos.push(groups[i].poNumber);
            }
          }
        }
        this.selectedOrdersForPlace = selectedPos;
      }

      // Snapshot context, then close the modal — closing
      // resets modifyContext + selectedPlaceGroups in
      // onModifyModalClose, which would otherwise race
      // with the dispatch branches below.
      this.showModifyModal = false;

      var self = this;
      this.$nextTick(function () {
        switch (ctx) {
          case "placing":
            // selectedOrdersForPlace was populated above.
            // confirmPlaceOrderPicker handles both the
            // all-selected (place direct) and partial
            // (split + place) paths — we get the partial
            // behavior for free.
            self.confirmPlaceOrderPicker();
            break;
          case "saving":
            // Hand off to the existing Save Cart Picker.
            self.openSaveCartPicker();
            break;
          case "modifying":
          case "reordering":
          case "restored":
          default:
            // No-op — user is already on /cart, modal
            // close is the action.
            break;
        }
      });
    },

    // ===== REORDER: PRIMARY "ADD TO CART" CLICK =====
    // (modifyContext === 'reordering' only.) Branches on PO coverage:
    //   • EVERY order group has a PO → persist all POs and add the whole
    //     cart (saveReorderToCart with no drop). Done.
    //   • SOME groups have a PO, others are blank → DON'T submit yet;
    //     flip the modal to its confirmation step (reorderConfirmStep)
    //     so the user can see what's being added vs. dropped, and either
    //     Revise or commit.
    // (reorderHasAnyPo gates the button itself, so this is never reached
    // with zero POs.)
    confirmAddToCartReorder() {
      if (this.savingModify) return;
      if (this.reorderNoPoGroups.length === 0) {
        // All orders have a PO — add the lot, drop nothing.
        this.saveReorderToCart(false);
        return;
      }
      // Mixed — review before committing.
      this.reorderConfirmStep = true;
    },

    // ===== REORDER: "REVISE" =====
    // (modifyContext === 'reordering' only.) Returns from the
    // confirmation step to the order/PO list so the user can enter more
    // POs. Pure UI toggle — no cart mutation.
    reviseReorder() {
      this.reorderConfirmStep = false;
    },

    // ===== REORDER: COMMIT TO CART =====
    // (modifyContext === 'reordering' only.) Persists the entered PO
    // Numbers onto the reordered orders and writes the cart.
    //   dropNoPo === false → keep every order (the all-POs path).
    //   dropNoPo === true  → keep ONLY the orders that have a PO and
    //                        REMOVE the no-PO orders from the cart (the
    //                        "Save Orders to Cart" path from the
    //                        confirmation step).
    //
    // Reuses saveModifyChanges' delete+recreate strategy: the existing
    // server cart is deleted, then re-saved with id=0 (SaveNewCart, no
    // duplicate-part guard) carrying only the kept orders. Rebuilding
    // via saveCart with a filtered purchaseOrders array is the cleanest
    // way to drop orders — there is no per-order cart-delete endpoint,
    // and SaveNewCart cleanly replaces the cart contents wholesale.
    //
    // Sets reorderConfirmed=true on success so onModifyModalClose does
    // NOT then discard the freshly-saved cart as a phantom.
    saveReorderToCart(dropNoPo) {
      if (this.savingModify) return;

      var self = this;
      var groups = this.modifyOptionsGrouped || [];

      // Map each backing order id → { po, hasPo } from its group's
      // draft. Built up-front so the keep/drop decision and the PO
      // assignment both read a stable snapshot.
      var idToPo = {};
      var idToHasPo = {};
      groups.forEach(function (g) {
        var po = (self.groupPoDrafts[g.key] || "").trim();
        g.assemblies.forEach(function (a) {
          idToPo[a.id] = po;
          idToHasPo[a.id] = po !== "";
        });
      });

      // Build the kept-orders payload. When dropNoPo is true, orders
      // whose group PO is blank are excluded entirely so the rebuilt
      // cart contains ONLY the PO'd orders. Apply the PO# onto each
      // kept order's `number` (the saveCart per-order PO field).
      var keptOrders = [];
      this.orders.forEach(function (order) {
        var hasMapping = Object.prototype.hasOwnProperty.call(
          idToHasPo,
          order.id
        );
        // Defensive: an order not represented in the grouped view
        // (shouldn't happen in the reorder flow) is kept untouched so
        // we never silently lose cart contents.
        if (!hasMapping) {
          keptOrders.push(order);
          return;
        }
        if (dropNoPo && !idToHasPo[order.id]) {
          // No PO + drop requested → leave it out of the new cart.
          return;
        }
        order.number = idToPo[order.id];
        keptOrders.push(order);
      });

      if (keptOrders.length === 0) {
        // Nothing to add (every order was blank and we're dropping) —
        // shouldn't be reachable because reorderHasAnyPo gates the
        // button, but guard so we never write an empty cart.
        this.$bvToast.toast(
          "Enter at least one PO Number before adding to the cart.",
          {
            title: "PO Number Required",
            autoHideDelay: 5000,
            variant: "warning",
            solid: true,
          }
        );
        return;
      }

      this.savingModify = true;

      var existingCartId = parseInt(self.cartId, 10) || 0;

      var doSave = function () {
        return apiService.saveCart({
          // id=0 forces SaveNewCart (no duplicate-part check); the
          // existing cart is deleted just below first.
          id: 0,
          purchaseOrders: keptOrders,
          companyId: self.companyId,
          specialInstructions: "",
        });
      };

      var savePromise;
      if (existingCartId > 0) {
        savePromise = apiService
          .deleteCart({ id: existingCartId })
          .catch(function (e) {
            // Soft-fail: a delete error doesn't block the recreate.
            console.warn(
              "[Cart Reorder] deleteCart before recreate failed:",
              e
            );
          })
          .then(doSave);
      } else {
        savePromise = doSave();
      }

      savePromise
        .then(function (request) {
          self.savingModify = false;
          // Mark confirmed BEFORE the modal closes so
          // onModifyModalClose's reorder-discard guard sees it and
          // does NOT delete the cart we just wrote.
          self.reorderConfirmed = true;

          if (request && request.id) {
            self.cartId = request.id;
            try {
              localStorage.cartId = request.id;
            } catch (e) {
              // Storage disabled — surfaces nowhere critical.
            }
          }
          if (request) {
            self.fullOrder = request;
            if (request.purchaseOrders) {
              self.orders = request.purchaseOrders;
            }
          }
          if (self.$cartBus) {
            self.$cartBus.$emit("cart-updated");
          }
          self.$bvToast.toast(
            dropNoPo
              ? "Added to your cart. Orders without a PO were not added."
              : "Added to your cart.",
            {
              title: "Cart Updated",
              autoHideDelay: 3000,
              variant: "success",
              solid: true,
            }
          );
          // Close the modal — user stays on /cart with the saved
          // orders now rendering in the grid.
          self.showModifyModal = false;
        })
        .catch(function (err) {
          self.savingModify = false;
          var traceId =
            err && err.error && err.error.response && err.error.response.data
              ? err.error.response.data.traceId
              : "";
          self.$bvToast.toast(
            "Could not add to cart. " + (traceId ? "TraceID: " + traceId : ""),
            {
              title: "Error",
              autoHideDelay: 5000,
              variant: "danger",
              solid: true,
            }
          );
        });
    },

    onModifyModalOpen() {
      // Don't auto-select an item. Modal opens in a
      // pure browse state — the user picks the Order they
      // want, expands it, then picks an assembly to edit.
      // Auto-selecting the first row would also auto-show
      // the PO/Special Instructions editor below, which
      // looks like the modal is asking the user to edit
      // something specific when it isn't.
      //
      // openModifyForOrder() pre-sets activeModifyId for
      // callers that DO want a specific row drilled into;
      // that path skips this no-op default.
      this.applyInstructionsToAll = false;
      this.applyInstructionsToOrder = false;
      this.applyPoToAll = false;
      // Reorder-context flags — start the confirmation step closed and
      // treat the cart as unconfirmed until an Add-to-Cart path succeeds
      // (so a cancel discards the phantom reorder cart). Reset on every
      // open regardless of context; they're inert outside 'reordering'.
      this.reorderConfirmStep = false;
      this.reorderConfirmed = false;

      // Seed a per-order PO Number draft from each Order's current PO
      // so every Order's header input renders with its existing value
      // and the user can set/edit all POs without expanding. Fresh
      // object keeps it reactive; keyed by group key.
      var poDrafts = {};
      (this.modifyOptionsGrouped || []).forEach(function (g) {
        poDrafts[g.key] = g.poNumber || "";
      });
      this.groupPoDrafts = poDrafts;

      // Start with every group collapsed. If a caller pre-
      // set activeModifyId via openModifyForOrder() — i.e.
      // they explicitly want a specific row drilled into —
      // expand only the group containing that row. Otherwise
      // the user gets a clean overview-first state and
      // expands what they want via the chevron toggles.
      this.expandedModifyGroups = {};
      var activeId = this.activeModifyId;
      if (activeId != null) {
        var grp = (this.modifyOptionsGrouped || []).find(function (g) {
          return g.assemblies.some(function (a) {
            return a.id === activeId;
          });
        });
        if (grp) {
          this.$set(this.expandedModifyGroups, grp.key, true);
        }
      }
    },

    // Toggle a Modify Order group's expanded state. Called
    // from the chevron button on each group header.
    toggleModifyGroup(key) {
      if (this.expandedModifyGroups[key]) {
        this.$set(this.expandedModifyGroups, key, false);
      } else {
        this.$set(this.expandedModifyGroups, key, true);
      }
    },

    // Update a single order's PO Number draft (from its header input).
    // $set keeps it reactive even for a group key that wasn't present
    // when the modal opened (e.g. after removing a part reshuffles the
    // groups). Save Changes reads these drafts to apply all POs at once.
    setGroupPo(key, value) {
      this.$set(this.groupPoDrafts, key, value);
    },

    // Toggle a group's place-selection state. Used in
    // 'placing' context when each Order header carries a
    // checkbox. Set/unset is symmetric — false rather than
    // delete so the reactive Object.keys() walk in the
    // selectedPlaceGroupCount computed sees the empty
    // entries too. (Vue 2 reactivity is fine with that
    // and it sidesteps a Vue.delete dance.)
    toggleSelectPlaceGroup(key) {
      this.$set(this.selectedPlaceGroups, key,
                !this.selectedPlaceGroups[key]);
    },

    // Reset modal state when the modal closes (Cancel,
    // Close button, Save Changes success, ESC). Without
    // clearing activeModifyId here, the next open would
    // fall into onModifyModalOpen's "if (!this.activeModifyId)"
    // skip path and the wrong row could stay selected.
    // Also collapses every group so a re-open has a clean
    // overview-first layout.
    onModifyModalClose() {
      // ===== REORDER: DISCARD THE PHANTOM CART ON CANCEL =====
      // The reorder flow creates a server cart up front (the cloned
      // order with its POs cleared) and auto-opens this modal. If the
      // user closes it WITHOUT confirming (✕ / backdrop / Esc) that
      // cart would linger and show a phantom total in the floating cart
      // widget. So: when the closing context is 'reordering' and the
      // user never hit an Add-to-Cart path (reorderConfirmed still
      // false), delete that cart, clear the cart id, and tell the
      // widget to refresh to empty. Captured BEFORE the resets below so
      // the cleared modifyContext doesn't race this check. The
      // successful confirm paths set reorderConfirmed=true, so they skip
      // this and keep the cart.
      //
      // (cartIdRFQ is intentionally NOT touched here — the reorder flow
      // is Order-only and uses localStorage.cartId; cartIdRFQ tracks a
      // separate Quote/RFQ cart that this flow never creates.)
      if (this.modifyContext === "reordering" && this.reorderConfirmed !== true) {
        var self = this;
        var phantomId = parseInt(this.cartId, 10) || 0;
        var clearLocal = function () {
          self.cartId = null;
          try {
            localStorage.removeItem("cartId");
            // Loading the reorder cart switched the selected company to the
            // reordered order's company; restore the one selected before the
            // reorder so the header doesn't silently change on cancel.
            if (self.reorderPrevCompanyId) {
              localStorage.companyId = self.reorderPrevCompanyId;
            }
          } catch (e) {
            // Storage disabled — nothing more we can do.
          }
          // Hard-refresh so the /cart page AND the floating cart widget
          // re-fetch and show the now-empty cart. A soft cart-updated emit
          // left the already-rendered order total on screen until a manual
          // refresh, so reload once the cart is actually gone.
          window.location.reload();
        };
        if (phantomId > 0) {
          apiService
            .deleteCart({ id: phantomId })
            .then(clearLocal)
            .catch(function (e) {
              // Soft-fail: even if the server delete fails, still clear
              // the local id + refresh so the widget stops showing the
              // phantom total.
              console.warn("[Cart Reorder] discard deleteCart failed:", e);
              clearLocal();
            });
        } else {
          clearLocal();
        }
      }

      this.activeModifyId = null;
      this.modifyPoNumberDraft = "";
      this.modifySpecialInstructionsDraft = "";
      this.applyInstructionsToAll = false;
      this.applyInstructionsToOrder = false;
      this.applyPoToAll = false;
      this.groupPoDrafts = {};
      this.expandedModifyGroups = {};
      this.modifyContext = null;
      this.selectedPlaceGroups = {};
      this.reorderConfirmStep = false;
      this.reorderConfirmed = false;
    },

    // Persist the edited PO + Special Instructions on the
    // active order. Mutates this.orders in place so the
    // page reflects the change immediately, then pushes
    // the full cart to the server via saveCart so the
    // backend picks up both the (possibly renamed) PO
    // number and the per-PO specialInstructions field.
    //
    // Per-PO propagation: a single PO can map to multiple
    // backend purchaseOrder rows (one per machine+assembly).
    // The user's intent ("special instructions for THIS
    // order") is PO-scoped, so we propagate the instructions
    // to every row sharing the active row's PO Number, not
    // just the one row that happens to be the radio target.
    // Without this, instructions get saved to one row and
    // the others (under the same PO) keep their stale
    // values — and on read-back, buildGroupedOrders may
    // pick whichever row happens to be the primary, which
    // might or might not be the one the user wrote to.
    //
    // Save strategy: delete + recreate (id=0 → SaveNewCart).
    // SaveOldCart's DoesPartExistInTheCart check rejects any
    // request that re-sends an already-present part with
    // "This part is already added. Please adjust the quantity
    // in the Cart." That's exactly what happens for a
    // metadata-only modify save — every part is already in
    // the cart, by definition. SaveNewCart has no duplicate
    // check, so we delete the existing cart row first, then
    // save fresh. The new cart's id replaces self.cartId and
    // localStorage.cartId so subsequent operations target it.
    saveModifyChanges() {
      if (this.savingModify) return;

      var self = this;
      var groups = this.modifyOptionsGrouped || [];
      var newInstr = this.modifySpecialInstructionsDraft || "";

      // ===== VALIDATION: every order needs a PO Number =====
      // PO Numbers are entered per order in each Order's header row (no
      // expansion needed). Every order must have one before the cart can
      // be saved/placed — collect any blanks and name them so the user
      // knows exactly which orders still need a PO.
      var blanks = [];
      groups.forEach(function (g) {
        if (!((self.groupPoDrafts[g.key] || "").trim())) {
          blanks.push(g.orderLabel);
        }
      });
      if (blanks.length > 0) {
        this.$bvToast.toast(
          "Enter a PO Number for: " + blanks.join(", ") + ".",
          {
            title: "PO Number Required",
            autoHideDelay: 5000,
            variant: "warning",
            solid: true,
          }
        );
        return;
      }

      this.savingModify = true;

      // ===== APPLY PER-ORDER PO NUMBERS =====
      // Decide each backing order's new PO from its CURRENT group
      // membership FIRST (into a map), THEN apply — so renaming one
      // group onto another's number can't cross-contaminate mid-loop.
      // The direct id match covers no-PO orders and each group's primary
      // assembly rows; the number-match fallback catches merged siblings
      // that share a PO'd group's old number.
      var idToNewPo = {};
      groups.forEach(function (g) {
        var po = (self.groupPoDrafts[g.key] || "").trim();
        g.assemblies.forEach(function (a) {
          idToNewPo[a.id] = po;
        });
      });
      this.orders.forEach(function (order) {
        if (Object.prototype.hasOwnProperty.call(idToNewPo, order.id)) {
          order.number = idToNewPo[order.id];
          return;
        }
        var grp = groups.find(function (g) {
          return g.poNumber && g.poNumber === order.number;
        });
        if (grp) {
          order.number = (self.groupPoDrafts[grp.key] || "").trim();
        }
      });

      // ===== APPLY SPECIAL INSTRUCTIONS =====
      // Only when a row is selected in the (optional) expanded editor.
      // SI follows the same active-row / apply-to-order / apply-to-all
      // rules as before; PO is handled above. Sibling matching uses the
      // active order's now-current PO.
      if (this.activeModifyId != null) {
        var activeId = this.activeModifyId;
        var applyAll = this.applyInstructionsToAll === true;
        var applyToOrder = this.applyInstructionsToOrder === true;
        var activeOrder = this.orders.find(function (o) {
          return o.id === activeId;
        });
        var activePo = activeOrder ? activeOrder.number : null;
        this.orders.forEach(function (order) {
          if (order.id === activeId) {
            self.$set(order, "specialInstructions", newInstr);
          } else if (applyToOrder && activePo && order.number === activePo) {
            self.$set(order, "specialInstructions", newInstr);
          } else if (applyAll) {
            self.$set(order, "specialInstructions", newInstr);
          }
        });
      }

      var existingCartId = parseInt(self.cartId, 10) || 0;

      var doSave = function () {
        return apiService.saveCart({
          // id=0 forces SaveNewCart (no duplicate check).
          // The existing cart is deleted above before this
          // runs, so the freshly-created cart becomes the
          // active one.
          id: 0,
          purchaseOrders: self.orders,
          companyId: self.companyId,
          // Cart-level Special Instructions kept blank now
          // that per-PO instructions are the source of truth.
          specialInstructions: "",
        });
      };

      var savePromise;
      if (existingCartId > 0) {
        savePromise = apiService
          .deleteCart({ id: existingCartId })
          .catch(function (e) {
            // Soft-fail: a delete error doesn't block the
            // recreate. Worst case the old cart lingers in
            // /account/savedcarts as a duplicate; the new
            // cart still gets created and is the active one.
            console.warn(
              "[Cart Modify] deleteCart before recreate failed:",
              e
            );
          })
          .then(doSave);
      } else {
        // No existing cart row to clean up — just save fresh.
        savePromise = doSave();
      }

      savePromise
        .then(function (request) {
          self.savingModify = false;
          // Capture the new cart id so subsequent operations
          // (further modifies, save-cart, place-order) target
          // the freshly-created cart row, not the deleted one.
          if (request && request.id) {
            self.cartId = request.id;
            try {
              localStorage.cartId = request.id;
            } catch (e) {
              // Storage disabled — surfaces nowhere critical.
            }
          }
          // Refresh from the server response so client
          // and server are aligned (handles any
          // normalization). New row ids will replace the
          // old ones in self.orders, which means
          // activeModifyId is now stale — but the modal is
          // closing right below, so that's fine.
          if (request) {
            self.fullOrder = request;
            if (request.purchaseOrders) {
              self.orders = request.purchaseOrders;
            }
          }
          // Header badge refresh — without this, the
          // View Cart counter keeps showing the pre-recreate
          // count until the next page navigation.
          if (self.$cartBus) {
            self.$cartBus.$emit("cart-updated");
          }
          self.$bvToast.toast(
            applyAll
              ? "Saved. Special Instructions applied to all orders."
              : "Saved.",
            {
              title: "Order Updated",
              autoHideDelay: 3000,
              variant: "success",
              solid: true,
            }
          );
          // After saving, run the contextual post-confirm
          // action — places the order, opens the save
          // picker, etc. Without this dispatch a "Save
          // Changes" click would just dirty-save and stop,
          // forcing the user to click Place Order / Save
          // Cart / Add again. The dispatcher closes the
          // modal itself, so we don't close it here too.
          self.dispatchModifyConfirm();
        })
        .catch(function (err) {
          self.savingModify = false;
          var traceId =
            err && err.error && err.error.response && err.error.response.data
              ? err.error.response.data.traceId
              : "";
          self.$bvToast.toast(
            "Could not save changes. " + (traceId ? "TraceID: " + traceId : ""),
            {
              title: "Error",
              autoHideDelay: 5000,
              variant: "danger",
              solid: true,
            }
          );
        });
    },

    // "Add More Parts" — the original purpose of the
    // Modify modal. Routes to /orders/create with modify*
    // query params so the create page lands on the picked
    // order's machine + assembly with the same PO Number.
    goAddMoreParts() {
      var opt = this.activeModifyOption;
      if (!opt) return;
      this.showModifyModal = false;
      var query = {
        modifyCompanyId: this.companyId,
        modifyMachineId: opt.machineId,
        // ===== Why machineName is required here =====
        // opt.machineId is the catalog Machine.Id (sourced from
        // PurchaseOrder.MachineId in modifyOptions), but Step 2's
        // dropdown on /orders/create is keyed on CompanyMachine.Id
        // — the two are different IDs that share a column name.
        // Without modifyMachineName, the create page receives an
        // ID that doesn't match any item in its loaded machines
        // list, validateMachineSelection clears it, and Step 2
        // silently shows "Select your machine" even though the
        // user came here from a specific machine context.
        //
        // Passing the description text lets applyModifyQuery
        // resolve to the correct CompanyMachine.Id by looking up
        // machine.text in the dropdown's options. See
        // pendingModifyMachineName in Create-Order.vue's
        // data() and getMachinesByCompany.then for the
        // consumer side.
        modifyMachineName: opt.machineName,
        modifyAssemblyId: opt.assemblyId,
        modifyPoNumber: opt.poNumber,
        modifyConfiguredWear: opt.configuredWear ? "1" : "0",
      };
      if (opt.subAssemblyId) {
        query.modifySubAssemblyId = opt.subAssemblyId;
      }
      this.$router.push({
        name: "Create New Order",
        query: query,
      });
    },

    goToSearch() {
      var query = { from: "order" };

      // If the user's cart contains any non-wear part, signal
      // the Search page to disable Show Wear Parts Only on
      // arrival. They were almost certainly searching with
      // the wear filter off when they added those parts, and
      // returning to search with the filter on would hide the
      // very content they came to expand on. See
      // cartHasNonWearPart() for the membership check.
      if (this.cartHasNonWearPart()) {
        query.allParts = "true";
      }

      // Most-recently-added PO. EF returns purchaseOrders
      // ordered by primary-key id ASC (creation order), so
      // the last element is the newest entry.
      var lastOrder = (this.orders && this.orders.length > 0)
        ? this.orders[this.orders.length - 1]
        : null;

      if (lastOrder) {
        // Code → first part of that PO. A PO normally only
        // has one part (the add-flow groups by Number/Machine/
        // Assembly), but we defensively take parts[0] in case
        // a later add merged into the same PO group.
        var firstPart = (lastOrder.parts && lastOrder.parts.length > 0)
          ? lastOrder.parts[0]
          : null;
        if (firstPart && firstPart.part) {
          var code = firstPart.part.code || firstPart.part.item;
          if (code) query.code = code;
        }

        // Machine → assembly.machine.description.
        // Assembly  → assembly.name.
        // Both can be absent on older cart rows with un-hydrated
        // nav properties; we pass through what we have and let
        // the Search page tolerate missing params.
        if (lastOrder.assembly) {
          if (lastOrder.assembly.machine
              && lastOrder.assembly.machine.description) {
            query.machineName = lastOrder.assembly.machine.description;
          }
          if (lastOrder.assembly.name) {
            query.assembly = lastOrder.assembly.name;
          }
        }
      }

      // Clear stale fallback values before navigating. The
      // Search page's searchResults() falls back to
      // localStorage.code when the URL has no code, which
      // means a missing/empty code field here would silently
      // surface whatever the user's last header-bar search
      // left in localStorage — typically a different part.
      // Clearing forces the search page to honor our URL or
      // show no search at all.
      try {
        delete localStorage.code;
        delete localStorage.searchTerm;
        delete localStorage.description;
      } catch (e) {
        // localStorage access can throw in incognito / quota
        // contexts; ignore.
      }

      this.$router.push({ name: "Search", query: query });
    },

    // ===== SAVE CART PICKER MODAL =====
    // Opens the picker with every Order pre-checked so the
    // common case (Save All) is one click away. The user
    // can deselect individual Orders to leave them in the
    // active cart while saving the rest.
    //
    // Selection is by PO Number (group.number) — multi-
    // assembly Orders share one PO so they save/keep
    // together as one unit, the same way the Place Order
    // picker treats them.
    openSaveCartPicker() {
      if (!this.cartId || !this.orders || this.orders.length === 0) {
        this.$bvToast.toast("Your cart is empty — there is nothing to save.", {
          title: "Empty Cart",
          autoHideDelay: 4000,
          variant: "warning",
          solid: true,
        });
        return;
      }
      // Pre-select every PO. Most users want to save the
      // whole cart; deselection is the deliberate gesture.
      var groups = this.groupedOrders || [];
      this.selectedOrdersForPicker = groups.map(function (g) {
        return g.number;
      });
      this.showSaveCartPickerModal = true;
    },

    toggleSelectAllOrdersForPicker() {
      var groups = this.groupedOrders || [];
      if (this.selectedOrdersForPicker.length === groups.length) {
        this.selectedOrdersForPicker = [];
      } else {
        this.selectedOrdersForPicker = groups.map(function (g) {
          return g.number;
        });
      }
    },

    confirmSaveCartPicker(bvModalEvent) {
      // Prevent the modal from closing on click — saveCartForLater
      // closes it manually after the API call resolves so an
      // error doesn't lose the user's selection.
      if (bvModalEvent) bvModalEvent.preventDefault();
      if (this.selectedOrdersForPicker.length === 0) return;
      this.saveCartForLater();
    },

    // ===== SAVE CART FOR LATER =====
    // Two paths:
    //
    // ALL SELECTED — entire cart graduates to "saved for
    // later." Server-side `saveCart` flushes any pending
    // edits, then localStorage pointers are cleared and the
    // user navigates to /account/savedcarts.
    //
    // PARTIAL — picked orders split into a NEW cart row via
    // POST /cart/{id}/savepartial. The user stays on this
    // page with the leftover orders still in their active
    // cart. The new cart appears under SavedCarts so they
    // can resume the saved subset later. No order is ever
    // deleted by this action.
    saveCartForLater() {
      if (this.savingCart) return;
      // selectedOrdersForPicker holds PO Numbers
      // (group.number), not row ids — see openSaveCartPicker
      // for why. Walk this.orders and split by PO membership.
      var selectedPoNumbers = this.selectedOrdersForPicker.slice();
      if (selectedPoNumbers.length === 0) return;
      this.savingCart = true;

      var self = this;
      var groups = this.groupedOrders || [];
      var allSelected = selectedPoNumbers.length === groups.length;

      // ===== ALL-SELECTED PATH =====
      // No server-side save call here. The cart is already
      // persisted (every Order/PO/Part add and every qty
      // tweak goes through its own API mutation —
      // updatePurchaseOrder, updatePoNumbers, etc. — at the
      // moment the user makes the change), so the cart row
      // server-side already reflects what's on screen. All
      // we need to do is detach this user from it as their
      // active cart.
      //
      // Why we used to call saveCart here: to "flush" any
      // pending local edits. Two reasons that's no longer
      // needed:
      //   1. The mutations above persist eagerly; nothing is
      //      held back in local state waiting for a flush.
      //   2. saveCart with id=existing triggers SaveOldCart
      //      server-side, which runs DoesPartExistInTheCart
      //      against every part in purchaseOrders. For a
      //      cart that already has those parts (which is
      //      always the case here — we're saving the
      //      currently-loaded active cart), the check throws
      //      "This part is already added." That was the
      //      bug: the moment a user resumed a saved cart and
      //      tried to save it again with no edits, they hit
      //      the duplicate-part check and got a 500.
      //
      // The saveCart call is gone. Logic below is the same
      // localStorage cleanup + nav that used to live in the
      // .then handler.
      if (allSelected) {
        // Clear active-cart pointers. Keep companyId so the
        // user stays in the same company context on the
        // Saved Carts page.
        localStorage.cartId = 0;
        delete localStorage.poNumber;
        delete localStorage.machineId;
        delete localStorage.assemblyId;

        // ===== Clear Search page's staging-tray cache =====
        // Search/Index.vue persists its right-rail staging
        // tray to localStorage under searchStaging_<companyId>,
        // restored on every mount. After this save the tray
        // no longer reflects an active cart — the user
        // shouldn't see those parts when they navigate back
        // to Search. Clear the cache so mounted reads empty.
        //
        // Restoring is symmetric: if the user later resumes a
        // cart from /account/savedcarts (which sets
        // localStorage.cartId to the resumed cart's id),
        // Search/Index.vue's getCartSuccessful re-derives the
        // staging tray from the cart's purchase orders and the
        // right rail repopulates automatically.
        if (self.companyId) {
          localStorage.removeItem("searchStaging_" + self.companyId);
        }
        localStorage.removeItem("searchStaging_anon");

        if (self.$cartBus) {
          self.$cartBus.$emit("cart-updated");
        }

        self.$bvToast.toast(
          "Your cart has been saved. You can resume it anytime from Saved Carts.",
          {
            title: "Cart Saved",
            autoHideDelay: 4000,
            variant: "success",
            solid: true,
          }
        );

        self.savingCart = false;
        self.showSaveCartPickerModal = false;
        // Pass flashCartId so SavedCarts.vue can briefly
        // highlight the just-saved row to confirm the save
        // landed. self.cartId was the active cart's id and
        // it's the same row that now appears under
        // /account/savedcarts (the cart was saved server-side
        // already; we're just detaching it from active state).
        self.$router.push({
          path: "/account/savedcarts",
          query: self.cartId ? { flashCartId: self.cartId } : {},
        });
        return;
      }

      // ===== PARTIAL PATH =====
      // Split the SELECTED orders off into a new "saved" cart
      // and leave the unselected orders in the active cart so
      // editing can continue. FeApiService doesn't expose a
      // dedicated split endpoint (savePartialQuotes is
      // referenced in older code but isn't actually defined on
      // the service — calls throw TypeError silently), so we
      // orchestrate the split with primitives that DO exist:
      //
      //   1. saveCart({id:0, purchaseOrders: selected})
      //      Creates a new cart row with the orders the user
      //      is saving for later. id=0 forces SaveNewCart
      //      server-side, sidestepping
      //      DoesPartExistInTheCart's duplicate check.
      //
      //   2. deleteCart({id: originalCartId})
      //      Removes the original cart that had everything.
      //
      //   3. saveCart({id:0, purchaseOrders: unselected})
      //      Recreates the active cart with only the orders
      //      the user wants to keep editing. The new cart's
      //      id replaces self.cartId / localStorage.cartId so
      //      subsequent operations target it.
      //
      // This is the same pattern Place Order picker uses for
      // its partial path — just with selected/unselected
      // swapped (Place puts selected in active, Save puts
      // selected in the new saved cart).
      // selectedOrdersForPicker holds PO Numbers, so build a
      // lookup and walk this.orders splitting rows by their
      // .number membership. A multi-assembly Order with PO 1
      // contributes 3 rows that all share number=1, so every
      // row of that Order moves together.
      var selectedPoLookup = {};
      selectedPoNumbers.forEach(function (n) {
        selectedPoLookup[String(n)] = true;
      });
      var unselectedOrders = self.orders.filter(function (o) {
        return !!o.number && !selectedPoLookup[String(o.number)];
      });
      var selectedOrders = self.orders.filter(function (o) {
        return !!o.number && selectedPoLookup[String(o.number)];
      });
      var originalCartId = self.cartId;

      // Holds the id of the cart we just created with the
      // selected orders. Captured here so the success
      // handler at the end can pass it to /account/savedcarts
      // for the flash-confirmation highlight.
      var savedCartId = null;

      apiService
        .saveCart({
          id: 0,
          purchaseOrders: selectedOrders,
          companyId: self.companyId,
          specialInstructions: "",
        })
        .then(function (savedResp) {
          if (savedResp && savedResp.id) {
            savedCartId = savedResp.id;
          }
          return apiService
            .deleteCart({ id: originalCartId })
            .catch(function (e) {
              // Soft-fail: stale original cart in the system
              // is recoverable, doesn't block the save.
              console.warn(
                "[Save Partial] deleteCart for original failed:",
                e
              );
            });
        })
        .then(function () {
          return apiService.saveCart({
            id: 0,
            purchaseOrders: unselectedOrders,
            companyId: self.companyId,
            specialInstructions: "",
          });
        })
        .then(function (newActiveResp) {
          // Refresh local state to point at the new active
          // cart that holds the unselected (kept-editing)
          // orders.
          if (newActiveResp && newActiveResp.id) {
            self.cartId = newActiveResp.id;
            try { localStorage.cartId = newActiveResp.id; } catch (e) {}
            self.fullOrder = newActiveResp;
            if (newActiveResp.purchaseOrders) {
              self.orders = newActiveResp.purchaseOrders;
            }
          }

          self.savingCart = false;
          self.showSaveCartPickerModal = false;

          if (self.$cartBus) {
            self.$cartBus.$emit("cart-updated");
          }

          // savedCount is the number of Orders (PO groups)
          // saved, not assemblies — selectedOrders holds the
          // backing rows but the user thinks in Orders, so
          // count the distinct PO numbers we just split off.
          var savedCount = selectedPoNumbers.length;
          self.$bvToast.toast(
            savedCount === 1
              ? "1 order was saved. The remaining orders stay in your active cart."
              : savedCount + " orders were saved. The remaining orders stay in your active cart.",
            {
              title: "Cart Saved",
              autoHideDelay: 4000,
              variant: "success",
              solid: true,
            }
          );

          // Navigate to /account/savedcarts so the user can
          // see the row they just created — same destination
          // as the all-selected path. Passing flashCartId in
          // query so SavedCarts.vue can briefly highlight the
          // row to confirm the save landed where expected.
          //
          // router.push (not replace) so Back returns to the
          // (now-trimmed) /cart, matching the all-selected
          // path's navigation behavior.
          var query = savedCartId ? { flashCartId: savedCartId } : {};
          self.$router.push({
            path: "/account/savedcarts",
            query: query,
          });
        })
        .catch(function (err) {
          self.savingCart = false;
          var traceId =
            err && err.error && err.error.response && err.error.response.data
              ? err.error.response.data.traceId
              : "";
          self.$bvToast.toast(
            "Failed to save selected orders. " +
              (traceId ? "TraceID: " + traceId : ""),
            {
              title: "Error",
              autoHideDelay: 5000,
              variant: "danger",
              solid: true,
            }
          );
        });
    },
    saveQuote() {
      if (!this.fullOrder || !this.cartId) {
        this.cartError = true;
      } else {
        localStorage.poNumberRFQ = localStorage.poNumber;
        delete localStorage.cartId;
        delete localStorage.poNumber;
        this.$router.push({
          name: "Quote Saved Items",
          params: {
            id: this.cartId,
            order: this.fullOrder,
            purchaseOrders: this.fullOrder.purchaseOrders,
            companyId: this.companyId,
            fullOrder: this.fullOrder,
            SpecialInstructions: this.SpecialInstructions,
            CreateQuote: true,
          },
        });
      }
    },
  },
};
</script>

<style scoped>
.text-decoration-underline {
  text-decoration: underline;
}

/* ===== GENERAL HEADINGS / SECTION SPACING ===== */
#cartTop h1 {
  color: #c0392b;
  font-weight: 700;
}

#reviewInfo h2 {
  color: #333;
  font-weight: 700;
}

#cartTop {
  /* Small top padding so the red rule sits near the top of the content area,
     flush with the right-edge widget (matches the other portal pages). */
  padding: 8px 0 16px;
}

/* Brand-red top accent on the Review & Checkout card, matching the
   red line used on the other content cards across the portal. */
#cartTop .container {
  border-top: 3px solid #c0392b;
}

#cartTop h1 {
  margin-bottom: 0;
}

/* ===== EMPTY CART STATE ===== */
#emptyCart {
  padding: 12px 0 48px;
}

/* ===== UNIFIED EMPTY CART PANEL =====
   Single white card holding the empty-state hero, the resume intro, and
   both saved-carts columns. Replaces the multi-card layout where each
   piece had its own background and the intro paragraph floated on the
   page background between them. */
.empty-cart-panel {
  background: #fff;
  border: 1px solid #eaeaea;
  border-radius: 8px;
  padding: 32px 24px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 28px;
}

.empty-cart-hero {
  text-align: center;
  padding: 8px 0 24px;
  margin-bottom: 24px;
  border-bottom: 1px solid #f0f0f0;
}

.empty-cart-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;
  margin: 0 auto 18px;
  border-radius: 50%;
  background: #faf6f5;
  color: #c0392b;
}

.empty-cart-title {
  font-size: 24px;
  font-weight: 700;
  color: #222;
  margin: 0 0 6px;
}

.empty-cart-subtitle {
  font-size: 15px;
  color: #777;
  margin: 0;
}

.saved-loading p,
.saved-carts-empty p {
  font-size: 15px;
  color: #555;
  line-height: 1.6;
  text-align: center;
  margin: 0;
}

.saved-carts-empty {
  background: #fff;
  border: 1px dashed #d8d8d8;
  border-radius: 8px;
  padding: 24px 20px;
}

.saved-carts-empty strong {
  color: #c0392b;
}

.saved-carts-intro {
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  margin: 0 0 20px;
  text-align: center;
}

.saved-carts-intro strong {
  color: #c0392b;
}

.saved-section {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0 6px;
  box-shadow: none;
  height: 100%;
}

.saved-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 2px solid #f0f0f0;
}

.saved-section-title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.saved-section-count {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #888;
  background: #f3f3f3;
  padding: 3px 9px;
  border-radius: 10px;
}

.saved-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.saved-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #fafafa;
  border: 1px solid #ececec;
  border-radius: 6px;
  cursor: pointer;
  text-decoration: none !important;
  transition: background 0.15s ease, border-color 0.15s ease,
    transform 0.15s ease;
}

.saved-item:hover {
  background: #fff;
  border-color: #c0392b;
  transform: translateX(2px);
}

.saved-item-main {
  flex: 1 1 auto;
  min-width: 0;
}

.saved-item-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 3px;
}

.saved-item-date {
  font-size: 11px;
  font-weight: 700;
  color: #777;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.saved-item-type {
  display: inline-block;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 2px 7px;
  border-radius: 3px;
  color: #fff;
}

.saved-item-type-order {
  background: #c0392b;
}

.saved-item-type-quote {
  background: #333;
}

.saved-item-company {
  font-size: 14px;
  font-weight: 700;
  color: #222;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.saved-item-meta {
  font-size: 12px;
  color: #777;
}

.saved-item-sep {
  margin: 0 6px;
  color: #bbb;
}

.saved-item-resume {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.saved-list-empty p {
  font-size: 13px;
  color: #999;
  font-style: italic;
  margin: 0;
  padding: 16px 4px;
  text-align: center;
}

.saved-view-all {
  text-align: center;
  margin-top: 8px;
  padding-top: 14px;
  border-top: 1px solid #eee;
}

.saved-view-all-link {
  display: inline-block;
  font-size: 13px;
  font-weight: 700;
  color: #555;
  text-decoration: none;
  padding: 6px 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.saved-view-all-link:hover {
  color: #c0392b;
  text-decoration: underline;
}

@media (max-width: 767.98px) {
  .empty-cart-panel {
    padding: 24px 16px 18px;
  }
  .empty-cart-icon {
    width: 68px;
    height: 68px;
  }
  .empty-cart-title {
    font-size: 20px;
  }
  .saved-item {
    flex-wrap: wrap;
  }
  .saved-item-resume {
    width: 100%;
    text-align: right;
  }
}


/* ===== CART HEADER ===== */
.cart-header {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 14px;
  margin-bottom: 24px;
}

.cart-header-title {
  flex: 1 1 auto;
  min-width: 200px;
  /* No left shift — the heading aligns flush with the page's left
     content edge and the cards below it. */
  padding-left: 0;
}

.cart-header-actions {
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  /* Cap + left-align so the 3-across row never slides under the floating
     widget on the right edge. */
  max-width: 600px;
}
/* Three equal-width buttons across, instead of stacking vertically. */
.cart-header-actions .btn {
  flex: 1 1 0;
  min-width: 0;
  white-space: nowrap;
}

/* Back to Search sits in the header alongside Save Cart and
   Clear Cart. Visually distinct from both: filled red so it
   reads as a primary navigation action, not destructive
   (Clear Cart) and not neutral (Save Cart). The arrow-left
   icon reinforces "go back to where you came from". */
.btn-back-search {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 18px;
  background: #c0392b;
  border: 1.5px solid #c0392b;
  border-radius: 6px;
  color: #fff !important;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.btn-back-search:hover,
.btn-back-search:focus {
  background: #a32e22;
  border-color: #a32e22;
  color: #fff !important;
  text-decoration: none;
  box-shadow: 0 2px 6px rgba(192, 57, 43, 0.25);
}

.btn-back-search .btn-icon {
  flex-shrink: 0;
  stroke-width: 2;
  color: inherit;
}

.btn-save-cart {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 18px;
  background: #fff;
  border: 1.5px solid #333;
  border-radius: 6px;
  color: #111 !important;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.btn-save-cart:hover,
.btn-save-cart:focus {
  background: #333;
  color: #fff !important;
  border-color: #333;
  text-decoration: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.btn-save-cart .btn-icon {
  flex-shrink: 0;
  stroke-width: 2;
  color: inherit;
}

/* Clear Cart sits next to Save Cart in the header. Same shape, but
   visually de-emphasized with a red accent so it reads as the
   destructive action — keeps Save Cart as the primary affordance. */
.btn-clear-cart {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 18px;
  background: #fff;
  border: 1.5px solid #c0392b;
  border-radius: 6px;
  color: #c0392b !important;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.btn-clear-cart:hover,
.btn-clear-cart:focus {
  background: #c0392b;
  color: #fff !important;
  border-color: #c0392b;
  text-decoration: none;
  box-shadow: 0 2px 6px rgba(192, 57, 43, 0.18);
}

.btn-clear-cart .btn-icon {
  flex-shrink: 0;
  stroke-width: 2;
  color: inherit;
}

/* ===== CART GRAND TOTAL =====
   Sits below all PO group subtotals and visually anchors the cart with
   one bottom-line number. Heavy black top border + larger type so it
   reads as "the total" without needing explicit labels beyond "Cart Total". */
.cart-grand-total {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 18px 20px;
  margin-top: 8px;
  margin-bottom: 24px;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-top: 3px solid #222;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.cart-grand-total-label {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #333;
}

.cart-grand-total-value {
  font-size: 22px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
}

@media (max-width: 575.98px) {
  .cart-grand-total {
    padding: 14px 16px;
  }
  .cart-grand-total-value {
    font-size: 18px;
  }
}

/* ===== FORM INPUTS ===== */
input[type="text"],
textarea {
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 13px;
  color: #333;
  background: #fff;
}

input[type="text"]:focus,
textarea:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.08);
}

textarea {
  min-height: 120px;
  resize: vertical;
}

.special-instructions {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #555;
}

/* ===== 2-COLUMN ORDERS GRID =====
   Renders order cards side-by-side on desktop so the user
   sees more orders at a glance without scrolling. Stacks
   to a single column on tablet and below where the column
   width would crush the order details. */
.cart-orders-grid-wrap {
  margin-top: 0;
}

.cart-orders-grid {
  /* Bootstrap row defaults provide horizontal gutter; we add
     vertical spacing between rows so stacked orders breathe. */
  row-gap: 0;
}

.cart-order-col {
  margin-bottom: 22px;
}

@media (max-width: 991.98px) {
  /* Below lg, columns stack — drop the bottom margin
     duplication since each col is its own row. */
  .cart-order-col {
    margin-bottom: 16px;
  }
}

/* Inside the col, the po-group fills the column width and
   loses its outer margin (the col handles spacing now). */
.cart-order-col .po-group {
  margin: 0;
  height: 100%;
}

/* ===== PO GROUP (the new grouping container) ===== */
.po-group {
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-left: 3px solid #c0392b;
  border-radius: 8px;
  margin: 0 0 28px;
  padding: 24px 22px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* Order header — sits above the assembly cards in each PO group.
   Sequential label ("Order #1", "Order #2", …) plus assembly count.
   PO number + edit affordance is on the subtotal row at the bottom of
   each PO group, not here. */
.order-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #d8d8d8;
}

.order-header-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.order-header-label {
  font-size: 20px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
}

.order-header-meta {
  font-size: 12px;
  color: #777;
  font-style: italic;
}

/* ===== PER-PO SPECIAL INSTRUCTIONS READOUT =====
   Shown directly below the "Order #N · X assemblies" header
   row whenever group.specialInstructions has content. The
   text is truncated to ~140 chars with a Show more / Show
   less toggle (see toggleInstructions / isInstrExpanded /
   instrPreview methods) so a long note doesn't push the
   assembly cards down. Visually distinct from the assembly
   description (which lives below in each card) so users
   don't confuse PO-scoped notes with assembly metadata. */
.order-header-instr {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  padding: 6px 10px;
  background: #fffaf2;
  border: 1px solid #f3e3c2;
  border-left: 3px solid #d49a30;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #5a4014;
}

.order-header-instr-label {
  font-weight: 700;
  color: #8a6516;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 11px;
  flex-shrink: 0;
}

.order-header-instr-text {
  flex: 1 1 auto;
  word-break: break-word;
}

.order-header-instr-toggle {
  flex-shrink: 0;
  color: #c0392b;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: pointer;
  text-decoration: underline;
}

.order-header-instr-toggle:hover {
  color: #8a2519;
  text-decoration: underline;
}

/* "applies to all N assemblies" qualifier sits inside the
   consolidated Order-header readout's label so the user
   immediately understands the scope without confusing it
   with a per-assembly note. Quieter weight + italic so it
   reads as parenthetical metadata, not a header. */
.order-header-instr-scope {
  font-weight: 500;
  font-style: italic;
  text-transform: none;
  letter-spacing: 0;
  color: #8a6516;
  font-size: 11px;
  margin-left: 2px;
}

/* ===== PER-ASSEMBLY SPECIAL INSTRUCTIONS =====
   Renders inside each .assembly-card-heading, below the
   machineName + assemblyDescription. Visually echoes the
   amber Order-header readout but more compact (one-line,
   inline label) since assembly cards are denser than the
   Order header. Always shown when the assembly has its own
   non-empty value — including in the "everyone shares" case
   where the Order header above is also showing it (the
   redundancy is intentional and reinforces consistency). */
.assembly-instructions {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  margin-top: 6px;
  padding: 4px 8px;
  background: #fffaf2;
  border-left: 2px solid #d49a30;
  border-radius: 3px;
  font-size: 11px;
  line-height: 1.4;
  color: #5a4014;
  word-break: break-word;
}

.assembly-instructions-label {
  font-weight: 700;
  color: #8a6516;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 10px;
  flex-shrink: 0;
  white-space: nowrap;
}

.assembly-instructions-text {
  flex: 1 1 auto;
  font-weight: 500;
}

/* ===== ASSEMBLY CARD (inner blocks within a PO group) ===== */
.assembly-card {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 6px;
  padding: 18px 20px 14px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.assembly-card:last-of-type {
  margin-bottom: 18px;
}

.assembly-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.assembly-card-heading {
  flex: 1 1 auto;
  min-width: 200px;
}

/* PO subtotal block on the LAST assembly card per PO group, right-justified
   in the card header so it shares a row with the assembly title. Eliminates
   the standalone subtotal row at the bottom of each PO group, tightening
   vertical spacing between orders. */
.assembly-card-po-subtotal {
  flex: 0 1 auto;
  /* Allow the pill to shrink and its content to wrap when the
     column is narrow — otherwise the pill jets out of the
     parent card in the 2-col grid layout. */
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 8px 14px;
}

.assembly-card-po-label {
  font-size: 12px;
  color: #555;
  /* Wrap to a second line when the column is too narrow to
     fit "Subtotal for PO Number: 12345" on one row. */
  white-space: normal;
  word-break: break-word;
}

.assembly-card-po-amount {
  font-size: 16px;
  font-weight: 700;
  color: #c0392b;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

/* When the assembly card sits inside the 2-col orders grid,
   the column is too narrow for the "machine title | subtotal"
   side-by-side flex layout to fit. Stack the subtotal pill
   below the title instead — same pattern the mobile rule
   below uses, but triggered by the 2-col context rather than
   by viewport width. */
.cart-order-col .assembly-card-header {
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}

.cart-order-col .assembly-card-po-subtotal {
  align-self: stretch;
  justify-content: space-between;
}

@media (max-width: 767.98px) {
  .assembly-card-header {
    flex-direction: column;
  }
  .assembly-card-po-subtotal {
    align-self: stretch;
    justify-content: space-between;
  }
}

/* Actions moved to a centered footer row at the bottom of each card */
.assembly-card-footer {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f1f1f1;
}

.assembly-card-footer .btn-sm {
  font-size: 12px;
  padding: 7px 22px;
  letter-spacing: 0.3px;
  min-width: 120px;
}

.assemblyName {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  margin-bottom: 2px;
}

.assemblyDescription {
  font-size: 13px;
  color: #777;
}

/* ===== SUBTOTAL PO INLINE EDIT =====
   The PO Number is now editable from the bottom-of-PO-group subtotal
   row instead of from a header at the top. Pencil icon next to the
   number, click → swap to inline input + Save/Cancel. */
.subtotal-po-edit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-left: 4px;
  color: #888;
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.15s ease, color 0.15s ease;
  vertical-align: middle;
}

.subtotal-po-edit:hover {
  background: #f0f0f0;
  color: #c0392b;
  text-decoration: none;
}

.subtotal-po-edit svg {
  stroke-width: 2.2;
}

.subtotal-po-edit-input {
  display: inline-block;
  width: 160px !important;
  padding: 5px 10px !important;
  font-size: 13px !important;
  font-weight: 600;
  color: #c0392b !important;
  margin: 0 6px !important;
  vertical-align: middle;
}

.subtotal-po-save {
  font-size: 12px;
  padding: 5px 12px;
  margin: 0 4px 0 0;
  vertical-align: middle;
}

.subtotal-po-cancel {
  color: #777 !important;
  text-decoration: underline;
  background: transparent !important;
  border: none !important;
  padding: 5px 8px !important;
  box-shadow: none !important;
  font-size: 12px;
  vertical-align: middle;
}

.subtotal-po-cancel:hover {
  color: #c0392b !important;
}

/* ===== COLLAPSIBLE ASSEMBLY SUMMARY ROW ===== */
.assembly-summary {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 12px 10px 10px;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 4px;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.assembly-summary:hover {
  background: #f3f3f3;
  border-color: #e0e0e0;
}

.assembly-summary.expanded {
  background: #fff;
  border-color: #ddd;
}

.assembly-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 50%;
  border: 1.5px solid #c0392b;
  background: #fff;
  color: #c0392b;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.assembly-summary:hover .assembly-toggle {
  background: #c0392b;
  color: #fff;
}

.assembly-toggle.expanded {
  background: #333;
  border-color: #333;
  color: #fff;
}

.assembly-summary:hover .assembly-toggle.expanded {
  background: #555;
  border-color: #555;
}

.assembly-toggle-icon {
  display: block;
  line-height: 1;
  margin-top: -2px;
}

.assembly-summary-content {
  flex: 1 1 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.assembly-summary-count {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #888;
  margin-right: 4px;
}

.code-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
}

.code-chip .code-qty {
  color: #c0392b;
  font-weight: 700;
  font-size: 12px;
}

.assembly-summary-empty {
  font-size: 13px;
  color: #999;
  font-style: italic;
}

.assembly-summary-total {
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 700;
  color: #c0392b;
  min-width: 100px;
  text-align: right;
}

.assembly-table-wrap {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

/* ===== PO NUMBER (used in the assembly-card-po-subtotal block) ===== */
.subtotal-po {
  color: #c0392b;
  font-weight: 700;
}

/* ===== LEGACY PO / ENTER PO FORM (for orders needing a PO) ===== */
.enterPO .alert-danger {
  font-size: 13px;
  line-height: 1.5;
}

.enterPO label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 0;
}

.POOrders {
  margin-bottom: 30px;
}

.POOrders .container {
  position: relative;
}

/* ===== BUTTONS ===== */
.btn {
  font-weight: 600;
  letter-spacing: 0.2px;
}

.btn-primary {
  white-space: nowrap;
}

.btn-dark {
  white-space: nowrap;
}

.table-btn {
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  white-space: nowrap;
}

.table-btn:hover {
  background-color: #c0392b !important;
  border-color: #c0392b !important;
}

/* ===== CART TABLE ===== */
table {
  margin-bottom: 0;
}

.table th {
  font-size: 13px;
  font-weight: 700;
  color: #333;
  vertical-align: middle;
}

.table td {
  font-size: 13px;
  color: #333;
  vertical-align: middle;
}

.nowrap {
  white-space: nowrap;
}

.table-input {
  width: 80px !important;
  min-width: 80px;
  display: inline-block;
  margin-right: 8px;
  padding: 7px 10px;
}

.btn-remove {
  position: relative;
  display: inline-block;
  width: 18px;
  height: 18px;
  text-decoration: none;
  cursor: pointer;
}

.btn-remove::before,
.btn-remove::after {
  content: "";
  position: absolute;
  top: 8px;
  left: 0;
  width: 18px;
  height: 2px;
  background: #c0392b;
}

.btn-remove::before {
  transform: rotate(45deg);
}

.btn-remove::after {
  transform: rotate(-45deg);
}

.btn-remove:hover::before,
.btn-remove:hover::after {
  background: #a93226;
}

/* ===== REVIEW INFO / FINAL ACTIONS ===== */
#reviewInfo {
  padding-top: 24px;
  margin-top: 16px;
  /* border-top removed — it spanned the full viewport (the
     div renders edge-to-edge while its inner .container is
     the constrained-width content area), producing a thin
     horizontal line that cut across the whole page above
     the verify-info banner. The vertical spacing
     (padding-top + margin-top) gives enough separation
     between the cart total above and the verify message
     below without a visible rule. */
}

#reviewInfo .alert-secondary {
  border: 1px solid #e6e6e6;
  background: #f8f8f8;
  color: #555;
}

#reviewInfo .alert-secondary h4 {
  margin-bottom: 0;
  font-size: 16px;
  font-weight: 600;
}

#finalActions {
  padding: 24px 0 32px;
}

.btn-place-order {
  display: block;
  width: 100%;
  text-align: center;
  padding: 14px 20px;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

/* ===== RESET CART MODAL =====
   Custom modal (not msgBoxConfirm) that shows a structured summary of
   what's about to be deleted. Visual treatment echoes the Cart Inactivity
   Warning modal: centered, compact width, title at top, a body block
   with a list of assemblies + totals, and stacked full-width action
   buttons. Overrides b-modal's default horizontal footer because the
   primary visual is the body, not the buttons. */

/* We aren't scoped-safe for b-modal's root elements (they get portaled
   to the end of body), so these selectors are deliberately deep. */
.reset-cart-modal-header {
  background: #fff;
  border-bottom: 1px solid #eee;
  padding: 16px 22px;
}

::v-deep #resetCartModal .modal-title {
  font-size: 16px;
  font-weight: 700;
  color: #222;
  letter-spacing: 0.2px;
}

::v-deep #resetCartModal .modal-content {
  border: none;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}

.reset-cart-modal-body {
  padding: 18px 22px 22px;
}

.reset-cart-intro {
  margin: 0 0 14px 0;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

.reset-cart-intro strong {
  color: #c0392b;
}

.reset-cart-summary {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 18px;
  max-height: 260px;
  overflow-y: auto;
}

.reset-cart-summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8e8e8;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
}

.reset-cart-summary-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.reset-cart-summary-row:last-of-type {
  border-bottom: none;
}

.reset-cart-summary-left {
  flex: 1 1 auto;
  min-width: 0;
}

.reset-cart-summary-machine {
  font-size: 13px;
  font-weight: 700;
  color: #222;
  letter-spacing: 0.2px;
}

.reset-cart-summary-assembly {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 1px;
}

.reset-cart-summary-meta {
  font-size: 11px;
  color: #777;
  margin-top: 3px;
}

.reset-cart-summary-cost {
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  white-space: nowrap;
}

.reset-cart-summary-orphan {
  padding: 10px 0 4px;
  font-size: 12px;
  color: #9c5700;
  font-style: italic;
}

/* Totals header at the top of the modal — leads with the magnitude
   (assembly count + part count + grand total) so the user sees the
   scale of the reset before the per-row breakdown. */
.reset-cart-totals {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  flex-wrap: wrap;
  padding: 14px 16px;
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-top: 3px solid #222;
  border-radius: 6px;
}

.reset-cart-totals-counts {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.reset-cart-totals-count {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}

.reset-cart-totals-num {
  font-size: 20px;
  font-weight: 700;
  color: #222;
  letter-spacing: 0.2px;
  line-height: 1;
}

.reset-cart-totals-unit {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #777;
}

.reset-cart-totals-sep {
  color: #ccc;
  font-size: 16px;
  line-height: 1;
}

.reset-cart-totals-amount {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.reset-cart-totals-amount-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #777;
}

.reset-cart-totals-amount-value {
  font-size: 22px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
  line-height: 1.1;
}

@media (max-width: 575.98px) {
  .reset-cart-totals {
    flex-direction: column;
    gap: 10px;
  }
  .reset-cart-totals-amount-value {
    font-size: 20px;
  }
}

.reset-cart-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.reset-cart-actions .btn {
  width: 100%;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 4px;
}

.reset-cart-btn-keep {
  background: #c0392b !important;
  border-color: #c0392b !important;
  color: #fff !important;
}

.reset-cart-btn-keep:hover,
.reset-cart-btn-keep:focus {
  background: #a82e22 !important;
  border-color: #a82e22 !important;
  color: #fff !important;
}

.reset-cart-btn-clear {
  background: #fff !important;
  border: 1px solid #ccc !important;
  color: #555 !important;
}

.reset-cart-btn-clear:hover,
.reset-cart-btn-clear:focus {
  background: #f5f5f5 !important;
  border-color: #999 !important;
  color: #333 !important;
}

/* ===== ALERTS ===== */
.alert {
  border-radius: 6px;
}

.alert h4,
.alert strong {
  font-weight: 700;
}

/* ===== MOBILE TWEAKS ===== */
@media (max-width: 991.98px) {
  .cart-header {
    flex-direction: column;
    align-items: stretch;
  }

  .cart-header-title {
    /* Drop the 48px desktop shift on narrow viewports so the
       title doesn't get pushed past the right edge. */
    padding-left: 0;
  }

  .cart-header-actions {
    width: 100%;
  }

  .btn-save-cart {
    width: 100%;
  }

  .assemblyName {
    font-size: 18px;
  }

  .assembly-card-footer {
    flex-direction: column;
  }

  .assembly-card-footer .btn-sm {
    width: 100%;
    min-width: 0;
  }

  .subtotal-po-edit-input {
    width: 100% !important;
    margin: 6px 0 !important;
    display: block;
  }

  .assembly-summary {
    flex-wrap: wrap;
  }

  .assembly-summary-content {
    order: 3;
    width: 100%;
  }

  .assembly-summary-total {
    text-align: right;
    min-width: 0;
    margin-left: auto;
  }

  .table-input {
    width: 100% !important;
    min-width: 0;
    margin-right: 0;
    margin-bottom: 8px;
  }

  .table-btn {
    width: 100%;
  }

  .btn-primary,
  .btn-dark {
    width: 100%;
    text-align: center;
  }
}

/* ===== MODIFY ORDER MODAL =====
   Targeted via modal-class="modify-order-modal" so the
   styles reach the portaled modal root (scoped CSS doesn't
   apply to b-modal contents since they're rendered outside
   the component subtree). */
.modify-order-modal .modify-modal-intro {
  margin: 0 0 14px;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

/* ===== RE-ORDER COMPANY LABEL (Feature 2) =====
   Compact sub-banner directly under the context banner stating
   the company this re-order is for. Soft neutral chip so it reads
   as a clear, on-brand fact line without competing with the
   emphasized red context banner above it. */
.modify-order-modal .modify-reorder-company {
  margin: 0 0 14px;
  padding: 8px 12px;
  background: #f6f8fa;
  border: 1px solid #e2e6ea;
  border-left: 3px solid #c0392b;
  border-radius: 6px;
  font-size: 13px;
  color: #555;
}
.modify-order-modal .modify-reorder-company strong {
  color: #2a2a2a;
  font-weight: 700;
}

/* ===== CART RESTORED BANNER =====
   Sits above the modal-intro paragraph. Boxed, bordered,
   shadowed so the user's eye is drawn to it on modal
   open — this is the confirmation moment that ties their
   "I clicked Resume" action to "yes, your cart is back."

   Color palette pulls a soft red tint from the brand
   accent so the banner reads as on-brand rather than a
   generic info panel. Border is heavier (2px) than
   typical info banners since the goal is emphasis. */
.modify-order-modal .modify-restored-banner {
  background: linear-gradient(
    135deg,
    #fff5f3 0%,
    #fff 100%
  );
  border: 2px solid #c0392b;
  border-radius: 10px;
  padding: 18px 22px;
  margin: 0 0 18px;
  text-align: center;
  box-shadow: 0 4px 16px rgba(192, 57, 43, 0.12),
              0 1px 3px rgba(0, 0, 0, 0.04);
}

.modify-order-modal .modify-restored-title {
  font-size: 20px;
  font-weight: 800;
  color: #c0392b;
  letter-spacing: 0.3px;
  margin-bottom: 6px;
  line-height: 1.2;
}

.modify-order-modal .modify-restored-body {
  font-size: 13px;
  color: #444;
  line-height: 1.5;
}

.modify-order-modal .modify-restored-body strong {
  color: #222;
  font-weight: 700;
}

@media (max-width: 575.98px) {
  .modify-order-modal .modify-restored-title {
    font-size: 18px;
  }
  .modify-order-modal .modify-restored-banner {
    padding: 14px 16px;
  }
}

.modify-order-modal .modify-options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modify-order-modal .modify-option {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #e3e3e3;
  border-left: 3px solid #c0392b;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
}

.modify-order-modal .modify-option:hover {
  background: #fff8f8;
  border-color: #c0392b;
  box-shadow: 0 2px 6px rgba(192, 57, 43, 0.12);
  transform: translateX(2px);
}

.modify-order-modal .modify-option-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.modify-order-modal .modify-option-orderlabel {
  font-weight: 700;
  color: #c0392b;
  font-size: 14px;
}

.modify-order-modal .modify-option-assembly {
  font-weight: 600;
  color: #222;
  font-size: 13px;
}

.modify-order-modal .modify-option-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
  color: #666;
}

.modify-order-modal .modify-option-meta strong {
  color: #444;
}

/* ===== MODIFY ORDER MODAL — EDITOR =====
   Mirrors the RFQ-side editor styles. The picker is a
   list of radio rows of orders, the editor is a card
   with PO + Special Instructions inputs + apply-to-all
   toggle, and the action row sits at the bottom. */
.modify-order-modal .modify-quote-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 18px;
  /* No max-height / overflow — every Order is collapsed by
     default so the picker fits comfortably without scroll
     even with several Orders in the cart. Expanding all of
     them at once with many assemblies could overflow the
     modal body, but that's the modal's natural scroll, not
     a clipped inner scrollbar that hides Order #N rows
     until the user knows to scroll. */
}

/* ===== PER-PART LIST INSIDE ASSEMBLY ROW =====
   Shown beneath each assembly's pick row when the group is
   expanded. Each part has a delete X. Clicking the X stops
   propagation so it doesn't also fire the assembly radio
   underneath. */
.modify-order-modal .modify-quote-pick-wrap {
  border: 1px solid #e3e3e3;
  border-radius: 6px;
  background: #fff;
  margin-bottom: 8px;
  transition: border-color 200ms ease, box-shadow 200ms ease;
}

.modify-order-modal .modify-quote-pick-wrap:last-child {
  margin-bottom: 0;
}

/* Pick row inside the wrap loses its own border/background
   since the wrap carries those now — otherwise we'd get
   double borders. */
.modify-order-modal .modify-quote-pick-wrap .modify-quote-pick-row {
  border: 0;
  border-radius: 6px 6px 0 0;
  margin-bottom: 0;
  background: transparent;
}

.modify-order-modal .modify-quote-pick-wrap .modify-quote-pick-row.is-selected {
  background: #fff8f8;
  box-shadow: none;
}

.modify-order-modal .modify-parts-list {
  border-top: 1px dashed #ececec;
  padding: 6px 12px 8px;
  background: #fafafa;
  border-radius: 0 0 6px 6px;
}

.modify-order-modal .modify-part-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  font-size: 12px;
  border-bottom: 1px dotted #eee;
}

.modify-order-modal .modify-part-row:last-child {
  border-bottom: 0;
}

.modify-order-modal .modify-part-info {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.modify-order-modal .modify-part-discount-chip {
  display: inline-block;
  background: #c8e6c9;
  color: #1b5e20;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 6px;
  vertical-align: middle;
  white-space: nowrap;
  /* Parent .modify-part-info is flex-direction: column.
     Flex items default to align-items: stretch on the
     cross axis, which was stretching this chip to fill
     the full row width (~600px instead of ~80px). Pin
     the chip to the start so it sizes to content. */
  align-self: flex-start;
}

/* /cart Review & Checkout part-detail rows. Same chip
   visual as the modal version, but here the parent
   .part-detail-meta is a flex ROW so no align-self
   workaround is needed — inline-block + nowrap is enough
   for the chip to size to its text content. */
.part-discount-chip {
  display: inline-block;
  background: #c8e6c9;
  color: #1b5e20;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 6px;
  vertical-align: middle;
  white-space: nowrap;
}
.modify-part-code {
  font-family: monospace;
  font-weight: 700;
  color: #222;
  font-size: 11px;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modify-order-modal .modify-part-desc {
  color: #666;
  font-size: 11px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.modify-order-modal .modify-part-qty {
  flex: 0 0 auto;
  font-weight: 600;
  color: #555;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.modify-order-modal .modify-part-cost {
  flex: 0 0 auto;
  font-weight: 700;
  color: #222;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  min-width: 70px;
  text-align: right;
}

.modify-order-modal .modify-part-remove {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid transparent;
  background: transparent;
  color: #c0392b;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background 180ms ease, border-color 180ms ease,
              color 180ms ease;
}

.modify-order-modal .modify-part-remove:hover {
  background: #c0392b;
  border-color: #c0392b;
  color: #fff;
}

/* ===== HIERARCHICAL ORDER GROUPS =====
   Each Order # gets its own bordered section with a sticky
   header at the top. Single-assembly Orders show a flat row
   inside; multi-assembly Orders nest assemblies under the
   header so the Order > Assembly hierarchy reads cleanly.
   The .is-multi modifier emphasizes the grouping with extra
   visual weight on the header. */
.modify-order-modal .modify-order-group {
  border: 1px solid #e3e3e3;
  border-left: 3px solid #c0392b;
  border-radius: 4px;
  padding: 8px 10px;
  background: #fff;
}

.modify-order-modal .modify-order-group.is-multi {
  background: #fafafa;
  padding: 10px 12px;
}

/* ===== ORDER GROUP HEADER =====
   Three-slot flex row: label (left) · total (center) · count
   (right). Button styling is reset since the whole header
   doubles as a click target for expand/collapse.

   Sub-row: chevron toggle + helper text. Visually
   subordinate so the header reads as primary identifier
   and the toggle reads as secondary affordance. */
.modify-order-modal .modify-order-group {
  background: #fafafa;
  border: 1px solid #ececec;
  border-left: 3px solid #c0392b;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 12px;
  transition: box-shadow 200ms ease;
}

.modify-order-modal .modify-order-group.is-expanded {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

/* Wraps the optional select checkbox + the toggle button
   that doubles as the Order header. Flex row so the
   checkbox sits left and the title spans the rest. */
.modify-order-modal .modify-order-group-head-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modify-order-modal .modify-order-group-select {
  flex: 0 0 auto;
  margin: 0;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.modify-order-modal .modify-order-group-select input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #c0392b;
}

.modify-order-modal .modify-order-group-head {
  display: flex;
  align-items: center;
  gap: 16px;
  /* Share the header row with the per-order PO field on the right
     (was width:100%, which pushed the PO field onto its own line). */
  flex: 1 1 auto;
  min-width: 0;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
}

/* ===== PER-ORDER PO FIELD (in each Order header) ===== */
.modify-order-modal .modify-order-group-po-field {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  cursor: text;
}
.modify-order-modal .modify-order-group-po-label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #666;
  white-space: nowrap;
}
.modify-order-modal .modify-order-group-po-input {
  width: 150px;
  max-width: 38vw;
  padding: 7px 9px;
  font-size: 0.9rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  /* Empty (placeholder showing) → yellow prompt; turns white once a
     PO Number is typed (see :not(:placeholder-shown) below). */
  background: #fff8c4;
  color: #222;
}
.modify-order-modal .modify-order-group-po-input:not(:placeholder-shown) {
  background: #fff;
}
.modify-order-modal .modify-order-group-po-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.12);
}

.modify-order-modal .modify-order-group-head:hover
  .modify-order-group-label {
  color: #a52d20;
}

.modify-order-modal .modify-order-group-label {
  flex: 1 1 auto;
  font-weight: 700;
  color: #c0392b;
  font-size: 14px;
  letter-spacing: 0.3px;
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
}

.modify-order-modal .modify-order-group-po {
  font-size: 12px;
  font-weight: 400;
  color: #555;
  letter-spacing: 0;
}

.modify-order-modal .modify-order-group-po strong {
  color: #222;
  font-weight: 700;
}

.modify-order-modal .modify-order-group-po-empty {
  color: #999;
  font-style: italic;
}

.modify-order-modal .modify-order-group-total {
  flex: 0 0 auto;
  font-weight: 700;
  font-size: 14px;
  color: #222;
  font-variant-numeric: tabular-nums;
  text-align: center;
  white-space: nowrap;
}

.modify-order-modal .modify-order-group-count {
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #888;
  background: #eee;
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
}

/* ===== EXPAND TOGGLE ROW =====
   Sits beneath the title. Subdued so the header row above
   stays visually primary. Chevron icon flips between
   '+' (collapsed) and '−' (expanded). */
.modify-order-modal .modify-order-group-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 0;
  padding: 6px 0 0;
  margin: 0;
  cursor: pointer;
  color: #777;
  font-size: 12px;
  font-weight: 500;
  transition: color 180ms ease;
}

.modify-order-modal .modify-order-group-toggle:hover {
  color: #c0392b;
}

.modify-order-modal .modify-order-group-toggle-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #d8d8d8;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  color: #555;
  transition: background 180ms ease, border-color 180ms ease,
              color 180ms ease;
}

.modify-order-modal .modify-order-group-toggle:hover
  .modify-order-group-toggle-icon {
  background: #c0392b;
  border-color: #c0392b;
  color: #fff;
}

.modify-order-modal .modify-order-group-toggle.is-open
  .modify-order-group-toggle-icon {
  background: #c0392b;
  border-color: #c0392b;
  color: #fff;
}

.modify-order-modal .modify-order-group-toggle-text {
  letter-spacing: 0.2px;
}

/* ===== ASSEMBLIES CONTAINER =====
   Renders only when expanded. Top margin + light border
   separates from the header above. */
.modify-order-modal .modify-order-group-assemblies {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e3e3e3;
}

/* ===== ASSEMBLY ROW =====
   Three-slot layout: radio + body + cost. Radio left so
   selection state is clear; body grows to fill; cost
   pinned right and tabular-num so columns align across
   rows. */

.modify-order-modal .modify-quote-pick-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 12px;
  border: 1px solid #e3e3e3;
  border-left: 3px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  margin: 0 0 6px 0;
  background: #fff;
}

.modify-order-modal .modify-quote-pick-row:last-child {
  margin-bottom: 0;
}

/* Nested rows are now the default (every row is inside an
   expandable group), so the indent treatment isn't needed
   any more — keep the rule in case a single-assembly group
   also gets the modifier, but with no extra indent. */
.modify-order-modal .modify-quote-pick-row.is-nested {
  margin-left: 0;
  border-left-color: #ddd;
}

.modify-order-modal .modify-quote-pick-row:hover {
  background: #fafafa;
}

.modify-order-modal .modify-quote-pick-row.is-selected {
  background: #fff8f8;
  border-color: #c0392b;
  box-shadow: 0 1px 4px rgba(192, 57, 43, 0.10);
}

.modify-order-modal .modify-option-instr-flag {
  font-style: italic;
  color: #8a6516;
  font-weight: 600;
}

.modify-order-modal .modify-quote-pick-radio {
  margin-top: 4px;
  flex-shrink: 0;
}

.modify-order-modal .modify-quote-pick-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
}

/* Per-assembly cost on the right of each picker row.
   tabular-num so digits align vertically across rows.
   Pinned right via flex; whitespace nowrap prevents
   wrapping on narrow viewports. */
.modify-order-modal .modify-quote-pick-cost {
  flex: 0 0 auto;
  align-self: center;
  font-weight: 700;
  font-size: 13px;
  color: #222;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  padding-left: 8px;
}

/* Scope qualifier on the Special Instructions label tells
   the user exactly which assembly the input below will
   write to — important now that per-assembly editing is
   the default and the user might assume the textarea
   applies to the whole Order. */
.modify-order-modal .modify-editor-scope {
  font-weight: 500;
  font-style: italic;
  text-transform: none;
  letter-spacing: 0;
  color: #777;
  font-size: 11px;
}

.modify-order-modal .modify-editor {
  margin-top: 6px;
  padding: 14px 16px;
  background: #fafafa;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
}

.modify-order-modal .modify-editor-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}

.modify-order-modal .modify-editor-row:last-child {
  margin-bottom: 0;
}

.modify-order-modal .modify-editor-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #555;
  margin: 0;
}

.modify-order-modal .modify-editor-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
}

.modify-order-modal .modify-editor-input:focus {
  outline: none;
  border-color: #c0392b;
  box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.15);
}

.modify-order-modal .modify-editor-textarea {
  resize: vertical;
  min-height: 70px;
  font-family: inherit;
}

.modify-order-modal .modify-editor-toggle-row {
  margin-top: 4px;
}

.modify-order-modal .modify-apply-all {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 13px;
  color: #444;
  cursor: pointer;
}

.modify-order-modal .modify-apply-all input[type="checkbox"] {
  margin: 0;
  flex-shrink: 0;
}

.modify-order-modal .modify-actions {
  display: flex;
  flex-direction: row;
  justify-content: stretch;
  align-items: stretch;
  gap: 10px;
  margin-top: 18px;
  flex-wrap: nowrap;
}

.modify-order-modal .modify-action-btn {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 12px;
  padding: 8px 16px;
  /* Each button takes equal share of the row so the three sit
     side-by-side at consistent widths regardless of label length. */
  flex: 1 1 0;
  min-width: 0;
  white-space: nowrap;
}

/* ===== PLACE ORDER PICKER ACTIONS =====
   Custom action row at the bottom of the picker modal body,
   replacing b-modal's default footer. Two buttons (Place,
   Cancel) stretched to equal widths via flex so they always
   sit side-by-side regardless of label length. The b-modal
   default footer was rendering with stacked / oddly-styled
   buttons in this layout — moving the actions inside the
   body and using direct @click handlers gives consistent
   styling and predictable event firing. */
.place-order-picker-modal .place-order-actions {
  display: flex;
  flex-direction: row;
  gap: 10px;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #f0f0f0;
  flex-wrap: nowrap;
}

.place-order-picker-modal .place-order-action-btn {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 12px;
  padding: 10px 16px;
  flex: 1 1 0;
  min-width: 0;
  white-space: nowrap;
}

/* Per-assembly machine list inside each picker row. Each
   assembly span needs to render inline so the "·" separators
   read as one continuous meta line. */
.place-order-picker-modal .convert-quote-row-asm {
  display: inline;
}

.place-order-picker-modal .convert-quote-row-sep {
  color: #bbb;
}

/* ===== SAVE CART PICKER MODAL =====
   Copied from RFQSavedItems' picker styles so the Save
   Cart UX is visually identical to Save for Later on the
   quotes page. The classes are unscoped (b-modal mounts
   to body, not the component subtree) so they need to be
   present in whichever component spawns the modal. */
.convert-modal-intro {
  margin: 0 0 14px;
  font-size: 13px;
  color: #555;
}

.convert-quote-list {
  margin: 0 0 12px;
  padding: 0;
}

.convert-quote-row {
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid #ececec;
  border-radius: 6px;
  margin-bottom: 6px;
}

.convert-quote-row:last-child {
  margin-bottom: 0;
}

.convert-quote-row .custom-control-label {
  cursor: pointer;
  width: 100%;
}

.convert-quote-row-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 2px;
}

.convert-quote-row-label {
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  letter-spacing: 0.3px;
}

.convert-quote-row-meta {
  font-size: 12px;
  color: #444;
  font-weight: 600;
}

.convert-quote-row-sub {
  font-size: 11px;
  color: #888;
  letter-spacing: 0.2px;
  text-transform: uppercase;
}

.convert-quote-row-amount {
  color: #c0392b;
  font-weight: 700;
}

.convert-quote-row-poref {
  color: #555;
  font-weight: 500;
}

.convert-quote-row-poref strong {
  color: #222;
  font-weight: 700;
}

.convert-quote-toggle-all {
  text-align: right;
  margin: 0 0 12px;
}

.convert-quote-toggle-all a {
  font-size: 11px;
  font-weight: 700;
  color: #c0392b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
  text-decoration: none;
}

.convert-quote-toggle-all a:hover {
  text-decoration: underline;
}

.convert-quote-note {
  margin: 8px 0 0;
  font-size: 12px;
  color: #666;
  font-style: italic;
}

.convert-quote-note-warn {
  color: #c0392b;
  font-style: normal;
  font-weight: 600;
}

</style>

<!-- Unscoped: targets BootstrapVue's portaled .modal-dialog / .modal-content
     wrapper elements (and the dark/mid-mode input overrides), which the
     scoped block above cannot reach. -->
<style>
/* "Modify Order" modal — push the dialog below the fixed site header so the
   title bar isn't hidden behind the nav; let the body scroll when tall.
   (Mirrors the working .modify-quote-modal rule in RFQSavedItems.vue.) */
.modify-order-modal .modal-dialog {
  margin-top: 170px;
  margin-bottom: 30px;
}
.modify-order-modal .modal-content {
  max-height: calc(100vh - 200px);
}
.modify-order-modal .modal-body {
  overflow-y: auto;
}

/* ===== STACK ABOVE THE FLOATING HEADER SEARCH =====
   The site header's floating autocomplete (#header-search + its
   .custom-dropdown, Head.vue) lives INSIDE the .stickyHeader element,
   which steele-consistency-fix.css pins at z-index: 9200 !important.
   Because .stickyHeader is position:fixed with a z-index, it forms a
   stacking context, so the search paints at the 9200 layer — ABOVE the
   global Bootstrap-Vue modal (.modal is bumped to only 9110 !important
   in that same file). That's why the search bleeds over this modal.

   Fix: raise THIS modal above the 9200 sticky-header layer. !important
   is required to beat the global `.modal { z-index: 9110 !important }`.
   The .modal wrapper AND the .modal-dialog go to 10050; the dialog is
   opaque, so where it overlaps the search (the modal starts at
   margin-top:170px, the search floats ~120-150px) it now paints on top
   and the search no longer bleeds through the modal.

   Backdrop: deliberately left at the global 9100. BootstrapVue applies
   modal-class="modify-order-modal" to the .modal wrapper but NOT to the
   separate .modal-backdrop, and a global .modal-backdrop bump would
   shove OTHER modals' backdrops above their own dialogs (those stay at
   the global 9110), breaking every other modal whenever this file's
   unscoped CSS is loaded. The dialog bump alone resolves the reported
   bug (search covering the modal); the search not being dimmed by the
   backdrop is cosmetic and never covers the modal content. */
.modify-order-modal.modal {
  z-index: 10050 !important;
}
.modify-order-modal .modal-dialog {
  z-index: 10050 !important;
}
/* The header's floating Search field (#header-search) lives in the sticky
   header's OWN stacking context (z-index 9200), which sits above the modal's
   context — so a z-index bump on the modal can't lift it over the search.
   Instead, hide the Search field while ANY modal is open. BootstrapVue adds
   the `modal-open` class to <body> whenever a modal is shown, so it returns
   automatically on close. */
body.modal-open #header-search {
  display: none !important;
}

/* ===== REORDER CONFIRMATION STEP =====
   Partial-PO confirm view inside the Modify modal (reorder flow only).
   Two lists — green "added" vs muted "not added" — so the user sees
   exactly what the partial submit will keep and drop. */
.modify-order-modal .modify-reorder-confirm-section {
  margin-top: 16px;
}
.modify-order-modal .modify-reorder-confirm-heading {
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 6px;
}
.modify-order-modal .modify-reorder-confirm-added .modify-reorder-confirm-heading {
  color: #1e7e34;
}
.modify-order-modal .modify-reorder-confirm-dropped .modify-reorder-confirm-heading {
  color: #6b6b6b;
}
.modify-order-modal .modify-reorder-confirm-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.modify-order-modal .modify-reorder-confirm-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid #e3e3e3;
  border-radius: 6px;
  margin-bottom: 6px;
}
.modify-order-modal .modify-reorder-confirm-added .modify-reorder-confirm-item {
  border-color: #b7e1c2;
  background: #f3fbf5;
}
.modify-order-modal .modify-reorder-confirm-dropped .modify-reorder-confirm-item {
  background: #f7f7f7;
}
.modify-order-modal .modify-reorder-confirm-order {
  font-weight: 600;
}
.modify-order-modal .modify-reorder-confirm-po {
  font-weight: 700;
  color: #1e7e34;
  white-space: nowrap;
}
/* Mid-dark + dark mode: keep the yellow-when-empty / white-when-filled PO
   inputs. The global dark input styles would otherwise override the scoped
   light-mode rule, so re-assert here with !important + higher specificity. */
html.dark-mode .modify-order-modal .modify-order-group-po-input,
html.mid-mode .modify-order-modal .modify-order-group-po-input {
  background: #fff8c4 !important;
  color: #222 !important;
}
html.dark-mode .modify-order-modal .modify-order-group-po-input:not(:placeholder-shown),
html.mid-mode .modify-order-modal .modify-order-group-po-input:not(:placeholder-shown) {
  background: #fff !important;
  color: #222 !important;
}
html.dark-mode .modify-order-modal .modify-order-group-po-input::placeholder,
html.mid-mode .modify-order-modal .modify-order-group-po-input::placeholder {
  color: #6b6b6b !important;
}

/* Re-order company label (Feature 2) — dark / mid variants so the
   neutral chip doesn't read as a glaring light box. */
html.dark-mode .modify-order-modal .modify-reorder-company,
html.mid-mode .modify-order-modal .modify-reorder-company {
  background: #2b2b2b;
  border-color: #444;
  border-left-color: #c0392b;
  color: #ddd;
}
html.dark-mode .modify-order-modal .modify-reorder-company strong,
html.mid-mode .modify-order-modal .modify-reorder-company strong {
  color: #fff;
}
</style>